import { displayPhone, phonePlan } from '../../shared/country/phonePlan';
import { contactOtpError } from '../../features/profile/contactOtpError';
import { bindContactRewardLabel } from '../../shared/profile/bindContactReward';
import { useRegistrationSecurity } from '../../features/auth/RegistrationChallenge';
import { USERNAME_MAX_LEN, validateUsername } from '../../shared/username/validateUsername';
import { poolForAccount } from '../../shared/username/generator';
import { suggestAvailableUsername } from '../../shared/username/suggest';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Bell,
  Camera,
  Check,
  ChevronRight,
  Coins,
  Copy,
  Dices,
  Eye,
  EyeOff,
  Gift,
  HelpCircle,
  Ticket,
  Languages,
  List,
  Lock,
  Mail,
  LogOut,
  MessageCircle,
  Pencil,
  Phone,
  RefreshCw,
  ReceiptText,
  Shield,
  ShieldCheck,
  UserRound,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties, type PointerEvent, type ReactNode } from 'react';
import { useAppStore, type MeSecurityTargetChannel, type PlayerRoute } from '../../app/appStore';
import {
  userDisplayId,
  userName,
  userVipLevel,
  userWalletBrl,
  type PlayerUser,
} from '../../entities/user/types';
import {
  checkUsername,
  updateMeAvatarData,
  updateMeDefaultAvatar,
  updateMePreferences,
  updateMeUsername,
  resetForgotPassword,
  sendForgotPasswordOtp,
  type UsernameResponse,
} from '../../features/auth/api';
import { useAuthStore } from '../../features/auth/authStore';
import { DevicePushPreference } from '../../features/push/DevicePushPreference';
import { NotificationToggleConfirm } from '../../features/push/NotificationToggleConfirm';
import {
  fetchActivityDrum,
  fetchActivitiesSummary,
  fetchActivityAudits,
  fetchResponsibleGaming,
  fetchSecuritySessions,
  claimActivityCheckin,
  confirmContactOtp,
  revokeSecuritySession,
  sendContactOtp,
  updateActivityDrumState,
  updateResponsibleGaming,
  fetchRewardInbox,
  claimReward,
  type RewardInboxItem,
  type ActivityCheckin,
  type CheckinDay,
  type ActivityClaim,
  type ActivityDrumItem,
  type ActivityDrumResponse,
  type ResponsibleGamingLimits,
  type SecuritySession,
  type ActivityDailyBet,
  type ActivityReferrals,
} from '../../features/profile/api';
import { fetchMyCoupons, fetchClaimableCoupons, claimDepositCoupon, fetchCouponHistory, fetchRaffleData, fetchTicketLedger, type DepositCoupon, type ClaimableCoupon } from '../../features/wallet/api';
import {
  checkinDayRewards,
  checkinRewardText,
  checkinTileLabel,
  checkinTrackFunded,
  resolveCheckinTrackDays,
  type RichCheckinDay,
} from '../../shared/profile/checkinDays';
import {
  REWARDS_V4_PREVIEW,
  PREVIEW_VOUCHERS,
  PREVIEW_BIND_MISSIONS,
  PREVIEW_AUTO_BANNERS,
  localizePreview,
  type PreviewRules,
} from '../../features/profile/rewardsPreview';
import { fetchMyBets, type BetItem } from '../../features/room/api';
import { BetHistorySheet } from '../../features/bets/BetHistorySheet';
import { fetchSocialFollowers, fetchSocialFollowing } from '../../features/leaderboard/api';
import { isKycVerified, KycFormSheet } from '../../features/profile/KycSheets';
import { LanguageSwitcher } from '../../shared/i18n/LanguageSwitcher';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { couponHeadline as sharedCouponHeadline, withTrueRate } from '../../shared/wallet/couponCopy';
import { useDepositOrders } from '../../features/wallet/useDepositOrders';
import { unpaidDepositOrders } from '../../shared/wallet/unpaidDeposits';
import '../../features/wallet/unpaidDeposits.css';
import { DEFAULT_LANGUAGE, LANGUAGES, MESSAGES_PT_BR, type MessageKey } from '../../shared/i18n/messages';
import { e164Digits, offeredLanguages } from '../../shared/country/countryConfig';
import { activeCountry, useCountryStore } from '../../shared/country/countryStore';
import { CountryFlag } from '../../shared/country/CountryFlag';
import { phoneHintText } from '../../features/auth/authShared';
import { GAME_LABELS, gameNameKey, pickLabel } from '../../shared/game/betOptions';
import type { GameCode } from '../../entities/room/types';
import { SheetPortal } from '../../shared/ui/SheetPortal';
import { localClock, localDayKey, localDayShort } from '../../shared/lib/datetime';
import { fmtMoney, fmtAmount, fmtCompact, fmtInt } from '../../shared/lib/format';
import { currentUnixSeconds, formatRaffleCountdown } from '../../shared/lib/raffleCountdown';
import { avatarGroups, defaultAvatarUrl, defaultAvatarSvgDataUri } from '../../shared/profile/avatarDefaults';

export function MePage() {
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const logout = useAuthStore((state) => state.logout);
  const clearGuest = useAuthStore((state) => state.clearGuest);
  const setUser = useAuthStore((state) => state.setUser);
  const route = useAppStore((state) => state.route);
  const setRoute = useAppStore((state) => state.setRoute);
  const setAuditReturn = useAppStore((state) => state.setAuditReturn);
  const betHistoryReturn = useAppStore((state) => state.betHistoryReturn);
  const setBetHistoryReturn = useAppStore((state) => state.setBetHistoryReturn);
  const meSecurityTargetChannel = useAppStore((state) => state.meSecurityTargetChannel);
  const setMeSecurityTargetChannel = useAppStore((state) => state.setMeSecurityTargetChannel);
  const openWalletTicketsFlow = useAppStore((state) => state.openWalletTicketsFlow);
  const setFollowsTargetTab = useAppStore((state) => state.setFollowsTargetTab);
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const countryLocales = useCountryStore((state) => state.country.availableLocales);
  const setLanguage = useI18nStore((state) => state.setLanguage);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(() => route === 'bet-history');
  const [rgOpen, setRgOpen] = useState(() => route === 'me.limits');
  const [activitiesOpen, setActivitiesOpen] = useState(() => route === 'me.activities');
  const [inviteOpen, setInviteOpen] = useState(() => route === 'me.invite');
  const [securityOpen, setSecurityOpen] = useState(() => route === 'me.security');
  const [kycOpen, setKycOpen] = useState(() => route === 'me.kyc');
  const [vipOpen, setVipOpen] = useState(() => route === 'me.vip');
  const [profileOpen, setProfileOpen] = useState(() => route === 'me.profile');
  const [preferenceMessage, setPreferenceMessage] = useState('');
  const [activityNotificationConfirmTarget, setActivityNotificationConfirmTarget] = useState<boolean | null>(null);
  const [displayIdCopyMessage, setDisplayIdCopyMessage] = useState('');
  const [displayIdCopyError, setDisplayIdCopyError] = useState(false);
  const queryClient = useQueryClient();
  // Same query keys as the follows page — one cache, so counts here and the
  // list there can never disagree.
  const followersQuery = useQuery({
    queryKey: ['social-followers', token],
    queryFn: () => fetchSocialFollowers(token || ''),
    enabled: !!token && !guestMode,
  });
  const depositOrdersQuery = useDepositOrders({ poll: false });
  const unpaidCount = useMemo(
    () => unpaidDepositOrders(depositOrdersQuery.data || [], Math.floor(Date.now() / 1000)).length,
    [depositOrdersQuery.data],
  );
  const followingQuery = useQuery({
    queryKey: ['social-following', token],
    queryFn: () => fetchSocialFollowing(token || ''),
    enabled: !!token && !guestMode,
  });

  useEffect(() => {
    setHistoryOpen(route === 'bet-history');
    setProfileOpen(route === 'me.profile');
    setVipOpen(route === 'me.vip');
    setRgOpen(route === 'me.limits');
    setActivitiesOpen(route === 'me.activities');
    setInviteOpen(route === 'me.invite');
    setSecurityOpen(route === 'me.security');
    setKycOpen(route === 'me.kyc');
  }, [route]);

  const preferencesMutation = useMutation({
    mutationFn: (payload: { public_leaderboard?: boolean; activity_notifications?: boolean }) => {
      if (!token) throw new Error('UNAUTHORIZED');
      return updateMePreferences(token, payload);
    },
    onMutate: async (payload) => {
      setPreferenceMessage('');
      const previous = useAuthStore.getState().user;
      if (previous) setUser(mergePreferenceUser(previous, payload));
      return { previous };
    },
    onError: (_error, _payload, context) => {
      if (context?.previous) setUser(context.previous);
    },
    onSuccess: (payload, variables) => {
      if (payload.user) setUser(payload.user);
      setPreferenceMessage(preferenceSavedMessage(variables, t));
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  function leaveGuest(nextRoute: 'auth.login' | 'auth.register' | 'auth' = 'auth.register') {
    clearGuest();
    logout();
    setRoute(nextRoute);
  }

  function signOut() {
    if (guestMode) clearGuest();
    logout();
    setRoute('auth');
  }

  if (guestMode) {
    const guestHistoryGate = route === 'bet-history';
    const GuestIcon = guestHistoryGate ? List : UserRound;
    return (
      <section className="tab-page me-page">
        <MeHeader />
        <div className="guest-gate-panel">
          <GuestIcon size={28} aria-hidden="true" />
          <strong>{t(guestHistoryGate ? 'guestHistoryGateTitle' : 'guestProfileGateTitle')}</strong>
          <p>{t(guestHistoryGate ? 'guestHistoryGateBody' : 'guestProfileGateBody')}</p>
          <button className="primary-button" type="button" onClick={() => leaveGuest('auth.register')}>
            {t('guestGateCTA')}
          </button>
          <button className="secondary-button" type="button" onClick={() => leaveGuest('auth.login')}>
            {t('guestGateLogin')}
          </button>
          <button className="link-button guest-exit-link" type="button" onClick={() => leaveGuest('auth')}>
            {t('guestExitLink')}
          </button>
          {/* 访客从 Milhar 的「See all my bets」进来会落在这块转化墙上，而这块墙原本
              只有注册／登录／退出访客三个出口 —— 想回去看奖池的人没有路，只能去点
              底部标签栏。登记过来源就给一条回去的路。 */}
          {guestHistoryGate && betHistoryReturn ? (
            <button className="link-button guest-exit-link" type="button"
              onClick={() => { const back = betHistoryReturn; setBetHistoryReturn(null); setRoute(back); }}>
              ‹ {t('back')}
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  const displayId = userDisplayId(user);
  const totalBets = Number(user?.total_bets ?? user?.totalBets ?? 0);
  const followersCount = Number(followersQuery.data?.count ?? followersQuery.data?.items?.length ?? 0);
  const followingCount = Number(followingQuery.data?.count ?? followingQuery.data?.items?.length ?? 0);
  const openFollows = (targetTab: 'followers' | 'following') => {
    setFollowsTargetTab(targetTab);
    setRoute('me.follows');
  };
  const totalWon = Number(user?.total_won ?? user?.totalWon ?? 0);
  const totalProfit = Number(user?.total_profit_brl ?? user?.totalProfitBrl ?? 0);
  const vipProgress = Math.max(0, Math.min(100, Number(user?.vip_progress_pct ?? 0)));
  // Same rule as the VIP sheet: a level needs both halves, so the strip must
  // quote whichever is further behind — quoting the wagering shortfall alone
  // told a player blocked on deposits to go and bet.
  const nextVipBetShort = Number(user?.next_vip_exp_required ?? 0);
  const nextVipDepositShort = Number(user?.next_vip_deposit_required ?? 0);
  const nextVipAmount = Math.max(nextVipBetShort, nextVipDepositShort);
  const nextVipShortIsDeposit = nextVipDepositShort > nextVipBetShort;
  const publicLeaderboard = user?.public_leaderboard ?? user?.publicLeaderboard ?? true;
  const activityNotifications = user?.activity_notifications ?? user?.activityNotifications ?? true;

  return (
    <section className="tab-page me-page">
      <button className="me-float-back" type="button" onClick={() => setRoute('lobby')} aria-label={t('backToLobby')}>
        <ArrowLeft size={18} aria-hidden="true" />
      </button>

      <section className="me-identity-card me-identity-legacy">
        <button className="me-avatar-large profile-avatar-button" type="button" onClick={() => setRoute('me.profile')} aria-label={t('profileEditTitle')}>
          {avatarUrl(user) ? (
            <img
              src={avatarUrl(user)}
              alt=""
              onError={(e) => {
                const id = currentAvatarDefaultId(user);
                if (!id) return;
                e.currentTarget.onerror = null;
                e.currentTarget.src = defaultAvatarSvgDataUri(id);
              }}
            />
          ) : <span>{(userName(user)[0] || 'P').toUpperCase()}</span>}
          <em><Camera size={14} aria-hidden="true" /></em>
        </button>
        <button className="me-identity-main me-identity-profile-button" type="button" onClick={() => setRoute('me.profile')}>
          <strong>@{userName(user)}</strong>
        </button>
        {/* Social counts live with the identity, not in a settings row —
            the profile-header idiom players already know. */}
        <div className="me-social-stats">
          <button type="button" onClick={() => openFollows('followers')}>
            <b>{fmtInt(followersCount)}</b>
            <span>{followersCount === 1 ? t('followsFollowerOne') : t('followsFollowersTab')}</span>
          </button>
          <i aria-hidden="true" />
          <button type="button" onClick={() => openFollows('following')}>
            <b>{fmtInt(followingCount)}</b>
            <span>{t('followsFollowingTab')}</span>
          </button>
        </div>
        <button className="me-vip-summary-button tap" type="button" data-me-vip-strip-react="true" onClick={() => setVipOpen(true)}>
          <span className="me-vip-line">
            <b className="me-vip-badge">VIP {userVipLevel(user)}</b>
            <span className="me-vip-track">
              <span style={{ width: `${vipProgress}%` }} />
            </span>
            {userVipLevel(user) >= 100 ? null : <b className="me-vip-badge is-next">VIP {userVipLevel(user) + 1}</b>}
          </span>
          <small>
            {userVipLevel(user) >= 100
              ? t('vipMaxLevel')
              : `${t('vipFaltamShort').replace('%v%', fmtMoney(nextVipAmount))} ${nextVipShortIsDeposit ? t('vipShortInDeposits') : t('vipShortInBets')}`}
          </small>
        </button>
        <button className="me-hero-id" type="button" onClick={() => copyDisplayId(displayId)} aria-label={t('meIdTapHint')}>
          <span>{t('meIdLabel')}</span>
          <b>{displayId || '-'}</b>
          <Copy size={13} aria-hidden="true" />
        </button>
      </section>

      <section className="me-quick-grid">
        <QuickTile icon={Wallet} label={t('wallet')} onClick={() => setRoute('wallet')} />
        <QuickTile icon={List} label={t('meBetHistory')} onClick={() => setRoute('bet-history')} />
        <QuickTile icon={Shield} label={t('rgTitle')} onClick={() => setRoute('me.limits')} />
      </section>

      <section className="me-stats-grid">
        <div><strong className="font-mono">{fmtInt(totalBets)}</strong><span>{t('meTotalBets')}</span></div>
        <div><strong className="font-mono win-text">{fmtInt(totalWon)}</strong><span>{t('meWon')}</span></div>
        <div><strong className={`font-mono ${totalProfit > 0 ? 'win-text' : totalProfit < 0 ? 'lose-text' : ''}`}>{totalProfit < 0 ? '\u2212' : totalProfit > 0 ? '+' : ''}{fmtCompact(Math.round(Math.abs(totalProfit)), undefined, undefined, 1_000_000)}</strong><span>{t('meNetResult')}</span></div>
      </section>
      <p className="me-stats-period-note">{t('meStatsPeriodNote')}</p>

      <section className="me-menu-list">
        <PreferenceRow
          icon={publicLeaderboard ? Eye : EyeOff}
          label={t('meLeaderboardPublic')}
          enabled={publicLeaderboard}
          pending={preferencesMutation.isPending}
          onToggle={() => preferencesMutation.mutate({ public_leaderboard: !publicLeaderboard })}
        />
        <PreferenceRow
          icon={Bell}
          label={t('meActivityNotifications')}
          enabled={activityNotifications}
          pending={preferencesMutation.isPending}
          onToggle={() => setActivityNotificationConfirmTarget(!activityNotifications)}
        />
        <DevicePushPreference token={token} />
        <MenuAction icon={ShieldCheck} label={t('meRulesEntry')} onClick={() => setRoute('rules')} />
        <MenuAction icon={List} label={t('auditProgressEntry')} onClick={() => { setAuditReturn('me'); setRoute('me.audit'); }} />
        <MenuAction
          icon={ReceiptText}
          label={t('meTransactionRecords')}
          onClick={() => setRoute('me.history')}
          badge={unpaidCount}
          badgeAria={t('unpaidBadgeAria').replace('%n%', String(unpaidCount))}
        />
        <MenuAction icon={Users} label={t('meInvite')} onClick={() => setRoute('me.invite')} />
        <button className="me-menu-row" type="button" onClick={() => setLangPickerOpen(true)} data-language-row-react="true">
          <Languages size={19} aria-hidden="true" />
          <span>{t('meLanguage')}</span>
          <span className="me-menu-value">
            <small className="me-lang-name">{languageFullName(language)}</small>
            <ChevronRight size={16} aria-hidden="true" />
          </span>
        </button>
        <MenuAction icon={Lock} label={t('meSecurity')} onClick={() => setRoute('me.security')} />
        <MenuAction icon={MessageCircle} label={t('helpTitle')} onClick={() => setRoute('avisos.help')} />
        <button className="me-menu-row danger" type="button" onClick={signOut}>
          <LogOut size={19} aria-hidden="true" />
          <span>{t('signOut')}</span>
        </button>
      </section>
      {activityNotificationConfirmTarget !== null ? (
        <NotificationToggleConfirm
          kind="activity"
          nextEnabled={activityNotificationConfirmTarget}
          pending={preferencesMutation.isPending}
          onCancel={() => { if (!preferencesMutation.isPending) setActivityNotificationConfirmTarget(null); }}
          onConfirm={() => preferencesMutation.mutate(
            { activity_notifications: activityNotificationConfirmTarget },
            { onSettled: () => setActivityNotificationConfirmTarget(null) },
          )}
        />
      ) : null}

      <p className="me-footer-note">{t('meFooter')}</p>

      {preferenceMessage ? <p className="form-success preference-feedback-react" data-preference-feedback-react="true">{preferenceMessage}</p> : null}
      {preferencesMutation.isError ? <p className="form-error" data-preference-feedback-react="true">{t('preferencesFailed')}</p> : null}
      {/* Every sheet below lives at <body>, not inside this scrolling page —
          see SheetPortal for why Mobile Safari needs that. */}
      <SheetPortal>
      {langPickerOpen ? (
        <div className="sheet-backdrop" role="presentation" onMouseDown={() => setLangPickerOpen(false)}>
          <section className="profile-sheet lang-picker-sheet-react" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <div className="sheet-grabber" />
            <header className="sheet-header"><div><h2>{t('meLanguage')}</h2></div></header>
            {offeredLanguages(countryLocales, LANGUAGES, DEFAULT_LANGUAGE).map((lang) => (
              <button key={lang} className={`me-menu-row${language === lang ? ' active' : ''}`} type="button" onClick={() => { setLanguage(lang); setLangPickerOpen(false); }}>
                <span>{languageFullName(lang)}</span>
                {language === lang ? <Check size={16} aria-hidden="true" /> : null}
              </button>
            ))}
          </section>
        </div>
      ) : null}
      {profileOpen ? <ProfileSheet token={token || ''} user={user} onClose={() => { setProfileOpen(false); if (route === 'me.profile') setRoute('me'); }} /> : null}
      {vipOpen ? <VipSheet user={user} onClose={() => { setVipOpen(false); if (route === 'me.vip') setRoute('me'); }} /> : null}
      {historyOpen ? (
        <BetHistorySheet
          token={token || ''}
          onClose={() => {
            setHistoryOpen(false);
            if (route !== 'bet-history') return;
            // 谁送进来就回谁那儿；没人登记（从 Me 页自己点进来的）才回 Me
            const back = betHistoryReturn;
            setBetHistoryReturn(null);
            setRoute(back || 'me');
          }}
        />
      ) : null}
      {rgOpen ? <ResponsibleGamingSheet token={token || ''} onClose={() => { setRgOpen(false); if (route === 'me.limits') setRoute('me'); }} /> : null}
      {securityOpen ? (
        <SecuritySheet
          token={token || ''}
          user={user}
          initialVerifyChannel={meSecurityTargetChannel}
          onClose={() => {
            setSecurityOpen(false);
            setMeSecurityTargetChannel(null);
            if (route === 'me.security') setRoute('me');
          }}
          onOpenKyc={() => {
            setSecurityOpen(false);
            setRoute('me.kyc');
          }}
        />
      ) : null}
      {kycOpen ? (
        <KycFormSheet
          user={user}
          onApproved={(updatedUser) => {
            if (updatedUser) setUser(updatedUser);
            queryClient.invalidateQueries({ queryKey: ['me'] });
          }}
          approvedActionLabel={t('close')}
          onClose={() => {
            setKycOpen(false);
            if (route === 'me.kyc') setRoute('me');
          }}
        />
      ) : null}
      {activitiesOpen ? (
        <ActivitiesSheet
          token={token || ''}
          onClose={() => { setActivitiesOpen(false); if (route === 'me.activities') setRoute('me'); }}
          onRoute={(route) => {
            setActivitiesOpen(false);
            setRoute(route);
          }}
          onVerifyContact={(channel) => {
            setActivitiesOpen(false);
            setMeSecurityTargetChannel(channel ?? null);
            setRoute(channel ? 'me.security' : 'me.kyc');
          }}
          onRaffle={(view, resultId) => {
            setActivitiesOpen(false);
            // Land in the Sorteios venue, where the edition and draw time are
            // visible — never the bare tickets page. A settled draw still
            // deep-links straight to its result.
            if (view === 'result') openWalletTicketsFlow('result', resultId ?? null);
            else setRoute('sorteios');
          }}
          onInvite={() => {
            setActivitiesOpen(false);
            setRoute('me.invite');
          }}
        />
      ) : null}
      {inviteOpen ? <InviteSheet user={user} displayId={displayId} onClose={() => { setInviteOpen(false); if (route === 'me.invite') setRoute('me'); }} /> : null}
      </SheetPortal>
    </section>
  );

  async function copyDisplayId(value: string) {
    if (!value) return;
    const ok = await copyText(value);
    setDisplayIdCopyError(!ok);
    setDisplayIdCopyMessage(t(ok ? 'idCopied' : 'idCopyFail'));
    window.setTimeout(() => setDisplayIdCopyMessage(''), 1800);
  }
}

function ProfileSheet({ token, user, onClose }: { token: string; user: PlayerUser | null; onClose: () => void }) {
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const suggestSeq = useRef(0);
  const setUser = useAuthStore((state) => state.setUser);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const avatarChipsRef = useRef<HTMLDivElement | null>(null);
  const cropPointersRef = useRef(new Map<number, { x: number; y: number }>());
  const cropDragRef = useRef<{ pointerId: number; startX: number; startY: number; baseX: number; baseY: number } | null>(null);
  const cropPinchRef = useRef<{ dist: number; baseScale: number } | null>(null);
  const [usernameInput, setUsernameInput] = useState(userName(user));
  const [usernameError, setUsernameError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [avatarFileError, setAvatarFileError] = useState('');
  const [avatarCrop, setAvatarCrop] = useState<AvatarCropState | null>(null);
  const defaultId = currentAvatarDefaultId(user);
  // Only one group is rendered at a time — 100 tiles in a sheet is a scroll
  // marathon. Open on the group holding the avatar the player is wearing.
  const [avatarGroupKey, setAvatarGroupKey] = useState<string>(() => {
    const groups = avatarGroups();
    const worn = defaultId ? groups.find((group) => group.ids.includes(defaultId)) : null;
    return (worn || groups[0]).key;
  });
  const visibleGroup = avatarGroups().find((group) => group.key === avatarGroupKey) || avatarGroups()[0];
  const canChange = usernameChangeGate(user);
  const liveValidation = usernameInput === userName(user) ? { ok: true as const } : validateUsername(usernameInput);
  const canSubmitUsername = !!token &&
    canChange.ok &&
    usernameInput.trim() !== userName(user) &&
    liveValidation.ok;

  const usernameMutation = useMutation({
    mutationFn: () => updateMeUsername(token, { username: usernameInput.trim() }),
    onSuccess: (payload) => {
      const nextUser = payload.user || mergeUsernameUser(user, payload, usernameInput.trim());
      if (nextUser) setUser(nextUser);
      queryClient.invalidateQueries({ queryKey: ['me', token] });
      setUsernameError('');
      setStatusMessage(t('usernameSaved'));
    },
    onError: (error) => {
      setUsernameError(usernameServerError(error, t));
    },
  });

  const avatarMutation = useMutation({
    mutationFn: (payload: { type: 'default'; id: number } | { type: 'remove' } | { type: 'upload'; dataUrl: string }) => {
      if (payload.type === 'remove') return updateMeAvatarData(token, '');
      if (payload.type === 'upload') return updateMeAvatarData(token, payload.dataUrl);
      return updateMeDefaultAvatar(token, payload.id);
    },
    onSuccess: (payload, variables) => {
      const nextUser = payload.user || mergeAvatarUser(user, variables);
      if (nextUser) setUser(nextUser);
      queryClient.invalidateQueries({ queryKey: ['me', token] });
      setStatusMessage(t(variables.type === 'remove' ? 'avatarRemoved' : 'avatarSaved'));
      setAvatarCrop(null);
      setAvatarFileError('');
    },
  });

  useEffect(() => {
    avatarChipsRef.current?.querySelector('.active')?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, []);

  function handleUsernameInput(value: string) {
    // Discard any in-flight 🎲 suggestion once the player types — otherwise a
    // slow round trip can land after they've started editing and clobber it.
    suggestSeq.current += 1;
    setUsernameInput(foldAccents(value).slice(0, USERNAME_MAX_LEN));
    setUsernameError('');
    setStatusMessage('');
  }

  async function suggestUsername() {
    const seq = ++suggestSeq.current;
    setUsernameError('');
    setStatusMessage('');
    const pool = poolForAccount(user, language);
    const name = await suggestAvailableUsername(pool, (candidate) => checkUsername(token, candidate).then((r) => r.available));
    if (seq === suggestSeq.current) setUsernameInput(name);
  }

  function handleAvatarFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    setAvatarFileError('');
    setStatusMessage('');
    avatarMutation.reset();
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setAvatarFileError(t('avatarErrFormat'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setAvatarFileError(t('avatarErrTooBig'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const src = String(reader.result || '');
      const image = new Image();
      image.onload = () => {
        const baseScale = Math.max(AVATAR_CROP_SIZE / image.naturalWidth, AVATAR_CROP_SIZE / image.naturalHeight);
        setAvatarCrop({
          src,
          image,
          imageWidth: image.naturalWidth,
          imageHeight: image.naturalHeight,
          baseScale,
          scale: 1,
          x: 0,
          y: 0,
        });
      };
      image.onerror = () => setAvatarFileError(t('avatarErrLoad'));
      image.src = src;
    };
    reader.onerror = () => setAvatarFileError(t('avatarErrLoad'));
    reader.readAsDataURL(file);
  }

  function setCropScale(value: number) {
    setAvatarCrop((crop) => crop ? clampAvatarCrop({ ...crop, scale: Math.max(1, Math.min(4, value)) }) : crop);
  }

  function handleCropPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (!avatarCrop) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    cropPointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = Array.from(cropPointersRef.current.values());
    if (points.length === 1) {
      cropDragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        baseX: avatarCrop.x,
        baseY: avatarCrop.y,
      };
      cropPinchRef.current = null;
    } else if (points.length === 2) {
      cropDragRef.current = null;
      cropPinchRef.current = {
        dist: Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y),
        baseScale: avatarCrop.scale,
      };
    }
  }

  function handleCropPointerMove(event: PointerEvent<HTMLDivElement>) {
    if (!avatarCrop || !cropPointersRef.current.has(event.pointerId)) return;
    event.preventDefault();
    cropPointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = Array.from(cropPointersRef.current.values());
    if (points.length === 2 && cropPinchRef.current) {
      const dist = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
      setCropScale(cropPinchRef.current.baseScale * (dist / Math.max(1, cropPinchRef.current.dist)));
      return;
    }
    const drag = cropDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setAvatarCrop((crop) => crop ? clampAvatarCrop({
      ...crop,
      x: drag.baseX + event.clientX - drag.startX,
      y: drag.baseY + event.clientY - drag.startY,
    }) : crop);
  }

  function handleCropPointerUp(event: PointerEvent<HTMLDivElement>) {
    cropPointersRef.current.delete(event.pointerId);
    if (cropDragRef.current?.pointerId === event.pointerId) cropDragRef.current = null;
    if (cropPointersRef.current.size < 2) cropPinchRef.current = null;
  }

  function closeCrop() {
    cropPointersRef.current.clear();
    cropDragRef.current = null;
    cropPinchRef.current = null;
    setAvatarCrop(null);
  }

  function saveCroppedAvatar() {
    if (!avatarCrop || avatarMutation.isPending) return;
    const dataUrl = renderAvatarCrop(avatarCrop);
    if (!dataUrl) {
      setAvatarFileError(t('avatarErrLoad'));
      return;
    }
    setStatusMessage(t('avatarSaving'));
    avatarMutation.mutate({ type: 'upload', dataUrl });
  }

  const validationMessage = !canChange.ok
    ? t('usernameErrCooldown').replace('%date%', formatDateShort(canChange.nextAt))
    : usernameError || (!liveValidation.ok ? usernameValidationMessage(liveValidation.code, t) : '');

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet profile-edit-sheet" role="dialog" aria-modal="true" aria-label={t('profileEditTitle')} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('profileEditTitle')}</h2>
            <p>{t('profileEditSub')}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            ×
          </button>
        </header>

        <div className="profile-edit-body">
          {/* What you look like right now — otherwise the sheet hides the very
              thing being edited, and "which avatar am I wearing?" costs a
              100-tile scroll. */}
          <div className="profile-edit-preview">
            <span className="profile-edit-avatar">
              {avatarUrl(user) ? (
                <img
                  src={avatarUrl(user)}
                  alt=""
                  onError={(e) => {
                    const id = currentAvatarDefaultId(user);
                    if (!id) return;
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = defaultAvatarSvgDataUri(id);
                  }}
                />
              ) : <b>{(userName(user)[0] || 'P').toUpperCase()}</b>}
            </span>
            <strong>@{userName(user)}</strong>
            {avatarUrl(user) ? (
              <button className="profile-edit-remove" type="button" disabled={avatarMutation.isPending} onClick={() => avatarMutation.mutate({ type: 'remove' })}>
                {t('avatarRemove')}
              </button>
            ) : null}
          </div>

          <section className="profile-edit-card">
            <span className="profile-section-label">{t('usernameEditTitle')}</span>
            {/* Renaming is the one irreversible thing here — say so before the
                field, not in fine print under it. */}
            {canChange.viaGrace ? <span className="rename-chip is-free">{t('usernameGraceBadge')}</span> : null}
            {canChange.ok && !canChange.viaGrace ? <span className="rename-chip">{t('usernameNextChangeNotice')}</span> : null}
            <div className="username-edit-row">
              <label className="field username-field">
                <span>{t('usernameFieldLabel')}</span>
                <span className="username-input-wrap">
                  <i aria-hidden="true">@</i>
                  <input
                    value={usernameInput}
                    autoComplete="username"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={USERNAME_MAX_LEN}
                    placeholder={t('usernamePlaceholder')}
                    disabled={!canChange.ok || usernameMutation.isPending}
                    onChange={(event) => handleUsernameInput(event.target.value)}
                  />
                </span>
              </label>
              <button className="username-suggest-button" type="button" onClick={suggestUsername} disabled={usernameMutation.isPending || !canChange.ok} aria-label={t('usernameSuggestBtn')}>
                <Dices size={16} aria-hidden="true" />
                <span>{t('usernameSuggestShort')}</span>
              </button>
            </div>
            <p className={validationMessage ? 'form-error' : 'profile-hint'}>
              {validationMessage || t(usernameInput.trim() === userName(user) ? 'usernameRulesShort' : 'usernameRulesInline')}
            </p>
            {/* No dead slab: the button exists only once there is something to save. */}
            {usernameInput.trim() !== userName(user) ? (
              <button className="primary-button sheet-submit" type="button" disabled={!canSubmitUsername || usernameMutation.isPending} onClick={() => usernameMutation.mutate()}>
                <Pencil size={16} aria-hidden="true" />
                {t(usernameMutation.isPending ? 'submitting' : 'usernameSubmit')}
              </button>
            ) : null}
          </section>

          <section className="profile-edit-card" data-avatar-picker-sheet-react="true">
            <span className="profile-section-label">{t('avatarPickerTitle')}</span>
            <p className="profile-hint">{t('avatarPickerSub')}</p>
            {avatarUrl(user) && !defaultId ? (
              <div className="avatar-current-card">
                <img src={avatarUrl(user)} alt="" />
                <div>
                  <strong>{t('avatarCurrentLabel')}</strong>
                  <span>{t('avatarCurrentHint')}</span>
                </div>
              </div>
            ) : null}
            <button className="avatar-upload-tile" type="button" disabled={avatarMutation.isPending} onClick={() => fileInputRef.current?.click()} data-avatar-upload-trigger-react="true">
              <Camera size={22} aria-hidden="true" />
              <span>
                <strong>{t('avatarUploadCta')}</strong>
                <small>{t('avatarUploadHint')}</small>
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              data-avatar-upload-input-react="true"
              onChange={handleAvatarFile}
            />
            {avatarFileError ? <p className="form-error">{avatarFileError}</p> : null}
            <div className="avatar-group-chips" role="tablist" aria-label={t('avatarPickerTitle')} ref={avatarChipsRef}>
              {avatarGroups().map((group) => (
                <button
                  type="button"
                  role="tab"
                  key={group.key}
                  aria-selected={group.key === visibleGroup.key}
                  className={group.key === visibleGroup.key ? 'active' : ''}
                  onClick={() => setAvatarGroupKey(group.key)}
                >
                  {t(avatarGroupLabelKey(group.key))}
                </button>
              ))}
            </div>
            <div className="avatar-grid" data-avatar-default-grid-react="true">
              {visibleGroup.ids.map((id) => (
                <button
                  className={defaultId === id ? 'active' : ''}
                  type="button"
                  key={id}
                  disabled={avatarMutation.isPending}
                  onClick={() => avatarMutation.mutate({ type: 'default', id })}
                  aria-label={`${t('avatarPickerTitle')} ${id}`}
                  data-avatar-default-option-react="true"
                >
                  <img
                    src={defaultAvatarUrl(id)}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = defaultAvatarSvgDataUri(id);
                    }}
                  />
                </button>
              ))}
            </div>
          </section>

          {statusMessage ? <p className="form-success">{statusMessage}</p> : null}
          {avatarMutation.isError && !avatarCrop ? <p className="form-error">{t('avatarErrLoad')}</p> : null}
        </div>
      </section>
      {avatarCrop ? (
        <AvatarCropSheet
          crop={avatarCrop}
          pending={avatarMutation.isPending}
          error={avatarMutation.isError ? t('avatarErrLoad') : ''}
          onPointerDown={handleCropPointerDown}
          onPointerMove={handleCropPointerMove}
          onPointerUp={handleCropPointerUp}
          onScaleChange={setCropScale}
          onClose={closeCrop}
          onSave={saveCroppedAvatar}
        />
      ) : null}
    </div>
  );
}

function AvatarCropSheet({
  crop,
  pending,
  error,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onScaleChange,
  onClose,
  onSave,
}: {
  crop: AvatarCropState;
  pending: boolean;
  error: string;
  onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
  onScaleChange: (value: number) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  return (
    <div className="sheet-backdrop nested-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet avatar-crop-sheet" role="dialog" aria-modal="true" aria-label={t('avatarCropTitle')} onMouseDown={(event) => event.stopPropagation()} data-avatar-crop-sheet-react="true">
        <header className="avatar-crop-header">
          <button type="button" onClick={onClose} disabled={pending}>{t('avatarCropCancel')}</button>
          <strong>{t('avatarCropTitle')}</strong>
          <button type="button" onClick={onSave} disabled={pending}>{t(pending ? 'avatarSaving' : 'avatarCropSave')}</button>
        </header>
        <p className="profile-hint centered">{t('avatarCropHint')}</p>
        <div
          className="avatar-crop-stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-avatar-crop-stage-react="true"
        >
          <img
            src={crop.src}
            alt=""
            draggable={false}
            style={{ transform: avatarCropTransform(crop) }}
          />
          <div className="avatar-crop-mask" />
        </div>
        <label className="avatar-zoom-control">
          <span>{t('avatarCropZoomLabel')}</span>
          <input
            type="range"
            min="1"
            max="4"
            step="0.05"
            value={crop.scale}
            disabled={pending}
            onChange={(event) => onScaleChange(Number(event.target.value))}
          />
        </label>
        {error ? <p className="form-error avatar-crop-error-react" data-avatar-crop-error-react="true">{error}</p> : null}
      </section>
    </div>
  );
}

function SecuritySheet({
  token,
  user,
  initialVerifyChannel,
  onClose,
  onOpenKyc,
}: {
  token: string;
  user: PlayerUser | null;
  initialVerifyChannel?: 'phone' | 'email' | null;
  onClose: () => void;
  onOpenKyc: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const setUser = useAuthStore((state) => state.setUser);
  const country = useCountryStore((state) => state.country);
  const queryClient = useQueryClient();
  const registrationSecurity = useRegistrationSecurity();
  const [verifyChannel, setVerifyChannel] = useState<'phone' | 'email' | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const query = useQuery({
    queryKey: ['security-sessions', token],
    queryFn: () => fetchSecuritySessions(token),
    enabled: !!token,
  });
  const sessions = useMemo(() => normalizeSecuritySessions(query.data?.sessions || []), [query.data?.sessions]);
  const phoneVerified = Boolean(user?.phone_verified ?? user?.phoneVerified);
  const emailVerified = Boolean(user?.email_verified ?? user?.emailVerified);
  const kycVerified = isKycVerified(user);
  const strength = securityStrengthLabel(phoneVerified, emailVerified, t);
  const mutation = useMutation({
    mutationFn: (jti: string) => revokeSecuritySession(token, jti),
    onSuccess: (payload) => {
      queryClient.setQueryData(['security-sessions', token], payload);
      queryClient.invalidateQueries({ queryKey: ['security-sessions', token] });
    },
  });

  useEffect(() => {
    if (initialVerifyChannel) setVerifyChannel(initialVerifyChannel);
  }, [initialVerifyChannel]);

  if (verifyChannel) {
    return (
      <ContactVerifySheet
        token={token}
        channel={verifyChannel}
        user={user}
        onClose={() => setVerifyChannel(null)}
        onDone={(updatedUser) => {
          if (updatedUser) setUser(updatedUser);
          queryClient.invalidateQueries({ queryKey: ['me'] });
          queryClient.invalidateQueries({ queryKey: ['security-sessions', token] });
          setVerifyChannel(null);
        }}
      />
    );
  }

  if (passwordOpen) {
    return (
      <ChangePasswordSheet
        user={user}
        onClose={() => setPasswordOpen(false)}
      />
    );
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet security-sheet-react" role="dialog" aria-modal="true" aria-label={t('securityTitle')} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('securityTitle')}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>

        <div className="profile-sheet-list">
          <section className="security-strength-card">
            <div>
              <span>{t('securitySettingsLabel')}</span>
            </div>
            <b className={`security-strength-pill ${strength.tone}`}>{strength.label}</b>
          </section>

          <SecurityCheckCard
            icon={Lock}
            title={t('securityPasswordTitle')}
            body={t('securityPasswordBody')}
            verified
            value={t('securityPasswordReady')}
            actionLabel={t('securityPasswordCta')}
            onAction={() => setPasswordOpen(true)}
          />
          {registrationSecurity.data?.emailEnabled && <SecurityCheckCard
            icon={MessageCircle}
            title={t('securityEmailTitle')}
            body={emailVerified ? t('securityEmailBodyDone') : t('securityEmailBodyTodo')}
            verified={emailVerified}
            value={user?.email || ''}
            actionLabel={emailVerified ? t('securityEmailCtaChange') : user?.email ? t('securityEmailCtaVerify') : t('securityEmailCtaAdd')}
            onAction={() => setVerifyChannel('email')}
          />}
          {registrationSecurity.data?.smsEnabled && <SecurityCheckCard
            icon={Phone}
            title={t('securityPhoneTitle')}
            body={phoneVerified ? t('securityPhoneBodyDone') : t('securityPhoneBodyTodo')}
            verified={phoneVerified}
            value={user?.phone_e164 ? displayPhone(user.phone_e164, country) : (user?.phone || '')}
            actionLabel={phoneVerified ? t('securityPhoneCtaChange') : user?.phone ? t('securityPhoneCtaVerify') : t('securityPhoneCtaAdd')}
            onAction={() => setVerifyChannel('phone')}
          />}
          <div className="security-section-label">{t('securitySessionsLabel')}</div>
          {query.isLoading ? <SheetState icon={RefreshCw} title={t('loading')} /> : null}
          {query.isError ? <SheetState icon={HelpCircle} title={t('securityLoadFailed')} actionLabel={t('retry')} onAction={() => query.refetch()} /> : null}
          {!query.isLoading && !query.isError && sessions.length === 0 ? <SheetState icon={ShieldCheck} title={t('securityEmpty')} /> : null}
          {sessions.map((session) => (
            <SecuritySessionRow
              key={session.jti}
              session={session}
              pending={mutation.isPending}
              onRevoke={() => mutation.mutate(session.jti)}
            />
          ))}
          {mutation.isError ? <p className="form-error">{t('securityRevokeFailed')}</p> : null}
        </div>
      </section>
    </div>
  );
}

function ChangePasswordSheet({ user, onClose }: { user: PlayerUser | null; onClose: () => void }) {
  const t = useI18nStore((state) => state.t);
  const logout = useAuthStore((state) => state.logout);
  const setRoute = useAppStore((state) => state.setRoute);
  const identifier = passwordResetIdentifier(user);
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sent, setSent] = useState(false);
  const [success, setSuccess] = useState('');
  const passwordValid = /^(?=.*[A-Za-z])(?=.*\d).{6,32}$/.test(password);
  const sendMutation = useMutation({
    mutationFn: () => {
      if (!identifier.value) throw new Error('IDENTIFIER_MISSING');
      return sendForgotPasswordOtp(identifier.value);
    },
    onSuccess: () => {
      setSent(true);
      setSuccess(t('authOtpSent'));
    },
  });
  const resetMutation = useMutation({
    mutationFn: () => {
      if (!identifier.value) throw new Error('IDENTIFIER_MISSING');
      return resetForgotPassword({ identifier: identifier.value, password, otp });
    },
    onSuccess: () => {
      setSuccess(t('securityPasswordChangedLogin'));
      window.setTimeout(() => {
        logout();
        setRoute('auth.login');
      }, 900);
    },
  });

  function close() {
    if (sendMutation.isPending || resetMutation.isPending) return;
    onClose();
  }

  return (
    <div className="sheet-backdrop nested-backdrop" role="presentation" onMouseDown={close}>
      <section className="profile-sheet change-password-sheet-react" role="dialog" aria-modal="true" aria-label={t('securityPasswordCta')} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('securityPasswordCta')}</h2>
            <p>{t('securityPasswordResetSubtitle')}</p>
          </div>
          <button className="icon-button" type="button" onClick={close} aria-label={t('close')}>×</button>
        </header>

        <div className="verify-contact-form">
          <label>
            <span>{t('securityPasswordIdentifier')}</span>
            <input value={identifier.label || t('securityPasswordIdentifierMissing')} disabled />
          </label>
          {!identifier.value ? <p className="form-error">{t('securityPasswordIdentifierMissing')}</p> : null}
          <button className="secondary-button" type="button" disabled={!identifier.value || sendMutation.isPending || resetMutation.isPending} onClick={() => sendMutation.mutate()}>
            {sendMutation.isPending ? t('submitting') : sent ? t('sendOtp') : t('sendOtp')}
          </button>

          {sent ? (
            <>
              <label>
                <span>{t('otpFieldLabel')}</span>
                <input
                  value={otp}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </label>
              <label>
                <span>{t('securityPasswordNewLabel')}</span>
                <div className="password-row">
                  <input
                    value={password}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={t('passwordRegisterPlaceholder')}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={t('showPassword')}>
                    {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                  </button>
                </div>
              </label>
              <small>{t('passwordRegisterPlaceholder')}</small>
              <button className="primary-button" type="button" disabled={!passwordValid || otp.length < 4 || resetMutation.isPending} onClick={() => resetMutation.mutate()}>
                {resetMutation.isPending ? t('submitting') : t('forgotAction')}
              </button>
            </>
          ) : null}

          {success ? <p className="form-success">{success}</p> : null}
          {sendMutation.isError || resetMutation.isError ? <p className="form-error">{t('authForgotFail')}</p> : null}
        </div>
      </section>
    </div>
  );
}

function SecurityCheckCard({
  icon: Icon,
  title,
  body,
  verified,
  value,
  actionLabel,
  onAction,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
  verified: boolean;
  value: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <article className="security-check-card">
      <div className="security-check-icon"><Icon size={19} aria-hidden="true" /></div>
      <div>
        <strong>{title}</strong>
        <span>{body}</span>
        <small>{value}</small>
      </div>
      <b className={verified ? 'verified' : 'pending'}>{verified ? 'OK' : '!'}</b>
      {actionLabel && onAction ? (
        <button className="security-card-action" type="button" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </article>
  );
}

export function ContactVerifySheet({
  token,
  channel,
  user,
  onClose,
  onDone,
}: {
  token: string;
  channel: 'phone' | 'email';
  user: PlayerUser | null;
  onClose: () => void;
  onDone: (user?: PlayerUser) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const country = useCountryStore((state) => state.country);
  // 预填本人真实注册号（后端 phone_e164 明文完整号），可直接编辑。Email 未打码亦预填。
  const [value, setValue] = useState(() => (channel === 'email' ? (user?.email || '') : phonePlan(country).format(user?.phone_e164 || '', country)));
  const [otp, setOtp] = useState('');
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);
  const [rewardGranted, setRewardGranted] = useState(false);
  const [expiresIn, setExpiresIn] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const [errorKey, setErrorKey] = useState<MessageKey | ''>('');
  const doneUser = useRef<PlayerUser | undefined>(undefined);
  const otpInput = useRef<HTMLInputElement>(null);

  const verified = channel === 'phone'
    ? Boolean(user?.phone_verified ?? user?.phoneVerified)
    : Boolean(user?.email_verified ?? user?.emailVerified);
  const titleKey: MessageKey = verified
    ? (channel === 'phone' ? 'securityPhoneCtaChange' : 'securityEmailCtaChange')
    : (channel === 'phone' ? 'verifySheetTitlePhone' : 'verifySheetTitleEmail');
  // Reward promise comes from the REAL bind_contact activity config (same source
  // as the rewards center), not a hardcoded "1 bilhete" (D-08 alignment). Shares
  // the parent's cached query by key.
  const activitiesQuery = useQuery({
    queryKey: ['activities-summary', token],
    queryFn: () => fetchActivitiesSummary(token),
    enabled: !!token,
  });
  const rewardLabel = bindContactRewardLabel(activitiesQuery.data?.configs, channel, t);
  const showRewardPromise = !verified && !!rewardLabel;
  const subtitleText = verified
    ? t(channel === 'phone' ? 'verifyChangePhoneHint' : 'verifyChangeEmailHint')
    : (rewardLabel ? t('verifyIntroRewardAmount').replace('%v%', rewardLabel) : t('verifyIntroPlain'));

  const body = channel === 'phone' ? { phone: e164Digits(value, country) } : { email: value.trim().toLowerCase() };
  const valueValid = channel === 'phone' ? phonePlan(country).isValid(value, country) : isEmailValid(value);
  const shownContact = channel === 'phone' ? `+${country.phonePrefix} ${value}`.trim() : value.trim();
  const step = done ? 3 : sent ? 2 : 1;
  const fieldInvalid = channel === 'phone' && !valueValid && e164Digits(value, country).length >= 5;

  const sendMutation = useMutation({
    mutationFn: () => sendContactOtp(token, body),
    onSuccess: (payload) => {
      setErrorKey('');
      setSent(true);
      setOtp('');
      setExpiresIn(Number(payload.code_expires_in_s || payload.expires_in || 600));
      setCooldown(Math.ceil(Number(payload.retry_after_ms || 60000) / 1000));
      window.setTimeout(() => otpInput.current?.focus(), 60);
    },
    onError: (error) => {
      // 发码失败不是「检查验证码」（D-70）；429 带上等待时长做倒计时（D-71）。
      const info = contactOtpError(error, 'send');
      setErrorKey(info.key);
      if (info.cooldownSec > 0) setCooldown(info.cooldownSec);
    },
  });
  const confirmMutation = useMutation({
    mutationFn: () => confirmContactOtp(token, { ...body, otp }),
    onSuccess: (payload) => {
      setErrorKey('');
      setRewardGranted(Boolean(payload.reward_granted));
      doneUser.current = payload.user;
      setDone(true);
    },
    onError: (error) => setErrorKey(contactOtpError(error, 'confirm').key),
  });

  useEffect(() => {
    // 有验证码在跑、或冷却在倒数时都要走定时器——被限流的发码没有 sent 也要能倒计时。
    if (!sent && cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setExpiresIn((v) => Math.max(0, v - 1));
      setCooldown((v) => Math.max(0, v - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [sent, cooldown > 0]);

  function handleValueChange(next: string) {
    setValue(channel === 'phone' ? phonePlan(country).format(next, country) : next.slice(0, 254));
    setOtp('');
    setSent(false);
    setErrorKey('');
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet vc-sheet" role="dialog" aria-modal="true" aria-label={t(titleKey)} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        <div className="vc-top">
          <div className="vc-badge" aria-hidden="true">
            {channel === 'phone' ? (
              <svg viewBox="0 0 24 24" fill="none"><rect x="6.5" y="2.5" width="11" height="19" rx="2.6" stroke="#34d399" strokeWidth="1.5" /><path d="M9.5 4.6h5" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" /><path d="m14.9 12-3.2 3.2L10 13.6" stroke="#f5c451" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none"><rect x="3.5" y="5.5" width="17" height="13" rx="2.6" stroke="#34d399" strokeWidth="1.5" /><path d="m4.6 7.5 7.4 5.4 7.4-5.4" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="m14.9 15.4 1.7 1.6 3-3" stroke="#f5c451" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            )}
          </div>
          <div className="vc-tt">
            <h2>{t(titleKey)}</h2>
            <p className={showRewardPromise ? 'vc-reward' : ''}>{subtitleText}</p>
          </div>
          <button className="vc-x" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </div>

        <div className="vc-steps" aria-hidden="true">
          <div className={`vc-s${step > 1 ? ' done' : step === 1 ? ' active' : ''}`}><span className="vc-bead">{step > 1 ? '✓' : '1'}</span><span className="vc-lb">{t(channel === 'phone' ? 'verifyStepNumber' : 'verifyStepEmail')}</span></div>
          <div className={`vc-bar${step > 1 ? ' fill' : ''}`}><i /></div>
          <div className={`vc-s${step > 2 ? ' done' : step === 2 ? ' active' : ''}`}><span className="vc-bead">{step > 2 ? '✓' : '2'}</span><span className="vc-lb">{t('verifyStepCode')}</span></div>
          <div className={`vc-bar${step > 2 ? ' fill' : ''}`}><i /></div>
          <div className={`vc-s${step === 3 ? ' active' : ''}`}><span className="vc-bead">3</span><span className="vc-lb">{t('verifyStepDone')}</span></div>
        </div>

        {step === 1 ? (
          <div className="vc-body">
            <p className="vc-lab">{channel === 'phone' ? t('verifyFieldPhone') : t('securityEmailTitle')}</p>
            {channel === 'phone' ? (
              <div className={`vc-tel${fieldInvalid ? ' bad' : ''}`}>
                <span className="vc-pre" title={t('verifyCountryLocked')}>
                  <CountryFlag code={country.code} size={23} /><span className="vc-cc">+{country.phonePrefix}</span>
                  <span className="vc-lock"><svg viewBox="0 0 24 24" fill="none"><rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" /></svg></span>
                </span>
                <span className="vc-sep" />
                <input value={value} inputMode="tel" autoComplete="tel" aria-label={t('verifyFieldPhone')} onChange={(event) => handleValueChange(event.target.value)} />
                {value ? <button className="vc-clr" type="button" onClick={() => handleValueChange('')} aria-label={t('clear')}>×</button> : null}
              </div>
            ) : (
              <div className="vc-tel plain">
                <input value={value} inputMode="email" autoComplete="email" aria-label={t('securityEmailTitle')} onChange={(event) => handleValueChange(event.target.value)} />
                {value ? <button className="vc-clr" type="button" onClick={() => handleValueChange('')} aria-label={t('clear')}>×</button> : null}
              </div>
            )}
            <div className={`vc-hint${errorKey ? ' err' : (channel === 'phone' && valueValid ? ' ok' : '')}`}>
              {errorKey ? t(errorKey) : (channel === 'phone' ? (valueValid ? t('verifyNumberValid') : (phoneHintText(value, country, t) || t(phonePlan(country).introHint))) : '')}
            </div>
            <button className="vc-btn primary" type="button" disabled={!valueValid || cooldown > 0 || sendMutation.isPending} onClick={() => sendMutation.mutate()}>
              {cooldown > 0 ? `${t('sendOtp')} ${cooldown}s` : sendMutation.isPending ? t('submitting') : t('sendOtp')}
            </button>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="vc-body">
            <p className="vc-sentto">{t('verifySentTo').replace('%v%', shownContact)}</p>
            <div className={`vc-otp${errorKey ? ' bad' : ''}`} onClick={() => otpInput.current?.focus()}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`vc-cell${i === otp.length ? ' on' : ''}`}>{otp[i] ? otp[i] : (i === otp.length ? <span className="vc-car" /> : '')}</div>
              ))}
              <input ref={otpInput} className="vc-otp-in" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp} aria-label={t('otpFieldLabel')} onChange={(event) => { setOtp(event.target.value.replace(/\D/g, '').slice(0, 6)); setErrorKey(''); }} />
            </div>
            {errorKey ? <div className="vc-hint err">{t(errorKey)}</div> : null}
            <div className="vc-resend">
              <span>{otpExpiryLabel(expiresIn, t)}</span>
              <button type="button" disabled={cooldown > 0 || sendMutation.isPending} onClick={() => sendMutation.mutate()}>
                {cooldown > 0 ? t('verifyResendIn').replace('%s%', String(cooldown)) : t('verifyResend')}
              </button>
            </div>
            <button className="vc-btn primary" type="button" disabled={otp.length < 4 || confirmMutation.isPending} onClick={() => confirmMutation.mutate()}>
              {confirmMutation.isPending ? t('submitting') : t('verifyConfirmBtn')}
            </button>
            <button className="vc-btn ghost" type="button" onClick={() => { setSent(false); setOtp(''); setErrorKey(''); }}>{t(channel === 'phone' ? 'verifyChangeNumberBtn' : 'verifyChangeEmailBtn')}</button>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="vc-ok">
            <div className="vc-ok-ring" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="m5 12.8 4.2 4.2L19 7.2" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
            <h3>{verified ? t('verifyDoneChangeTitle') : t('verifyDoneVerifyTitle')}</h3>
            <p>{verified ? t('verifyDoneChangeBody').replace('%v%', shownContact) : t('verifyDoneVerifyBody')}</p>
            {rewardGranted ? (
              <div className="vc-ok-note reward">
                <span className="vc-gift" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><rect x="3.5" y="8.5" width="17" height="4.2" rx="1" stroke="currentColor" strokeWidth="1.6" /><path d="M5 12.7V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7.3M12 8.5V21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /><path d="M12 8.5S10.6 4 8.4 4.6C6.6 5.1 7.2 8.5 12 8.5Zm0 0S13.4 4 15.6 4.6C17.4 5.1 16.8 8.5 12 8.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg></span>
                <span>{rewardLabel ? t('verifyRewardGot').replace('%v%', rewardLabel) : t('verifyRewardToast')}</span>
              </div>
            ) : null}
            <button className="vc-btn primary" type="button" onClick={() => onDone(doneUser.current)}>{t('verifyDoneCta')}</button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function SecuritySessionRow({
  session,
  pending,
  onRevoke,
}: {
  session: SecuritySession;
  pending: boolean;
  onRevoke: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const active = session.active !== false;
  return (
    <article className="security-session-row">
      <div className="security-session-top">
        <span className={session.current ? 'current' : ''}>
          {session.current ? <ShieldCheck size={18} aria-hidden="true" /> : <UserRound size={18} aria-hidden="true" />}
        </span>
        <div>
          <strong>{sessionDeviceLabel(session)}</strong>
          <small>{session.current ? t('securityCurrent') : active ? t('securityActive') : t('securityEnded')}</small>
        </div>
      </div>
      <div className="security-session-meta">
        <span>{session.ip || '-'}</span>
        <span>{t('securityCreated')} {formatTime(session.created_at || 0)}</span>
        <span>{t('securityExpires')} {formatTime(session.expires_at || 0)}</span>
      </div>
      {!session.current && active ? (
        <button className="danger-outline-button" type="button" disabled={pending} onClick={onRevoke}>
          {pending ? t('submitting') : t('securitySignOut')}
        </button>
      ) : null}
    </article>
  );
}

function MeHeader() {
  const t = useI18nStore((state) => state.t);
  return (
    <header className="tab-page-header">
      <span className="tab-page-icon"><UserRound size={23} aria-hidden="true" /></span>
      <div>
        <p className="eyebrow">{t('meEyebrow')}</p>
        <h1>{t('me')}</h1>
      </div>
    </header>
  );
}

function VipSheet({ user, onClose }: { user: PlayerUser | null; onClose: () => void }) {
  const t = useI18nStore((state) => state.t);
  const level = userVipLevel(user);
  const nextLevel = Number(user?.next_vip_level || Math.min(100, level + 1));
  const progress = Math.max(0, Math.min(100, Number(user?.vip_progress_pct || 0)));
  const expTotal = Number(user?.total_effective_bet_brl ?? user?.totalEffectiveBet ?? user?.exp_total ?? 0);
  const nextRequired = Number(user?.next_vip_exp_required || 0);
  const thresholds = user?.vip_thresholds || {};
  const nearbyLevels = vipNearbyLevels(level, thresholds);
  const benefits = [...(user?.vip_benefits?.levels || [])].sort((a, b) => Number(a.minLevel || 0) - Number(b.minLevel || 0));
  const currentTier = benefits.filter((b) => level >= Number(b.minLevel || 1)).pop() || null;
  const nextTier = benefits.find((b) => level < Number(b.minLevel || 1)) || null;
  const nextTierNeeds = nextTier
    ? Math.max(0, Number(thresholds[String(nextTier.minLevel)] ?? 0) - expTotal)
    : 0;
  const currentTickets = Number(currentTier?.ticketBonus || 0);

  // A level is granted only when BOTH the wagering and the deposit threshold are
  // met — vipSummary takes the minimum of the two progressions. One bar and one
  // "R$ x to go" could only ever describe one of them, and the figure shown was
  // the wagering shortfall, so a player blocked on deposits was told to go and
  // bet. Two meters, each with its own real total.
  const betHave = expTotal;
  const betTarget = Number(user?.next_vip_bet_threshold || 0);
  const depositHave = Number(user?.total_deposit_brl || 0);
  const depositTarget = Number(user?.next_vip_deposit_threshold || 0);
  const meters = [
    betTarget > 0 ? { label: t('actVipMeterBets'), have: betHave, target: betTarget } : null,
    depositTarget > 0 ? { label: t('actVipMeterDeposits'), have: depositHave, target: depositTarget } : null,
  ].filter(Boolean) as Array<{ label: string; have: number; target: number }>;
  // Whichever half is further behind is the one actually holding the level up.
  const depositShort = Math.max(0, Number(user?.next_vip_deposit_required || 0));
  const betShort = Math.max(0, nextRequired);
  const bindingShort = Math.max(betShort, depositShort);
  const bindingIsDeposit = depositShort > betShort;

  // Room tiers step at their own minLevel, so most single levels unlock nothing
  // at all. Saying which is the honest answer to "what do I get for VIP 6?" —
  // and when the answer is nothing, the next real unlock is named instead.
  const nextLevelTier = benefits.find((b) => Number(b.minLevel || 1) === nextLevel) || null;

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet vip-sheet-react" role="dialog" aria-modal="true" aria-label={t('vipDetailsTitle')} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('vipDetailsTitle')}</h2>
            <p>{t('vipDetailsSubtitle')}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>

        <div className="vip-sheet-body">
          <section className="vip-hero-panel vip-hero-v2">
            <div className="vip-hero-head">
              <div>
                <strong>VIP {level}</strong>
                {currentTier ? (
                  <span className="vip-hero-rooms">{t('vipPerkRooms').replace('%b%', String(currentTier.label || currentTier.badge || ''))}</span>
                ) : null}
              </div>
              {level >= 100 ? <small className="vip-hero-faltam">{t('vipMaxLevel')}</small> : null}
            </div>
            {level < 100 && meters.length ? (
              <div className="vip-hero-meters">
                {/* VIP-local classes, not the Rewards card's mission-* ones:
                    .vip-hero-panel styles bare i/b/div/span, so anything
                    borrowed in here gets its bar stretched to a full row and its
                    figure wrapped in a gold chip. One owner per component. */}
                <div className="vip-meter-heads">
                  <span>VIP {level}</span>
                  <span>VIP {nextLevel}</span>
                </div>
                <span className="vip-meters">
                  {meters.map((meter) => {
                    // A condition already satisfied reads as "R$ 5.100 / R$ 600"
                    // otherwise — a fraction above 1 that invites the question of
                    // why the total exceeds the target. Met halves state the
                    // threshold they cleared instead.
                    const met = meter.have >= meter.target;
                    return (
                      <span key={meter.label} className={met ? 'vip-meter is-met' : 'vip-meter'}>
                        <em>{meter.label}</em>
                        <i role="progressbar" aria-valuenow={Math.min(100, Math.round((meter.have / Math.max(1, meter.target)) * 100))} aria-valuemin={0} aria-valuemax={100} aria-label={meter.label}>
                          <span style={{ width: `${Math.min(100, (meter.have / Math.max(1, meter.target)) * 100)}%` }} />
                        </i>
                        <b>{met ? `${fmtMoney(meter.target)} ✓` : `${fmtMoney(meter.have)} / ${fmtAmount(meter.target)}`}</b>
                      </span>
                    );
                  })}
                </span>
                {bindingShort > 0 ? (
                  <p className="vip-meter-short">
                    {`${t('vipFaltamShort').replace('%v%', fmtMoney(bindingShort))} ${bindingIsDeposit ? t('vipShortInDeposits') : t('vipShortInBets')}`}
                  </p>
                ) : null}
              </div>
            ) : level < 100 ? (
              <div className="vip-progress-row">
                <span>VIP {level}</span>
                <i className="reward-bar" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} aria-label={`${Math.round(progress)}%`}>
                  <span style={{ width: `${progress}%` }} />
                </i>
                <span>VIP {nextLevel}</span>
              </div>
            ) : null}
            {/* What VIP 6 actually buys. Usually nothing — tiers step at their
                own levels — and a player deserves to know that before spending
                to reach it, not after. */}
            {level < 100 ? (
              <p className="vip-next-level-truth">
                {nextLevelTier
                  ? t('vipNextLevelUnlocks')
                      .replace('%n%', String(nextLevel))
                      .replace('%tier%', String(nextLevelTier.label || nextLevelTier.badge || ''))
                  : nextTier
                    ? t('vipNextLevelNothing')
                        .replace('%n%', String(nextLevel))
                        .replace('%tier%', String(nextTier.label || nextTier.badge || ''))
                        .replace('%at%', String(nextTier.minLevel))
                    : t('vipNextLevelNothingPlain').replace('%n%', String(nextLevel))}
              </p>
            ) : null}
            <p className="vip-rule-line">{t('vipRule')}</p>
          </section>

          {nextTier ? (
            <section className="vip-next-unlock" data-vip-next-react="true">
              <div className="vip-section-title">{t('vipNextUnlockTitle')}</div>
              <article>
                <div className="vip-next-head">
                  <strong>{nextTier.label || nextTier.badge}</strong>
                  <span>
                    {t('vipNextUnlockAt').replace('%n%', String(nextTier.minLevel))}
                    {nextTierNeeds > 0 ? ` · ${t('vipFaltamShort').replace('%v%', fmtMoney(nextTierNeeds))}` : ''}
                  </span>
                </div>
                {/* No cashback figure here. Cashback is settled per room from
                    rooms.rakeback_pct; the VIP tier's activity_boost_pct that
                    used to be printed as "1% cashback" is never read by the
                    payout job at all. The real rate is shown on each room, where
                    it is actually earned. */}
                <div className="vip-next-perks">
                  {Number(nextTier.ticketBonus || 0) > 0 ? <small>+{fmtInt(Number(nextTier.ticketBonus))} {t('vipBenefitTickets')}</small> : null}
                </div>
              </article>
            </section>
          ) : null}

          <section className="vip-section-block">
            <div className="vip-section-title">{t('vipPerksNowTitle')}</div>
            <section className="vip-stat-grid three">
              <div><span>{t('vipEffectiveBet')}</span><strong>{fmtMoney(expTotal)}</strong></div>
              <div><span>{t('vipTotalDeposited')}</span><strong>{fmtMoney(depositHave)}</strong></div>
              <div><span>{t('vipPerkTickets')}</span><strong>{currentTickets > 0 ? `+${fmtInt(currentTickets)}` : '—'}</strong></div>
            </section>
          </section>

          {benefits.length ? (
            <section className="vip-section-block">
              <div className="vip-section-title">{t('vipSalasTitle')}</div>
              <p className="vip-salas-note">{t('vipSalasNote')}</p>
              <div className="vip-benefit-list">
                {benefits.map((benefit) => {
                  const minLevel = Number(benefit.minLevel || 1);
                  const unlocked = level >= minLevel;
                  const isCurrent = currentTier != null && benefit.minLevel === currentTier.minLevel;
                  const tickets = Number(benefit.ticketBonus || 0);
                  return (
                    <article className={unlocked ? 'active' : 'locked'} key={`${benefit.label}:${benefit.minLevel}`}>
                      <div>
                        <strong>
                          {benefit.label || benefit.badge || `VIP ${minLevel}`}
                          {isCurrent ? <em className="vip-here-chip">{t('vipYouAreHere')}</em> : null}
                        </strong>
                        <span>{unlocked ? t('vipUnlocked') : t('vipUnlockAt').replace('%n%', String(minLevel))}</span>
                      </div>
                      <div className="vip-sala-perks">
                        {tickets > 0 ? <small>+{fmtInt(tickets)} {t('vipBenefitTickets')}</small> : null}
                        {/* "Entry unlocked" on a tier the player has not reached
                            contradicts the "Unlocks at VIP N" beside it — that
                            line already says everything a locked tier with no
                            extra perk has to say. */}
                        {tickets <= 0 && unlocked ? <small className="vip-sala-none">{t('vipSalaEntry')}</small> : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ) : null}

          {nearbyLevels.length ? (
            <details className="vip-section-block vip-levels-fold">
              <summary className="vip-section-title tap">{t('vipNextLevels')}</summary>
              <div className="vip-level-list">
                {nearbyLevels.map((item) => (
                  <article className={item.level === level ? 'current' : ''} key={item.level}>
                    <span>VIP {item.level}</span>
                    <strong>{fmtMoney(item.threshold)}</strong>
                  </article>
                ))}
              </div>
            </details>
          ) : null}

        </div>
      </section>
    </div>
  );
}

function QuickTile({
  icon: Icon,
  label,
  onClick,
  disabled = false,
}: {
  icon: typeof Wallet;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button className="me-quick-tile" type="button" disabled={disabled} onClick={onClick}>
      <span className="me-quick-icon"><Icon size={20} aria-hidden="true" /></span>
      <span className="me-quick-label">{label}</span>
    </button>
  );
}

function MenuAction({
  icon: Icon,
  label,
  onClick,
  badge,
  badgeAria,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  badge?: number;
  badgeAria?: string;
}) {
  return (
    <button className="me-menu-row" type="button" onClick={onClick}>
      <Icon size={19} aria-hidden="true" />
      <span>{label}</span>
      <span className="me-menu-value">
        {badge && badge > 0 ? (
          <b className="me-menu-badge" aria-label={badgeAria}>
            {badge > 9 ? '9+' : badge}
          </b>
        ) : null}
        <i aria-hidden="true">›</i>
      </span>
    </button>
  );
}

function ResponsibleGamingSheet({ token, onClose }: { token: string; onClose: () => void }) {
  const t = useI18nStore((state) => state.t);
  const setWalletTargetAction = useAppStore((state) => state.setWalletTargetAction);
  const queryClient = useQueryClient();
  const [sessionStartedAt] = useState(() => Date.now());
  const [sessionMinutes, setSessionMinutes] = useState(0);
  const [coolOffMessage, setCoolOffMessage] = useState('');
  const query = useQuery({
    queryKey: ['responsible-gaming', token],
    queryFn: () => fetchResponsibleGaming(token),
    enabled: !!token,
  });
  const limits = query.data?.limits || {};
  const coolOffActive = Number(limits.cool_off_until || 0) > Math.floor(Date.now() / 1000);
  const [form, setForm] = useState(() => formFromLimits(limits));

  useEffect(() => {
    if (query.data?.limits) setForm(formFromLimits(query.data.limits));
  }, [query.data?.limits]);

  useEffect(() => {
    const tick = () => setSessionMinutes(Math.floor((Date.now() - sessionStartedAt) / 60_000));
    tick();
    const timer = window.setInterval(tick, 30_000);
    return () => window.clearInterval(timer);
  }, [sessionStartedAt]);

  const mutation = useMutation({
    mutationFn: () => updateResponsibleGaming(token, {
      dailyBetLimitBrl: clampMoney(form.daily, 0, 50_000),
      weeklyBetLimitBrl: clampMoney(form.weekly, 0, 250_000),
      monthlyBetLimitBrl: clampMoney(form.monthly, 0, 1_000_000),
    }),
    onSuccess: (payload) => {
      queryClient.setQueryData(['responsible-gaming', token], payload);
      queryClient.invalidateQueries({ queryKey: ['responsible-gaming', token] });
    },
  });
  const coolOffMutation = useMutation({
    mutationFn: (days: number) => updateResponsibleGaming(token, { coolOffDays: days }),
    onSuccess: (payload, days) => {
      queryClient.setQueryData(['responsible-gaming', token], payload);
      queryClient.invalidateQueries({ queryKey: ['responsible-gaming', token] });
      setCoolOffMessage(t('rgCoolOffSet').replace('{{d}}', String(days)).replace('%d%', String(days)));
    },
  });

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet" role="dialog" aria-modal="true" aria-label={t('rgTitle')} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('rgTitle')}</h2>
            <p>{t('rgIntro')}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>

        <div className="profile-sheet-list">
          {query.isLoading ? <SheetState icon={RefreshCw} title={t('loading')} /> : null}
          {query.isError ? <SheetState icon={HelpCircle} title={t('rgLoadError')} actionLabel={t('retry')} onAction={() => query.refetch()} /> : null}
          {!query.isLoading && !query.isError ? (
            <>
              <div className="rg-session-card">
                <span>{t('rgSession')}</span>
                <strong>{formatSessionMinutes(sessionMinutes, t)}</strong>
                <small>{t('rgSessionBody')}</small>
              </div>
              <div className="rg-limit-card">
                <div>
                  <strong>{t('rgLimitDescTitle')}</strong>
                  <span>{t('rgLimitDesc')}</span>
                </div>
                <LimitInput label={t('rgDailyLimit')} value={form.daily} max={50000} onChange={(daily) => setForm((value) => ({ ...value, daily }))} />
                <LimitInput label={t('rgWeeklyLimit')} value={form.weekly} max={250000} onChange={(weekly) => setForm((value) => ({ ...value, weekly }))} />
                <LimitInput label={t('rgMonthlyLimit')} value={form.monthly} max={1000000} onChange={(monthly) => setForm((value) => ({ ...value, monthly }))} />
                <button className="primary-button" type="button" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
                  {mutation.isPending ? t('submitting') : t('rgSaveLimits')}
                </button>
                {mutation.isError ? <p className="form-error">{t('rgLimitSaveFailed')}</p> : null}
              </div>
              <div className="rg-usage-grid">
                <UsageCell label={t('rgDailyUsed')} used={limits.daily_bet_used_brl} remaining={limits.daily_bet_remaining_brl} />
                <UsageCell label={t('rgWeeklyUsed')} used={limits.weekly_bet_used_brl} remaining={limits.weekly_bet_remaining_brl} />
                <UsageCell label={t('rgMonthlyUsed')} used={limits.monthly_bet_used_brl} remaining={limits.monthly_bet_remaining_brl} />
              </div>
              <PendingLimits limits={limits} />
              {coolOffActive ? (
                <div className="rg-cooloff-active">
                  <Lock size={18} aria-hidden="true" />
                  <span>{t('rgCoolOffActive')} {coolOffRemainingLabel(limits.cool_off_until)}</span>
                </div>
              ) : (
                <div className="rg-cooloff-picker">
                  <div>
                    <Shield size={18} aria-hidden="true" />
                    <span>
                      <strong>{t('rgCoolOff')}</strong>
                      <small>{t('rgCoolOffDesc')}</small>
                    </span>
                  </div>
                  <b>{t('rgCoolOffPick')}</b>
                  <div className="rg-cooloff-chip-grid">
                    {[1, 7, 30, 60].map((days) => (
                      <button key={days} className="rg-cooloff-chip" type="button" disabled={coolOffMutation.isPending} onClick={() => coolOffMutation.mutate(days)}>
                        {t(coolOffLabelKey(days))}
                      </button>
                    ))}
                  </div>
                  {coolOffMutation.isError ? <p className="form-error">{t('rgLimitSaveFailed')}</p> : null}
                  {coolOffMessage ? <p className="form-success">{coolOffMessage}</p> : null}
                </div>
              )}
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export function ActivitiesSheet({
  token,
  onClose,
  onRoute,
  onVerifyContact,
  onRaffle,
  onInvite,
  variant = 'sheet',
}: {
  token: string;
  onClose: () => void;
  onRoute: (route: PlayerRoute) => void;
  onVerifyContact: (channel?: MeSecurityTargetChannel) => void;
  onRaffle: (view: 'register' | 'participants' | 'result', resultId?: string | null) => void;
  onInvite: () => void;
  variant?: 'sheet' | 'page';
}) {
  const t = useI18nStore((state) => state.t);
  const setWalletTargetAction = useAppStore((state) => state.setWalletTargetAction);
  const setDepositCouponPrefill = useAppStore((state) => state.setDepositCouponPrefill);
  const setDepositCouponPreselectId = useAppStore((state) => state.setDepositCouponPreselectId);
  const queryClient = useQueryClient();
  const viewedActivityIds = useRef(new Set<string>());
  const checkinClaimAmountRef = useRef(0);
  // Claim results used to print a green line above the tab bar: it shifted the
  // whole page down, sat far from the card that changed, and lingered. The
  // card itself already flips to "Resgatado" and the badge drops, so the
  // remaining messages (locked promo, cashback-is-automatic, generic done) go
  // through the app's toast like every other transient confirmation.
  const showToast = useAppStore((state) => state.showToast);
  const setRoute = useAppStore((state) => state.setRoute);
  const setActivityNotice = (text: string, kind: 'info' | 'success' | 'warn' = 'success') => {
    if (text) showToast(text, kind);
  };
  const [rewardsTab, setRewardsTab] = useState<'earn' | 'assets' | 'hist'>('earn');
  // Deep entries (the welcome-coupons popup) land on a specific sub-tab; the
  // target is consumed once so a later plain visit starts at the default.
  const rewardsTargetTab = useAppStore((state) => state.rewardsTargetTab);
  const setRewardsTargetTabStore = useAppStore((state) => state.setRewardsTargetTab);
  useEffect(() => {
    if (!rewardsTargetTab) return;
    setRewardsTab(rewardsTargetTab);
    setRewardsTargetTabStore(null);
  }, [rewardsTargetTab, setRewardsTargetTabStore]);
  const [couponHistoryOpen, setCouponHistoryOpen] = useState(false);
  const ticketLedgerQuery = useQuery({ queryKey: ['ticket-ledger', token], queryFn: () => fetchTicketLedger(token), enabled: !!token });
  const ticketBalance = Number(ticketLedgerQuery.data?.balance || 0);
  const authUser = useAuthStore((state) => state.user);
  // Same key the lobby and the Sorteios page use, so this is normally a cache
  // read rather than a request. It is here only for the raffle's prize ceiling.
  const raffleQuery = useQuery({
    queryKey: ['raffle', token],
    queryFn: () => fetchRaffleData(token as string),
    enabled: !!token,
    staleTime: 30_000,
  });
  // The top bracket in the live programme — what the pool reaches when a draw
  // fills up. Read from config, never authored here.
  const rafflePrizeBrackets = useMemo(
    () => (raffleQuery.data?.event?.prize_brackets || []).map((b) => ({
      min_n: b?.min_n, max_n: b?.max_n, pool: b?.pool,
      tiers: (b?.tiers || []).map((tr) => ({ count: tr?.count, amount: tr?.amount, rewards: (tr?.rewards || []).map((rw) => ({ amount: rw?.amount })) })),
    })),
    [raffleQuery.data?.event?.prize_brackets],
  );
  const rafflePrizeCeiling = useMemo(
    () => rafflePrizeBrackets.reduce((max, bracket) => Math.max(max, Number(bracket?.pool || 0)), 0),
    [rafflePrizeBrackets],
  );
  const raffleEntries = Number(raffleQuery.data?.event?.total_entries || 0);
  const raffleStatus = String(raffleQuery.data?.event?.status || '');
  const raffleScheduledAt = Number(raffleQuery.data?.event?.scheduled_at || 0);
  // My entries in the CURRENT draw. The entries list is capped server-side, so
  // an absent row is not proof of absence for huge draws — but a present row
  // is proof of presence, and that is the direction that costs money if wrong
  // (reselling a draw the player is already in).
  const raffleMyEntries = useMemo(() => {
    const myId = String(authUser?.id ?? '');
    if (!myId) return 0;
    return (raffleQuery.data?.entries || []).filter((entry) => String(entry.user_id ?? '') === myId).length;
  }, [raffleQuery.data?.entries, authUser?.id]);
  const raffleCanAdd = useMemo(() => {
    const event = raffleQuery.data?.event;
    if (!event) return false;
    const maxTotal = Number(event.max_total_entries || 0);
    const maxMine = Number(event.max_entries_per_user || 0);
    const roomInDraw = maxTotal <= 0 || raffleEntries < maxTotal;
    const roomForMe = maxMine <= 0 || raffleMyEntries < maxMine;
    return roomInDraw && roomForMe;
  }, [raffleQuery.data?.event, raffleEntries, raffleMyEntries]);
  const hasEmail = authUser?.email === undefined ? undefined : Boolean(String(authUser?.email || '').trim());
  const [dismissedActivityIds, setDismissedActivityIds] = useState<Set<string>>(() => new Set());
  const drumQuery = useQuery({
    queryKey: ['activity-drum', token],
    queryFn: () => fetchActivityDrum(token),
    enabled: !!token,
  });
  const summaryQuery = useQuery({
    queryKey: ['activities-summary', token],
    queryFn: () => fetchActivitiesSummary(token),
    enabled: !!token,
  });
  const auditsQuery = useQuery({
    queryKey: ['activity-audits', token],
    queryFn: () => fetchActivityAudits(token, 10),
    enabled: !!token,
  });
  const couponMineQuery = useQuery({ queryKey: ['coupons-mine', token], queryFn: () => fetchMyCoupons(token), enabled: !!token });
  const couponClaimableQuery = useQuery({ queryKey: ['coupons-claimable', token], queryFn: () => fetchClaimableCoupons(token), enabled: !!token });
  // Shares RewardInboxBlock's cache entry — only used for the Earn tab badge.
  const inboxBadgeQuery = useQuery({ queryKey: ['reward-inbox', token], queryFn: () => fetchRewardInbox(token), enabled: !!token });
  const couponClaimableCount = (couponClaimableQuery.data?.items || []).length;
  const couponAvailableCount = (couponMineQuery.data?.items || []).filter((c) => c.status === 'available').length;
  const claimMutation = useMutation({
    mutationFn: (track: string) => claimActivityCheckin(token, track),
    onMutate: () => {
      setActivityNotice('');
    },
    onSuccess: (payload) => {
      const reward = payload?.reward;
      const amount = reward && typeof reward.amount === 'number' ? Number(reward.amount) : checkinClaimAmountRef.current;
      setActivityNotice(
        reward?.rewardKind === 'raffle_ticket'
          ? t('rewardClaimedTickets').replace('%n%', String(reward.ticketCount || 0))
          : reward?.rewardKind === 'coupon'
            ? t('couponClaimed')
            : amount > 0
              ? t('activitiesCheckinClaimedAmount').replace('%v%', fmtMoney(amount))
              : t('activitiesCheckinClaimed'),
      );
      queryClient.invalidateQueries({ queryKey: ['activities-summary', token] });
      queryClient.invalidateQueries({ queryKey: ['activity-audits', token] });
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
  function handleCheckinClaim(track: string, amount: number) {
    checkinClaimAmountRef.current = amount;
    claimMutation.mutate(track);
  }
  const activityStateMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'view' | 'click' | 'dismiss' }) => updateActivityDrumState(token, id, action),
    onSuccess: (payload, variables) => {
      queryClient.setQueryData<ActivityDrumResponse>(['activity-drum', token], (current) => mergeActivityState(current, variables.id, payload.state));
    },
  });
  const items = useMemo(() => {
    const remote = normalizeActivities(drumQuery.data?.items || []);
    const covered = new Set(remote.map((item) => item.catalog_code || item.type).filter(Boolean));
    const local = buildLocalMissions(summaryQuery.data?.configs || [], {
      dailyBet: summaryQuery.data?.dailyBet,
      referrals: summaryQuery.data?.referrals,
      emailVerified: Boolean(authUser?.email_verified ?? authUser?.emailVerified),
      phoneVerified: Boolean(authUser?.phone_verified ?? authUser?.phoneVerified),
      vipLevel: Number(authUser?.vip_level ?? authUser?.vipLevel ?? 0),
      vipProgressPct: Number(authUser?.vip_progress_pct ?? 0),
      vipNextRequired: Number(authUser?.next_vip_exp_required ?? 0),
      vipBetHave: Number(authUser?.total_effective_bet_brl ?? authUser?.totalEffectiveBet ?? 0),
      vipBetTarget: Number(authUser?.next_vip_bet_threshold ?? 0),
      // undefined until the backend ships the deposit half — the card then
      // degrades to the bets meter alone instead of inventing a zero.
      vipDepositHave: authUser?.total_deposit_brl === undefined ? undefined : Number(authUser.total_deposit_brl),
      vipDepositTarget: authUser?.next_vip_deposit_threshold === undefined ? undefined : Number(authUser.next_vip_deposit_threshold),
      vipDepositRequired: Number(authUser?.next_vip_deposit_required ?? 0),
      ticketBalance,
      rafflePrizeCeiling,
      rafflePrizeBrackets,
      raffleEntries,
      raffleStatus,
      raffleScheduledAt,
      raffleMyEntries,
      raffleCanAdd,
      hasEmail,
    }, t)
      .filter((mission) => !covered.has(mission.catalog_code) && !covered.has(mission.type));
    return [...remote, ...local]
      .filter((item) => !dismissedActivityIds.has(item.id))
      // The freebag promo is retired (V152 deletes its definition). Drop it
      // here as well so it stops showing the moment this ships, without
      // waiting on a backend release.
      .filter((item) => !isRetiredFreebag(item));
  }, [dismissedActivityIds, drumQuery.data?.items, summaryQuery.data?.configs, summaryQuery.data?.dailyBet, summaryQuery.data?.referrals, ticketBalance, rafflePrizeCeiling, rafflePrizeBrackets, raffleEntries, raffleStatus, raffleScheduledAt, raffleMyEntries, raffleCanAdd, hasEmail, authUser?.vip_level, authUser?.vipLevel, authUser?.vip_progress_pct, authUser?.next_vip_exp_required, authUser?.total_effective_bet_brl, authUser?.totalEffectiveBet, authUser?.total_deposit_brl, authUser?.next_vip_bet_threshold, authUser?.next_vip_deposit_threshold, authUser?.next_vip_deposit_required, authUser?.email_verified, authUser?.emailVerified, authUser?.phone_verified, authUser?.phoneVerified, t]);
  // Locked promos render as compact rows instead of hogging the hero carousel.
  const activeItems = useMemo(() => items.filter((item) => !activityLocked(item)), [items]);
  const lockedItems = useMemo(() => items.filter((item) => activityLocked(item)), [items]);
  const checkins = summaryQuery.data?.checkins || [];
  const allConfigs = summaryQuery.data?.configs || [];
  const checkinTrackDays = useMemo(() => resolveCheckinTrackDays(allConfigs), [allConfigs]);
  const fundedCheckins = useMemo(
    () => checkins.filter((c) => {
      if (c.active === false) return false;
      return checkinTrackFunded(checkinTrackDays.get(c.track));
    }),
    [checkins, checkinTrackDays],
  );
  // One number per ladder — how many rungs are behind you. That is all the
  // celebration needs to know.
  const clearedCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    activeItems.forEach((item) => {
      if (item.steps?.length) counts[`m:${item.id}`] = item.steps.filter((step) => step.state === 'done').length;
    });
    fundedCheckins.forEach((checkin) => {
      const days = checkinTrackDays.get(checkin.track) || [];
      const streakDay = Math.max(1, Math.min(7, checkin.streakDay || 1));
      counts[`c:${checkin.track}`] = days.filter(
        (d) => d.day < streakDay || (d.day === streakDay && Boolean(checkin.claimed)),
      ).length;
    });
    return counts;
  }, [activeItems, fundedCheckins, checkinTrackDays]);
  const justCleared = useJustCleared(clearedCounts);
  // One banded feed: check-ins and missions in the same array, sorted by
  // band (claim-now → open-today → in-progress → to-start → done-for-today),
  // then priority. The order is FROZEN once shown: completing an action
  // changes a card in place, it never jumps under the thumb — the sort only
  // reapplies on remount.
  const feedOrderRef = useRef<string[] | null>(null);
  const orderedFeed = useMemo(() => {
    type FeedEntry =
      | { kind: 'checkin'; key: string; band: number; prio: number; checkin: ActivityCheckin }
      | { kind: 'mission'; key: string; band: number; prio: number; item: ActivityDrumItem };
    const entries: FeedEntry[] = [];
    fundedCheckins.forEach((checkin) => {
      const eligible = Boolean(checkin.eligibility?.eligible);
      const claimed = Boolean(checkin.claimed);
      const band = eligible && !claimed ? 0 : claimed ? 4 : 1;
      entries.push({
        kind: 'checkin',
        key: `checkin:${checkin.track}`,
        band,
        prio: checkin.track === 'login' ? 99 : checkin.track === 'deposit' ? 98 : 97,
        checkin,
      });
    });
    activeItems.forEach((item) => {
      const band = item.band ?? ({ today: 1, progress: 2, once: 3 }[item.group || 'today'] ?? 1);
      entries.push({ kind: 'mission', key: item.id, band, prio: Number(item.priority || 0), item });
    });
    entries.sort((a, b) => a.band - b.band || b.prio - a.prio || a.key.localeCompare(b.key));
    const sortedKeys = entries.map((entry) => entry.key);
    let orderKeys = sortedKeys;
    if (feedOrderRef.current) {
      const present = new Set(sortedKeys);
      const kept = feedOrderRef.current.filter((key) => present.has(key));
      const known = new Set(kept);
      orderKeys = [...kept, ...sortedKeys.filter((key) => !known.has(key))];
    }
    if (orderKeys.length) feedOrderRef.current = orderKeys;
    const byKey = new Map(entries.map((entry) => [entry.key, entry]));
    return orderKeys.map((key) => byKey.get(key)).filter(Boolean) as FeedEntry[];
  }, [fundedCheckins, activeItems]);
  const claimableRewardCount = (inboxBadgeQuery.data?.claimable || []).length;
  const todayUnclaimedCheckins = fundedCheckins.filter((c) => c.eligibility?.eligible && !c.claimed).length;
  // The badge is a promise: "this many gold buttons are waiting on this tab".
  // Claiming is Earn's job — including deposit coupons still to be claimed, so
  // they count here and not on the pouch.
  const earnBadge = claimableRewardCount + todayUnclaimedCheckins + couponClaimableCount;
  // No summary bar above the tabs. It restated what the tab badge and the
  // check-in cards already say, and once the day was claimed it kept sitting
  // there with a leftover sentence where a value belongs. The claim button
  // lives on the card that owns the reward.
  // 预览层退场:活动已启用或收件箱出现真数据的类型,对应 mock 自动隐藏。
  const liveActivityTypes = useMemo(() => {
    const set = new Set<string>();
    (summaryQuery.data?.configs || []).forEach((c) => {
      if (c.enabled && c.activityType) set.add(c.activityType);
    });
    const inboxData = inboxBadgeQuery.data;
    [...(inboxData?.claimable || []), ...(inboxData?.inProgress || []), ...(inboxData?.recentClaimed || [])]
      .forEach((it) => set.add(it.activityType));
    return set;
  }, [summaryQuery.data?.configs, inboxBadgeQuery.data]);
  const activitiesTrackRef = useRef<HTMLDivElement>(null);
  const [activityIndex, setActivityIndex] = useState(0);

  function onActivitiesScroll() {
    const track = activitiesTrackRef.current;
    if (!track) return;
    const first = track.firstElementChild as HTMLElement | null;
    const step = first ? first.offsetWidth + 12 : track.clientWidth;
    setActivityIndex(step > 0 ? Math.round(track.scrollLeft / step) : 0);
  }

  function scrollToActivity(index: number) {
    const track = activitiesTrackRef.current;
    const card = track?.children[index] as HTMLElement | undefined;
    if (track && card) track.scrollTo({ left: card.offsetLeft - 16, behavior: 'smooth' });
  }
  const loading = drumQuery.isLoading || summaryQuery.isLoading || auditsQuery.isLoading;
  const error = drumQuery.isError || summaryQuery.isError || auditsQuery.isError;

  useEffect(() => {
    if (!token || items.length === 0) return;
    items.forEach((item) => {
      if (!isUuidLike(item.id) || activityStateHas(item, 'viewed_at') || viewedActivityIds.current.has(item.id)) return;
      viewedActivityIds.current.add(item.id);
      activityStateMutation.mutate({ id: item.id, action: 'view' });
    });
  }, [activityStateMutation, items, token]);

  function handleActivity(item: ActivityDrumItem) {
    const action = item.cta_action || '';
    const normalizedAction = activityActionValue(action);
    setActivityNotice('');
    if (activityLocked(item)) {
      setActivityNotice(t('activitiesLockedToast'), 'warn');
      return;
    }
    if (isUuidLike(item.id) && !activityStateHas(item, 'clicked_at')) {
      activityStateMutation.mutate({ id: item.id, action: 'click' });
    }
    const walletAction = walletActionFromActivityAction(normalizedAction);
    if (walletAction) {
      setWalletTargetAction(walletAction);
      onRoute('wallet');
    }
    else if (['deposit-bonus', 'first-deposit', 'wallet'].includes(normalizedAction)) onRoute('wallet');
    else if (['tournament', 'leaderboard'].includes(normalizedAction)) onRoute('leaderboard');
    else if (['verify-phone', 'bind-phone'].includes(normalizedAction)) onVerifyContact('phone');
    else if (['verify-email', 'bind-email'].includes(normalizedAction)) onVerifyContact('email');
    else if (['kyc', 'verify', 'verify-contact', 'bind-contact'].includes(normalizedAction)) onVerifyContact();
    else if (normalizedAction === 'earn-tickets') {
      // Send the player to the nearest card that actually pays tickets — and
      // if none is on the page, to the draws board, never to a dead sheet.
      const target = document.querySelector('[data-rewards-page-react] [data-pays-tickets="true"], [data-activities-sheet-react] [data-pays-tickets="true"]');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('is-flash');
        window.setTimeout(() => target.classList.remove('is-flash'), 1300);
      } else {
        onRoute('sorteios');
      }
    }
    else if (normalizedAction === 'sorteios') onRoute('sorteios');
    else if (['raffle-register', 'hourly-raffle'].includes(normalizedAction)) onRaffle('register');
    else if (['raffle-participants', 'raffle-entries'].includes(normalizedAction)) onRaffle('participants');
    else if (['raffle-reveal', 'raffle-result', 'hourly-raffle-result', 'raffle-settled'].includes(normalizedAction)) {
      const resultId = raffleActivityEventId(item);
      onRaffle('result', resultId);
    }
    else if (normalizedAction === 'invite' || normalizedAction === 'copy-invite' || normalizedAction === 'referral') onInvite();
    else if (normalizedAction.includes('cashback') || item.type?.includes('cashback')) setActivityNotice(t('activitiesCashbackAuto'), 'info');
    else {
      const activityRoute = routeFromActivityAction(normalizedAction);
      if (activityRoute) onRoute(activityRoute);
      else if (action) setActivityNotice(t('activitiesActionDone'), 'info');
    }
  }

  const body = (
        <div className="activity-card-list">
          {error ? (
            <SheetState
              icon={HelpCircle}
              title={t('activitiesLoadError')}
              actionLabel={t('retry')}
              onAction={() => {
                drumQuery.refetch();
                summaryQuery.refetch();
                auditsQuery.refetch();
              }}
            />
          ) : null}
          <nav className="rewards-tabbar" data-rewards-tabbar-react="true">
            <button type="button" className={rewardsTab === 'earn' ? 'on' : ''} onClick={() => setRewardsTab('earn')}>
              {t('rewardsTabEarn')}
              {earnBadge > 0 ? <span className="rewards-tab-badge is-action" aria-label={t('rewardsReadyCount').replace('%n%', fmtInt(earnBadge))}>{earnBadge}</span> : null}
            </button>
            <button type="button" className={rewardsTab === 'assets' ? 'on' : ''} onClick={() => setRewardsTab('assets')}>
              {t('rewardsTabCoupons')}
              {/* Counts the coupons in the pouch that can be spent right now.
                  Coupons still to be claimed are not in the pouch — they are
                  in Earn, and they count on Earn's badge. */}
              {couponAvailableCount > 0 ? (
                <span
                  className="rewards-tab-badge is-count"
                  aria-label={t('rewardsCouponsCount').replace('%n%', fmtInt(couponAvailableCount))}
                >
                  {couponAvailableCount}
                </span>
              ) : null}
            </button>
            <button type="button" className={rewardsTab === 'hist' ? 'on' : ''} onClick={() => setRewardsTab('hist')}>
              {t('rewardsTabHistory')}
            </button>
          </nav>
          {/* Under the tab bar, where the cards will land — the old spinner sat
              above the tabs, so the page's own controls jumped when it left. */}
          {rewardsTab === 'earn' && loading ? (
            <div className="activity-card-skeleton" aria-busy="true" aria-label={t('loading')} data-activities-skeleton-react="true">
              <span /><span /><span />
            </div>
          ) : null}
          {rewardsTab === 'earn' && !loading && !error && items.length === 0 && checkins.length === 0 ? <SheetState icon={Gift} title={t('activitiesEmpty')} /> : null}
          {rewardsTab === 'assets' ? (
            <CouponsTab
              token={token}
              onUse={(minDeposit, couponId) => {
                setDepositCouponPrefill(minDeposit > 0 ? minDeposit : null);
                setDepositCouponPreselectId(couponId);
                setWalletTargetAction('deposit');
                onRoute('wallet');
              }}
              onEarn={() => setRewardsTab('earn')}
            />
          ) : null}
          {rewardsTab === 'earn' ? <RewardInboxBlock token={token} /> : null}
          {rewardsTab === 'earn' && REWARDS_V4_PREVIEW ? <PreviewVouchersBlock liveTypes={liveActivityTypes} /> : null}
          {rewardsTab === 'earn' && (fundedCheckins.length || activeItems.length) ? (
            <section className="activity-section-block">
              {/* One feed, one card size. The four headings that used to chop
                  nine cards into four short lists are gone: order says the same
                  thing — what you can take, then today's, then the ladders you
                  are partway up, then the one-offs. */}
              <div className="reward-feed" data-activities-track-react="true">
                {orderedFeed.map((entry) => entry.kind === 'checkin' ? (() => {
                  const checkin = entry.checkin;
                  const days = checkinTrackDays.get(checkin.track) || [];
                  const streakDay = Math.max(1, Math.min(7, checkin.streakDay || 1));
                  const nextAmount = Number(days.find((d) => d.day === streakDay)?.amount ?? 0);
                  return (
                    <CheckinCard
                      key={entry.key}
                      checkin={checkin}
                      days={days}
                      pending={claimMutation.isPending}
                      justCleared={justCleared}
                      onClaim={() => handleCheckinClaim(checkin.track, nextAmount)}
                      onDeposit={() => {
                        setWalletTargetAction('deposit');
                        onRoute('wallet');
                      }}
                    />
                  );
                })() : (() => {
                  const item = entry.item;
                  const reward = item.reward || item.reward_label;
                  return (
                    <button
                      type="button"
                      className="reward-card mission-row"
                      key={item.id}
                      style={{ '--accent': activityAccent(item) } as CSSProperties}
                      disabled={activityLocked(item)}
                      onClick={() => handleActivity(item)}
                      data-activity-card-react="true"
                      data-pays-tickets={item.pays_tickets ? 'true' : undefined}
                    >
                      <span className="mission-body">
                        {/* What it is, what to do, what it pays, where you are.
                            The payoff gets its own line and its own size — it
                            was competing with a sentence at the same weight,
                            which is why the card had to be read rather than
                            glanced at. */}
                        <strong className="mission-title">{activityText(item.title_main || item.title, item.title_main_i18n || item.title_i18n, item.title_main_params, t)}</strong>
                        <small className="mission-ask">
                          {renderAskWithAccent(activityText(item.secondary, item.secondary_i18n, item.secondary_params, t) || '', item.ask_accent)}
                          {item.countdown_at ? <LiveCountdown at={item.countdown_at} variant={item.countdown_variant} t={t} /> : null}
                        </small>
                        <span className="mission-payoff">
                          {reward ? (
                            <b className={`mission-reward${item.reward_dim ? ' is-dim' : ''}`}>
                              {item.reward_prefix ? <i className="payoff-prefix">{item.reward_prefix} </i> : null}
                              {reward}
                              {item.reward_note ? <i className="payoff-note">{item.reward_note}</i> : null}
                            </b>
                          ) : <span />}
                          <em className="mission-cta">{activityCtaLabel(item, t)}</em>
                        </span>
                        {/* A two-dimensional gate (the VIP card's bets AND
                            deposits) renders as labeled meters with the real
                            totals — a lone percentage can't say which half is
                            holding the player back. */}
                        {item.meters?.length ? (
                          <span className="mission-meters">
                            {item.meters.map((meter) => (
                              <span key={meter.label} className="mission-meter">
                                <em>{meter.label}</em>
                                <i role="progressbar" aria-valuenow={Math.min(100, Math.round((meter.have / Math.max(1, meter.target)) * 100))} aria-valuemin={0} aria-valuemax={100} aria-label={meter.label}>
                                  <span style={{ width: `${Math.min(100, (meter.have / Math.max(1, meter.target)) * 100)}%` }} />
                                </i>
                                <b>{fmtMoney(meter.have)} / {fmtMoney(meter.target)}</b>
                              </span>
                            ))}
                          </span>
                        ) : null}
                        {/* Not every activity is a climb. Binding a contact is
                            one errand whose real payoff is what it protects —
                            those reasons live here, where a ladder would be. */}
                        {item.perks?.length ? (
                          <span className="mission-perks">
                            {item.perks.map((perk) => <span key={perk} className="mission-perk">{perk}</span>)}
                          </span>
                        ) : null}
                        {/* The ladder: what the rungs pay, and which one is
                            yours to clear next. Same grammar as the check-in
                            week strip so it is learned once. */}
                        {item.steps?.length ? (
                          <span className="mission-steps">
                            {item.steps.map((step, index) => (
                              <span
                                key={`${step.label}:${index}`}
                                className={`mission-step is-${step.state}${justCleared.has(`m:${item.id}:${index}`) ? ' is-just' : ''}`}
                                style={step.fill ? ({ '--step-fill': `${Math.round(step.fill * 100)}%` } as CSSProperties) : undefined}
                              >
                                <b>{step.reward}</b>
                                <em>{step.label}</em>
                              </span>
                            ))}
                          </span>
                        ) : null}
                        {!item.steps?.length && activityHasProgress(item) ? (
                          <span className="mission-progress">
                            <i className="mission-bar" role="progressbar" aria-valuenow={activityProgress(item)} aria-valuemin={0} aria-valuemax={100} aria-label={activityProgressLabel(item, t)}>
                              <span style={{ width: `${activityProgress(item)}%` }} />
                            </i>
                            <em>{item.progress_label || activityFraction(item) || `${fmtInt(activityProgress(item))}%`}</em>
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })())}
              </div>
            </section>
          ) : null}
          {rewardsTab === 'earn' && REWARDS_V4_PREVIEW && !liveActivityTypes.has('bind_contact') ? <PreviewBindMissionsBlock /> : null}
          {rewardsTab === 'earn' && lockedItems.length ? (
            <section className="activity-section-block" data-locked-promos-react="true">
              <div className="activity-section-title">{t('rewardsLockedTitle')}</div>
              {lockedItems.map((item) => (
                <button type="button" className="activity-locked-row" key={item.id} onClick={() => handleActivity(item)}>
                  <span className="reward-icon muted"><ActivityIcon item={item} /></span>
                  <span className="locked-body">
                    <b>{activityText(item.title_main || item.title, item.title_main_i18n || item.title_i18n, item.title_main_params, t)}</b>
                    <i className="reward-bar" role="progressbar" aria-valuenow={activityProgress(item)} aria-valuemin={0} aria-valuemax={100} aria-label={activityProgressLabel(item, t)}>
                      <span style={{ width: `${activityProgress(item)}%` }} />
                    </i>
                    {/* The chip already says "Locked" — this line's job is the
                        scale, so prefer the backend's real one and never echo
                        the status back. */}
                    <small className="locked-scale">{item.progress_label || `${fmtInt(activityProgress(item))}%`}</small>
                  </span>
                  <span className="activity-status-chip locked">{t('activitiesLocked')}</span>
                </button>
              ))}
            </section>
          ) : null}
          {rewardsTab === 'earn' && REWARDS_V4_PREVIEW ? <PreviewAutoBanners /> : null}
          {rewardsTab === 'hist' ? <RecentRewardsBlock token={token} /> : null}
          {/* Spent coupons and the ticket ledger are history, and History is
              where they now live — the Cupons tab holds only coupons the
              account can still use. */}
          {rewardsTab === 'hist' ? (
            <button type="button" className="coupon-history-link" onClick={() => setCouponHistoryOpen(true)} data-coupon-history-link-react="true">
              {t('couponHistoryLink')} ›
            </button>
          ) : null}
          {/* One ticket statement, in the wallet next to the money one — not a
              second, shallower copy of it here. */}
          {rewardsTab === 'hist' ? (
            <button type="button" className="coupon-history-link" onClick={() => setRoute('wallet.tickets')} data-ticket-ledger-link-react="true">
              {t('ticketLedgerTitle')} ›
            </button>
          ) : null}
          {claimMutation.isError ? <p className="form-error" data-activity-claim-error-react="true">{t('activitiesClaimFailed')}</p> : null}
          {couponHistoryOpen ? <CouponHistorySheet token={token} onClose={() => setCouponHistoryOpen(false)} /> : null}
        </div>
  );
  if (variant === 'page') {
    return (
      <section className="tab-page rewards-page" data-rewards-page-react="true">
        {/* The same header every other tab page carries. Naming the page only
            in the tab bar left this one screen without a title or a way back,
            so a player reaching for the on-screen back button had nothing to
            press. */}
        <header className="tab-page-header">
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('backToLobby')}>
            <ArrowLeft size={20} aria-hidden="true" />
          </button>
          {/* No eyebrow: .eyebrow is display:none app-wide, so it would be
              dead markup — and were it ever shown, "Prêmios" above "Central de
              prêmios" says the same word twice. */}
          <div><h1>{t('rewardsCenter')}</h1></div>
        </header>
        {body}
      </section>
    );
  }
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet" role="dialog" aria-modal="true" aria-label={t('activitiesTitle')} onMouseDown={(event) => event.stopPropagation()} data-activities-sheet-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('activitiesTitle')}</h2>
            <p>{t('activitiesSub')}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>
        {body}
      </section>
    </div>
  );
}

function ActivityStatusChip({ item }: { item: ActivityDrumItem }) {
  const t = useI18nStore((state) => state.t);
  const status = String(item.status || '').toLowerCase();
  const secondsLeft = activitySecondsLeft(item);
  if (activityLocked(item)) return <span className="activity-status-chip locked">{t('activitiesLocked')}</span>;
  if (status === 'claimed' || activityStateHas(item, 'clicked_at')) return <span className="activity-status-chip claimed">{t('activitiesClaimed')}</span>;
  if (secondsLeft > 0 && secondsLeft <= 86_400) {
    return <span className="activity-status-chip urgent">{t('activitiesExpiresIn').replace('%t%', formatActivityTimeLeft(secondsLeft))}</span>;
  }
  return null;
}



function rewardInboxName(it: RewardInboxItem, lang: string) {
  const n = it.titleI18n || {};
  return n[lang] || n['pt-BR'] || n['en'] || it.activityType;
}
function rewardInboxValue(it: RewardInboxItem, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (it.rewardKind === 'cash') return '+' + fmtMoney(Number(it.cashAmount || 0));
  if (it.rewardKind === 'raffle_ticket') {
    const n = Number(it.ticketCount || 0);
    return n === 1 ? t('rewardTicketOne') : t('rewardTickets').replace('%n%', String(n));
  }
  if (it.rewardKind === 'bundle') {
    const rewards: ActivityTierReward[] = [];
    if (Number(it.cashAmount || 0) > 0) rewards.push({ kind: 'cash', amount: Number(it.cashAmount) });
    if (Number(it.ticketCount || 0) > 0) rewards.push({ kind: 'raffle_ticket', ticket_count: Number(it.ticketCount) });
    if (it.couponTemplateId) rewards.push({ kind: 'coupon', coupon_code: `template:${it.couponTemplateId}` });
    return checkinRewardText(rewards, t);
  }
  return t('rewardCoupon');
}

function formatRemainingLong(seconds: number) {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  if (d > 0) {
    const h = Math.floor((s % 86400) / 3600);
    return h > 0 ? `${d}d ${h}h` : `${d}d`;
  }
  return formatActivityTimeLeft(s);
}

function RewardExpiry({ expiresAt }: { expiresAt?: number | null }) {
  const t = useI18nStore((state) => state.t);
  if (!expiresAt) return null;
  const left = expiresAt - Math.floor(Date.now() / 1000);
  if (left <= 0) return null;
  return (
    <em className={`reward-expiry ${left <= 48 * 3600 ? 'urgent' : ''}`}>
      {t('activitiesExpiresIn').replace('%t%', formatRemainingLong(left))}
    </em>
  );
}

// The Cupons tab IS the coupons. Every coupon the account holds gets the same
// gold card, and each card answers the only four questions a coupon raises:
// what it pays, what it takes to use it, what rollover it carries, and when it
// dies. Nothing else lives on this tab — the ticket tile, the wagering row and
// the history link each belonged to another subject, and behind them the real
// list was buried in a sheet: the badge said 4 while the page showed 1.
function CouponsTab({ token, onUse, onEarn }: {
  token: string;
  onUse: (minDeposit: number, couponId: number) => void;
  onEarn: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const mineQuery = useQuery({ queryKey: ['coupons-mine', token], queryFn: () => fetchMyCoupons(token), enabled: !!token });
  const mine = mineQuery.data?.items || [];
  // A POUCH, NOT A SHOP: only coupons the account already owns. Offers still
  // to be claimed live in Earn, with every other "take this" — an offer you
  // have not accepted is not an asset. Used and expired ones are history.
  //
  // The first-deposit ladder is ONE coupon with five stages, not five
  // coupons: rung 2 cannot be used before rung 1. Rendering it as five cards
  // implied five independent choices and buried the single thing the player
  // can actually do today.
  const ladderAll = mine.filter((c) => c.grantSource === 'first_deposit_1_5')
    .sort((a, b) => Number(a.sourceRef || 0) - Number(b.sourceRef || 0));
  const ladderLive = ladderAll.some((c) => c.status === 'available' || c.status === 'locked') ? ladderAll : [];
  const plain = mine.filter((c) => c.grantSource !== 'first_deposit_1_5');
  const available = plain.filter((c) => c.status === 'available')
    .sort((a, b) => (Number(a.expiresAt || 0) || Number.MAX_SAFE_INTEGER) - (Number(b.expiresAt || 0) || Number.MAX_SAFE_INTEGER));
  const locked = plain.filter((c) => c.status === 'locked')
    .sort((a, b) => Number(a.sourceRef || 0) - Number(b.sourceRef || 0));
  if (mineQuery.isLoading) return <SheetState icon={RefreshCw} title={t('loading')} />;
  if (!ladderLive.length && !available.length && !locked.length) {
    return (
      <SheetState
        icon={Gift}
        title={t('couponEmpty')}
        actionLabel={t('couponEmptyCta')}
        onAction={onEarn}
      />
    );
  }
  // One hero, then rows. Five identical gold heroes was five things shouting
  // the same volume: the card the player should use FIRST keeps the full
  // treatment, everything else states the same facts in a row a third the
  // height. The ladder, being a staged coupon, owns the hero slot when present.
  const [first, ...rest] = available;
  const heroPlain = ladderLive.length ? null : first;
  const rowPlain = ladderLive.length ? available : rest;
  return (
    <div className="coupon-gold-list" data-coupon-list-react="true">
      {ladderLive.length ? <CouponLadderCard coupons={ladderLive} onUse={onUse} /> : null}
      {heroPlain ? <CouponGoldCard coupon={heroPlain} state="use" onCta={() => onUse(Number(heroPlain.minDeposit) || 0, heroPlain.id)} /> : null}
      {!ladderLive.length && available.length > 1 ? <p className="coupon-list-caption">{t('couponPouchByExpiry')}</p> : null}
      {rowPlain.map((c) => (
        <CouponRow key={c.id} coupon={c} state="use" onCta={() => onUse(Number(c.minDeposit) || 0, c.id)} />
      ))}
      {locked.map((c) => (
        <CouponRow key={c.id} coupon={c} state="locked" />
      ))}
      {/* One coupon and a screen of black underneath read as "page failed to
          load". The way to get more is the honest thing to put there. */}
      <button type="button" className="coupon-more-link" onClick={onEarn} data-coupon-more-react="true">
        {t('couponMoreLink')} ›
      </button>
    </div>
  );
}

// The first-deposit ladder: ONE coupon, five stages, used in order. The card
// shows the stage the player can spend today at full size, and the whole climb
// as a strip underneath — where they have been, where they are, what is still
// ahead. Rendered as five separate cards it read as five choices, when there
// has only ever been one.
function CouponLadderCard({ coupons, onUse }: {
  coupons: DepositCoupon[];
  onUse: (minDeposit: number, couponId: number) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const active = coupons.find((c) => c.status === 'available');
  // The figure is the stage on offer now; with none available (every rung
  // spent this cycle) the card states the next one instead of a stale number.
  const head = active || coupons.find((c) => c.status === 'locked') || coupons[0];
  if (!head) return null;
  const cap = Number(head.maxBonus || 0);
  const minDeposit = Number(head.minDeposit || 0);
  const principal = Number(head.principalAuditMultiplier ?? 0);
  const bonus = Number(head.bonusAuditMultiplier ?? head.auditMultiplier ?? 0);
  const auditLine = principal > 0
    ? t('couponCardAudit').replace('%principal%', fmtInt(principal)).replace('%bonus%', fmtInt(bonus))
    : bonus > 0
      ? t('couponCardAuditBonus').replace('%bonus%', fmtInt(bonus))
      : '';
  return (
    <div className="coupon-hero is-ladder" data-coupon-card-react="true" data-coupon-ladder-react="true">
      <span className="hero-rings" aria-hidden="true" />
      <div className="hero-big">
        <b className="pct">{couponHeadline(head)}</b>
        {cap > 0 ? <span className="sub">{t('couponCardCap').replace('%cap%', fmtMoney(cap))}</span> : null}
      </div>
      <p className="hero-min">
        {minDeposit > 0 ? t('couponHeroMin').replace('%min%', fmtMoney(minDeposit)) : t('couponHeroHint')}
      </p>
      {auditLine ? <p className="hero-audit">{auditLine}</p> : null}
      {active ? (
        <button type="button" className="hero-cta" onClick={() => onUse(minDeposit, active.id)}>
          {t('couponHeroUse')}
        </button>
      ) : null}
      {/* The climb. Same grammar as the Earn ladders: cleared, current, ahead. */}
      <span className="coupon-ladder-strip">
        {coupons.map((c) => (
          <i
            key={c.id}
            className={`cstep${c.status === 'used' ? ' is-done' : c.status === 'available' ? ' is-now' : ''}`}
          >
            <b>{c.status === 'used' ? '✓' : couponHeadline(c).replace('+', '')}</b>
            <em>{t('actStepNth').replace('%n%', String(c.sourceRef || ''))}</em>
          </i>
        ))}
      </span>
      <p className="coupon-ladder-note">{t('couponLadderNote')}</p>
    </div>
  );
}

// Everything the hero says, one third the height: figure, terms, and the way
// to act. Gold filled = yours to spend; dark = yours but not unlocked yet.
// There is no "offer" state here — claiming happens in Earn.
function CouponRow({ coupon, state, pending, onCta }: {
  coupon: DepositCoupon;
  state: 'use' | 'locked';
  pending?: boolean;
  onCta?: () => void;
}) {
  const t = useI18nStore((state_) => state_.t);
  const terms = couponTermsLine(coupon, t);
  const rung = Number(coupon.sourceRef || 0);
  const expiresAt = Number(coupon.expiresAt || 0) || null;
  return (
    <div className={`coupon-row is-${state}`} data-coupon-row-react="true">
      <div className="coupon-row-body">
        <b className="coupon-row-head">
          {couponHeadline(coupon)}
          {Number(coupon.maxBonus || 0) > 0 ? <i>{t('couponCardCap').replace('%cap%', fmtMoney(Number(coupon.maxBonus)))}</i> : null}
        </b>
        <small>{terms}</small>
      </div>
      {state === 'locked' ? (
        <span className="coupon-row-note">{rung > 0 ? t('couponCardUnlockShort').replace('%n%', fmtInt(rung)) : t('couponCardLocked')}</span>
      ) : (
        <button type="button" className="coupon-row-cta" onClick={onCta} disabled={pending}>
          {pending ? t('loading') : t('couponRowUse')}
        </button>
      )}
      {expiresAt ? <RewardExpiry expiresAt={expiresAt} /> : null}
    </div>
  );
}

function couponHeadline(coupon: DepositCoupon | ClaimableCoupon) {
  return sharedCouponHeadline(coupon, fmtMoney);
}

// The two conditions, in one line, for the compact rows.
function couponTermsLine(
  coupon: DepositCoupon | ClaimableCoupon,
  t: ReturnType<typeof useI18nStore.getState>['t'],
) {
  const parts: string[] = [];
  const minDeposit = Number(coupon.minDeposit || 0);
  if (minDeposit > 0) parts.push(t('couponRowMin').replace('%min%', fmtMoney(minDeposit)));
  const principal = Number(coupon.principalAuditMultiplier ?? 0);
  const bonus = Number(coupon.bonusAuditMultiplier ?? coupon.auditMultiplier ?? 0);
  if (principal > 0) parts.push(t('couponCardAudit').replace('%principal%', fmtInt(principal)).replace('%bonus%', fmtInt(bonus)));
  else if (bonus > 0) parts.push(t('couponCardAuditBonus').replace('%bonus%', fmtInt(bonus)));
  return parts.join(' · ');
}

function CouponGoldCard({ coupon, state, pending, onCta }: {
  coupon: DepositCoupon;
  state: 'use' | 'locked';
  pending?: boolean;
  onCta?: () => void;
}) {
  const t = useI18nStore((state_) => state_.t);
  const headline = couponHeadline(coupon);
  const cap = Number(coupon.maxBonus || 0);
  const minDeposit = Number(coupon.minDeposit || 0);
  const expiresAt = Number(coupon.expiresAt || 0) || null;
  // Rollover, stated as the player will meet it. A 0× principal is not printed:
  // "deposit 0×" reads as a requirement when it is the absence of one.
  const principal = Number(coupon.principalAuditMultiplier ?? 0);
  const bonus = Number(coupon.bonusAuditMultiplier ?? coupon.auditMultiplier ?? 0);
  const auditLine = principal > 0
    ? t('couponCardAudit').replace('%principal%', fmtInt(principal)).replace('%bonus%', fmtInt(bonus))
    : bonus > 0
      ? t('couponCardAuditBonus').replace('%bonus%', fmtInt(bonus))
      : '';
  // A locked rung unlocks on its own deposit — say which one, since "locked"
  // alone tells the player nothing about how to get it.
  const rung = Number(coupon.sourceRef || 0);
  return (
    <div className={`coupon-hero${state === 'locked' ? ' is-locked' : ''}`} data-coupon-card-react="true">
      <span className="hero-rings" aria-hidden="true" />
      {/* No "DEPOSIT COUPON" chip: the tab is called Cupons and every card on
          it is one. Repeated down the page it cost a row of height per card
          and carried no information. */}
      <div className="hero-big">
        <b className="pct">{headline}</b>
        {cap > 0 ? <span className="sub">{t('couponCardCap').replace('%cap%', fmtMoney(cap))}</span> : null}
        <RewardExpiry expiresAt={expiresAt} />
      </div>
      <p className="hero-min">
        {minDeposit > 0 ? t('couponHeroMin').replace('%min%', fmtMoney(minDeposit)) : t('couponHeroHint')}
      </p>
      {auditLine ? <p className="hero-audit">{auditLine}</p> : null}
      {state === 'locked' ? (
        <p className="hero-locked">
          {rung > 0 ? t('couponCardUnlock').replace('%n%', fmtInt(rung)) : t('couponCardLocked')}
        </p>
      ) : (
        <button type="button" className="hero-cta" onClick={onCta} disabled={pending}>
          {pending ? t('loading') : t('couponHeroUse')}
        </button>
      )}
    </div>
  );
}

function RewardKindIcon({ kind }: { kind: string }) {
  if (kind === 'bundle') return <Gift size={16} strokeWidth={2.2} />;
  if (kind === 'coupon') return <Ticket size={16} strokeWidth={2.2} />;
  if (kind === 'raffle_ticket') return <Dices size={16} strokeWidth={2.2} />;
  return <Coins size={16} strokeWidth={2.2} />;
}

const PROGRESS_UNIT_KEY: Record<string, MessageKeyOf> = {
  invites: 'rewardUnitInvites',
  rounds: 'rewardUnitRounds',
  days: 'rewardUnitDays',
  contacts: 'rewardUnitContacts',
};
type MessageKeyOf = Parameters<ReturnType<typeof useI18nStore.getState>['t']>[0];

function rewardProgressLabel(it: RewardInboxItem, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const cur = Number(it.progressCurrent || 0);
  const tgt = Number(it.progressTarget || 0);
  const unitKey = it.progressUnit ? PROGRESS_UNIT_KEY[it.progressUnit] : undefined;
  const unit = unitKey ? ` ${t(unitKey)}` : '';
  return `${cur}/${tgt}${unit}`;
}

function RewardInboxBlock({ token }: { token: string }) {
  const t = useI18nStore((state) => state.t);
  const lang = useI18nStore((state) => state.language);
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState('');
  const [claimError, setClaimError] = useState(false);
  const lastClaim = useRef<RewardInboxItem | null>(null);
  const inboxQuery = useQuery({ queryKey: ['reward-inbox', token], queryFn: () => fetchRewardInbox(token), enabled: !!token });
  const claimMutation = useMutation({
    mutationFn: (id: number) => claimReward(token, id),
    onMutate: () => {
      setClaimError(false);
    },
    onSuccess: () => {
      const it = lastClaim.current;
      setNotice(
        it && it.rewardKind === 'cash'
          ? t('rewardClaimedCash').replace('%v%', fmtMoney(Number(it.cashAmount || 0)))
          : it && it.rewardKind === 'raffle_ticket'
            ? (Number(it.ticketCount || 0) === 1 ? t('rewardClaimedTicketOne') : t('rewardClaimedTickets').replace('%n%', String(it.ticketCount || 0)))
            : t('rewardClaimedGeneric'),
      );
      queryClient.invalidateQueries({ queryKey: ['reward-inbox', token] });
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['activity-audits', token] });
      queryClient.invalidateQueries({ queryKey: ['me-bonus-audit-records', token] });
    },
    onError: () => {
      setClaimError(true);
    },
  });
  const claimable = inboxQuery.data?.claimable || [];
  const inProgress = inboxQuery.data?.inProgress || [];
  // Claiming happens in Earn — all of it. The Cupons tab is a pouch: it holds
  // what the account already owns, and an offer you have not accepted is not
  // an asset. So the claimable deposit coupons live here, in the one place
  // that means "take this".
  const couponClaimQuery = useQuery({ queryKey: ['coupons-claimable', token], queryFn: () => fetchClaimableCoupons(token), enabled: !!token });
  const couponClaims = couponClaimQuery.data?.items || [];
  const couponClaimMutation = useMutation({
    mutationFn: (code: string) => claimDepositCoupon(token, code),
    onMutate: () => setClaimError(false),
    onSuccess: () => {
      setNotice(t('couponClaimed'));
      queryClient.invalidateQueries({ queryKey: ['coupons-claimable', token] });
      queryClient.invalidateQueries({ queryKey: ['coupons-mine', token] });
    },
    onError: () => setClaimError(true),
  });
  // Keep the section mounted while a success/error notice is showing, even after
  // the last claimable moves to recentClaimed — otherwise the confirmation vanishes.
  const hasContent = claimable.length > 0 || inProgress.length > 0 || couponClaims.length > 0;
  if (!hasContent && !notice && !claimError && !inboxQuery.isLoading && !inboxQuery.isError) return null;
  function doClaim(it: RewardInboxItem) {
    lastClaim.current = it;
    claimMutation.mutate(it.id);
  }
  return (
    <section className="activity-section-block reward-inbox">
      <div className="activity-section-title reward-inbox-head">
        <span>{t('rewardInboxTitle')}</span>
        {claimable.length + couponClaims.length > 0 ? <b className="reward-inbox-count">{t('rewardInboxReady').replace('%n%', String(claimable.length + couponClaims.length))}</b> : null}
      </div>
      {inboxQuery.isLoading ? <p className="reward-inbox-note">{t('loading')}</p> : null}
      {inboxQuery.isError ? (
        <p className="reward-inbox-note" data-reward-inbox-error-react="true">
          {t('rewardInboxError')}
          <button type="button" onClick={() => inboxQuery.refetch()}>{t('retry')}</button>
        </p>
      ) : null}
      {notice ? <p className="form-success" data-reward-claimed-react="true">{notice}</p> : null}
      {claimError ? <p className="form-error" data-reward-claim-error-react="true">{t('activitiesClaimFailed')}</p> : null}
      {couponClaims.map((c) => (
        <article className="reward-item claimable" key={`dc${c.templateId}`}>
          <span className="reward-icon"><RewardKindIcon kind="coupon" /></span>
          <div className="reward-body">
            <b>{withTrueRate((c.nameI18n || {})[lang] || (c.nameI18n || {})['pt-BR'] || t('couponRewardName'), c, lang)}</b>
            <span className="reward-value">{couponHeadline(c)}</span>
            <small>{couponTermsLine(c, t)}</small>
          </div>
          <button
            className="reward-claim"
            type="button"
            disabled={couponClaimMutation.isPending}
            onClick={() => couponClaimMutation.mutate(c.code)}
          >
            {t('rewardClaim')}
          </button>
        </article>
      ))}
      {claimable.map((it) => {
        const desc = (it.descI18n || {})[lang] || (it.descI18n || {})['pt-BR'] || '';
        const rollover = Number(it.auditMultiplier || 0);
        return (
          <article className="reward-item claimable" key={`c${it.id}`}>
            <span className="reward-icon"><RewardKindIcon kind={it.rewardKind} /></span>
            <div className="reward-body">
              <b>{rewardInboxName(it, lang)}</b>
              <span className="reward-value">{rewardInboxValue(it, t)}</span>
              {desc ? <small>{desc}</small> : null}
              {(it.rewardKind === 'cash' || it.rewardKind === 'bundle') && Number(it.cashAmount || 0) > 0 && rollover > 0 ? (
                <span className="reward-terms-chip">{t('rewardRollover').replace('%x%', String(rollover))}</span>
              ) : null}
              <RewardExpiry expiresAt={it.expiresAt} />
            </div>
            <button className="reward-claim" type="button" disabled={claimMutation.isPending} onClick={() => doClaim(it)}>
              {t('rewardClaim')}
            </button>
          </article>
        );
      })}
      {inProgress.map((it) => {
        const cur = Number(it.progressCurrent || 0);
        const tgt = Number(it.progressTarget || 0);
        const pct = tgt > 0 ? Math.min(100, Math.round((cur / tgt) * 100)) : 0;
        const progressLabel = rewardProgressLabel(it, t);
        return (
          <article className="reward-item progress" key={`p${it.id}`}>
            <span className="reward-icon muted"><RewardKindIcon kind={it.rewardKind} /></span>
            <div className="reward-body">
              <b>{rewardInboxName(it, lang)}</b>
              <span>{rewardInboxValue(it, t)} · {progressLabel}</span>
              <i className="reward-bar" role="progressbar" aria-valuenow={cur} aria-valuemin={0} aria-valuemax={tgt} aria-label={progressLabel}>
                <span style={{ width: `${pct}%` }} />
              </i>
            </div>
          </article>
        );
      })}
    </section>
  );
}

function RecentRewardsBlock({ token }: { token: string }) {
  const t = useI18nStore((state) => state.t);
  const lang = useI18nStore((state) => state.language);
  const inboxQuery = useQuery({ queryKey: ['reward-inbox', token], queryFn: () => fetchRewardInbox(token), enabled: !!token });
  const recent = inboxQuery.data?.recentClaimed || [];
  // A tab that can legitimately be empty needs to say so. This returned null,
  // so a player with no history got a black screen under the tab bar and no
  // way to tell it apart from a broken page.
  if (!recent.length) {
    return (
      <section className="activity-section-block reward-recent">
        <SheetState icon={Gift} title={t('rewardsHistoryEmpty')} description={t('rewardsHistoryEmptyHint')} />
      </section>
    );
  }
  return (
    <section className="activity-section-block reward-recent">
      <div className="activity-section-title">{t('rewardRecentTitle')}</div>
      {recent.slice(0, 6).map((it) => (
        <article className="reward-recent-row" key={`r${it.id}`}>
          <span className="reward-icon done"><RewardKindIcon kind={it.rewardKind} /></span>
          <div className="reward-body">
            <b>{rewardInboxName(it, lang)}</b>
            <span>{it.claimedAt ? formatDateShort(it.claimedAt) : t('rewardCreditedAuto')}</span>
          </div>
          <span className={`rwv4-hist-tag ${it.claimedAt ? 'claim' : 'auto'}`}>{it.claimedAt ? t('rewardHistClaimedTag') : t('rewardHistAutoTag')}</span>
          <span className="reward-recent-value">{rewardInboxValue(it, t)}</span>
        </article>
      ))}
      {REWARDS_V4_PREVIEW ? (
        <article className="reward-recent-row" data-preview-auto-row-react="true">
          <span className="reward-icon done"><RewardKindIcon kind="cash" /></span>
          <div className="reward-body">
            <b>{t('rewardPreviewAutoRow')}<i className="rwv4-preview-tag">{t('rewardPreviewTag')}</i></b>
            <span>{t('rewardCreditedAuto')}</span>
          </div>
          <span className="rwv4-hist-tag auto">{t('rewardHistAutoTag')}</span>
          <span className="reward-recent-value">+R$ 10</span>
        </article>
      ) : null}
    </section>
  );
}

/** Rewards v4 预览层:细则底部 sheet(表格 + 注释)。 */
function PreviewRulesSheet({ rules, onClose }: { rules: PreviewRules; onClose: () => void }) {
  const t = useI18nStore((state) => state.t);
  const lang = useI18nStore((state) => state.language);
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet" role="dialog" aria-modal="true" aria-label={localizePreview(rules.title, lang)} onMouseDown={(e) => e.stopPropagation()} data-rules-sheet-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div><h2>{localizePreview(rules.title, lang)}</h2></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>
        <div className="activity-card-list">
          <p className="rwv4-rules-lead">{localizePreview(rules.lead, lang)}</p>
          <table className="rwv4-rule-tbl">
            <tbody>
              {rules.rows.map((row, index) => (
                <tr key={index}>
                  <td>{localizePreview(row.label, lang)}</td>
                  <td>{localizePreview(row.value, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="rwv4-rules-notes">
            {rules.notes.map((note, index) => <li key={index}>{localizePreview(note, lang)}</li>)}
          </ul>
          <button className="rwv4-cta wide" type="button" onClick={onClose}>{t('close')}</button>
        </div>
      </section>
    </div>
  );
}

/** 预览层:demo 风格可领凭证行(mock 领取,只改本地状态)。 */
function PreviewVouchersBlock({ liveTypes }: { liveTypes: Set<string> }) {
  const t = useI18nStore((state) => state.t);
  const lang = useI18nStore((state) => state.language);
  const [claimed, setClaimed] = useState<Set<string>>(() => new Set());
  const [rules, setRules] = useState<PreviewRules | null>(null);
  // 真数据接管的活动,mock 退场(REWARDS_V4_PREVIEW 关闭时整层退场)。
  const pending = PREVIEW_VOUCHERS.filter((v) => !claimed.has(v.id) && !liveTypes.has(v.activityType));
  if (!pending.length && !rules) return null;
  return (
    <section className="activity-section-block" data-preview-vouchers-react="true">
      {pending.map((v) => (
        <article className="rwv4-voucher" key={v.id}>
          <span className="rwv4-ic">{v.icon}</span>
          <div className="rwv4-main">
            <b>{localizePreview(v.title, lang)}<i className="rwv4-preview-tag">{t('rewardPreviewTag')}</i></b>
            <small>{localizePreview(v.sub, lang)}</small>
            <button type="button" className="rwv4-rules" onClick={() => setRules(v.rules)}>{t('rewardRules')}</button>
          </div>
          <div className="rwv4-right">
            <span className={`rwv4-amt ${v.kind === 'raffle_ticket' ? 'ticket' : v.kind === 'coupon' ? 'coupon' : ''}`}>
              {v.kind === 'cash' ? fmtMoney(Number(v.cash || 0)) : v.kind === 'coupon' ? localizePreview(v.couponLabel, lang) : `${v.tickets} 🎟`}
            </span>
            <button type="button" className="rwv4-cta" onClick={() => setClaimed((cur) => new Set(cur).add(v.id))}>{t('rewardClaim')}</button>
            <span className={`rwv4-exp ${v.expiresInSec < 172800 ? 'urgent' : ''}`}>{formatActivityTimeLeftLabel(v.expiresInSec, t)}</span>
          </div>
        </article>
      ))}
      {rules ? <PreviewRulesSheet rules={rules} onClose={() => setRules(null)} /> : null}
    </section>
  );
}

function formatActivityTimeLeftLabel(seconds: number, t: ReturnType<typeof useI18nStore.getState>['t']) {
  return t('activitiesExpiresIn').replace('%t%', formatRemainingLong(seconds));
}

/** 预览层:绑定任务两步交互(Verificar → Resgatar,全 mock)。 */
function PreviewBindMissionsBlock() {
  const t = useI18nStore((state) => state.t);
  const lang = useI18nStore((state) => state.language);
  const [stage, setStage] = useState<Record<string, 'idle' | 'verified' | 'claimed'>>({});
  const visible = PREVIEW_BIND_MISSIONS.filter((m) => stage[m.id] !== 'claimed');
  if (!visible.length) return null;
  return (
    <section className="activity-section-block" data-preview-bind-react="true">
      <div className="activity-section-title">{t('rewardMissionsTitle')}</div>
      {visible.map((m) => {
        const st = stage[m.id] || 'idle';
        return (
          <article className="rwv4-voucher" key={m.id}>
            <span className="rwv4-ic">{m.icon}</span>
            <div className="rwv4-main">
              <b>{localizePreview(m.title, lang)}<i className="rwv4-preview-tag">{t('rewardPreviewTag')}</i></b>
              <small>{localizePreview(st === 'verified' ? m.verifiedSub : m.sub, lang)}</small>
            </div>
            <div className="rwv4-right">
              <span className={`rwv4-amt ${m.kind === 'raffle_ticket' ? 'ticket' : 'coupon'}`}>{localizePreview(m.rewardLabel, lang)}</span>
              {st === 'idle' ? (
                <button type="button" className="rwv4-cta ghost" onClick={() => setStage((cur) => ({ ...cur, [m.id]: 'verified' }))}>{t('rewardVerify')}</button>
              ) : (
                <button type="button" className="rwv4-cta" onClick={() => setStage((cur) => ({ ...cur, [m.id]: 'claimed' }))}>{t('rewardClaim')}</button>
              )}
            </div>
          </article>
        );
      })}
    </section>
  );
}

/** 预览层:自动到账广告位(AUTOMÁTICO)+ 细则。 */
function PreviewAutoBanners() {
  const t = useI18nStore((state) => state.t);
  const lang = useI18nStore((state) => state.language);
  const [rules, setRules] = useState<PreviewRules | null>(null);
  return (
    <section className="activity-section-block" data-preview-auto-react="true">
      <div className="activity-section-title">{t('rewardAutoTitle')}</div>
      {PREVIEW_AUTO_BANNERS.map((b) => (
        <button type="button" className="rwv4-auto-banner" key={b.id} onClick={() => setRules(b.rules)}>
          <span className="rwv4-auto-tag">{t('rewardAutoTag')}</span>
          <span className="rwv4-ic auto">{b.icon}</span>
          <span className="rwv4-main">
            <b>{localizePreview(b.title, lang)}</b>
            <small>{localizePreview(b.sub, lang)}</small>
            <em className="rwv4-auto-more">{t('rewardRules')}</em>
          </span>
        </button>
      ))}
      {rules ? <PreviewRulesSheet rules={rules} onClose={() => setRules(null)} /> : null}
    </section>
  );
}

/** 券历史:已用(核销存款+实得彩金)/已过期。片2。 */
function CouponHistorySheet({ token, onClose }: { token: string; onClose: () => void }) {
  const t = useI18nStore((state) => state.t);
  const lang = useI18nStore((state) => state.language);
  const query = useQuery({ queryKey: ['coupon-history', token], queryFn: () => fetchCouponHistory(token), enabled: !!token });
  const items = query.data?.items || [];
  const used = items.filter((c) => c.status === 'used');
  const expired = items.filter((c) => c.status === 'expired');
  const localize = (n?: Record<string, string>) => (n && (n[lang] || n['pt-BR'] || n['en'])) || '';
  const dayLabel = (sec?: number | null) => (sec ? new Date(sec * 1000).toLocaleDateString() : '');
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet" role="dialog" aria-modal="true" aria-label={t('couponHistoryLink')} onMouseDown={(e) => e.stopPropagation()} data-coupon-history-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div><h2>{t('couponHistoryLink')}</h2></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>
        <div className="activity-card-list">
          {query.isLoading ? <SheetState icon={RefreshCw} title={t('loading')} /> : null}
          {query.isError ? <SheetState icon={HelpCircle} title={t('activitiesLoadError')} actionLabel={t('retry')} onAction={() => query.refetch()} /> : null}
          {!query.isLoading && !query.isError && items.length === 0 ? <SheetState icon={Gift} title={t('couponHistoryEmpty')} /> : null}
          {used.length ? (
            <section className="activity-section-block">
              <div className="activity-section-title">{t('couponHistoryUsed')}</div>
              <div className="coupon-wallet-list">
                {used.map((c) => (
                  <article className="coupon-card" key={c.id}>
                    <div className="coupon-card-body">
                      <b>{withTrueRate(localize(c.nameI18n) || c.code, c, lang)}</b>
                      <small>
                        {dayLabel(c.usedAt)}
                        {Number(c.depositAmount || 0) > 0 ? ` · ${t('couponHistoryDeposit').replace('%v%', fmtMoney(Number(c.depositAmount)))}` : ''}
                      </small>
                    </div>
                    {Number(c.bonusEarned || 0) > 0 ? <span className="coupon-hist-earned">+{fmtMoney(Number(c.bonusEarned))}</span> : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}
          {expired.length ? (
            <section className="activity-section-block">
              <div className="activity-section-title">{t('couponHistoryExpired')}</div>
              <div className="coupon-wallet-list">
                {expired.map((c) => (
                  <article className="coupon-card expired" key={c.id}>
                    <div className="coupon-card-body">
                      <b>{withTrueRate(localize(c.nameI18n) || c.code, c, lang)}</b>
                      <small>{t('couponStatusExpired')}{c.usedAt ? ` · ${dayLabel(c.usedAt)}` : ''}</small>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </section>
    </div>
  );
}


function CheckinCard({
  checkin,
  days,
  pending,
  justCleared,
  onClaim,
  onDeposit,
}: {
  checkin: ActivityCheckin;
  days: CheckinDay[];
  pending: boolean;
  justCleared: Set<string>;
  onClaim: () => void;
  onDeposit: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const eligible = Boolean(checkin.eligibility?.eligible);
  const claimed = Boolean(checkin.claimed);
  const streakDay = Math.max(1, Math.min(7, checkin.streakDay || 1));
  const nextDay = days.find((d) => d.day === streakDay);
  // Say what unlocks it, not that it is locked. The bet track had this; the two
  // tracks actually running (login, deposit) fell back to a bare "Locked".
  const notEligibleLabel = checkin.track === 'bet'
    ? t('activitiesCheckinNotEligibleBet')
    : checkin.track === 'deposit'
      ? t('activitiesCheckinNotEligibleDeposit')
      : checkin.track === 'login'
        ? t('activitiesCheckinNotEligibleLogin')
        : t('activitiesNotEligible');
  const todayText = checkinRewardText(checkinDayRewards(nextDay as RichCheckinDay | undefined), t);
  // After day 7 the streak HOLDS at 7 (verified against nextStreakDay): every
  // further consecutive day pays R(7) again. "Tomorrow" therefore always has a
  // real figure — day 7 is a plateau, not an ending.
  const tomorrowDay = days.find((d) => d.day === Math.min(streakDay + 1, 7));
  const tomorrowText = checkinRewardText(checkinDayRewards(tomorrowDay as RichCheckinDay | undefined), t);
  const isDeposit = checkin.track === 'deposit';
  const actionable = eligible && !claimed;
  // Four states, in the order a player meets them: do the thing (deposit A),
  // take the money (claim), come back tomorrow (claimed), and a defensive
  // not-eligible fallback for the login track.
  const ask = actionable
    ? (isDeposit
        ? t('activitiesCheckinDepositAskClaim')
        : streakDay === 7 ? t('activitiesCheckinAskDay7') : t('activitiesCheckinAskClaimDay').replace('%n%', fmtInt(streakDay)))
    : claimed
      ? (streakDay >= 7
          ? t(isDeposit ? 'activitiesCheckinDepositAskWeekDone' : 'activitiesCheckinAskWeekDone')
          // The backend resets a missed day regardless of config — the warning
          // is always true, so it always shows (never a lying soft variant).
          : t(isDeposit ? 'activitiesCheckinDepositAskTomorrowReset' : 'activitiesCheckinAskComeBackReset'))
      // Day 1 sells "any amount" (kills the how-much fear). From day 2 there
      // is a streak to lose, and loss beats promise: say what missing today
      // costs. The warning is true — the backend resets on any missed day.
      : isDeposit
        ? (streakDay > 1 ? t('activitiesCheckinDepositAskDoStreak') : t('activitiesCheckinDepositAskDo'))
        : notEligibleLabel;
  const needsDeposit = isDeposit && !eligible && !claimed;
  // The card is one tap target wherever a tap does something: claim in place,
  // or go deposit. Claimed cards are static — a press that changes nothing
  // promises nothing.
  const bodyAction = actionable && !pending ? onClaim : needsDeposit ? onDeposit : null;
  const paysTickets = days.some((d) => checkinDayRewards(d as RichCheckinDay).some((r) => Number(r.ticket_count || 0) > 0));
  // Composite days (coupon + tickets) produce payoffs no 21px row can hold.
  // The biggest item gets the big type; the rest rides legible beside it —
  // money is never ellipsized. On a plain day the note slot instead says when
  // the figure lands ("ao depositar"), the same grammar as "ao confirmar".
  const splitReward = (text: string) => {
    const parts = text.split(' + ');
    return { main: parts[0] || '', rest: parts.length > 1 ? `+ ${parts.slice(1).join(' + ')}` : '' };
  };
  const todaySplit = splitReward(todayText || '');
  const tomorrowSplit = splitReward(tomorrowText || '');
  // The finale hook. Day 7 is why a streak is worth keeping, and its tile can
  // only say "1🎟+" — the "+" hides a coupon. When the last day pays more than
  // one thing, its full worth gets a line of its own under the strip.
  const finaleDay = days[days.length - 1];
  const finaleRewards = finaleDay ? checkinDayRewards(finaleDay as RichCheckinDay) : [];
  // At the day-7 plateau the dim payoff already reads "Amanhã: <day 7's full
  // worth>" — the finale line would repeat it word for word.
  const finaleText = finaleRewards.length > 1 && !(claimed && streakDay >= 7)
    ? checkinRewardText(finaleRewards, t)
    : '';
  return (
    <article
      className={`reward-card mission-row activity-checkin-card ${claimed ? 'claimed' : eligible ? 'eligible' : ''}${bodyAction ? '' : ' is-static'}`}
      style={{ '--accent': ACTIVITY_ACCENT[`checkin:${checkin.track}`] || ACCENT_FALLBACK } as CSSProperties}
      onClick={bodyAction || undefined}
      data-pays-tickets={paysTickets ? 'true' : undefined}
    >
      <span className="mission-body">
        <strong className="mission-title">{checkinTrackLabel(checkin.track, t)}</strong>
        <small className="mission-ask">{ask}</small>
        <span className="mission-payoff">
          {claimed ? (
            <b className="mission-reward is-dim">
              <i className="payoff-prefix">{t('actTomorrowPrefix')} </i>
              {tomorrowSplit.main || '—'}
              {tomorrowSplit.rest ? <i className="payoff-note is-stack">{tomorrowSplit.rest}</i> : null}
            </b>
          ) : (
            <b className="mission-reward">
              {todaySplit.main || '—'}
              {todaySplit.rest
                ? <i className="payoff-note is-stack">{todaySplit.rest}</i>
                : needsDeposit ? <i className="payoff-note">{t('actOnDeposit')}</i> : null}
            </b>
          )}
          {actionable ? (
            <button
              type="button"
              className="checkin-claim"
              disabled={pending}
              aria-label={`${t('activitiesClaimNow')} — ${todayText}`}
              onClick={(event) => { event.stopPropagation(); onClaim(); }}
            >
              {t('activitiesClaimNow')}
            </button>
          ) : needsDeposit ? (
            <em className="mission-cta">{t('activitiesCheckinDepositCta')}</em>
          ) : (
            <em className="mission-cta is-muted">{claimed ? t('activitiesClaimed') : notEligibleLabel}</em>
          )}
        </span>
      <div className="checkin-days">
        {days.map((d) => {
          const clearedThrough = claimed ? streakDay : streakDay - 1;
          const state =
            d.day <= clearedThrough ? 'done'
              : d.day === clearedThrough + 1 && d.day <= 7 ? (claimed ? 'next' : 'today')
                : 'up';
          // Deposit track, today's tile: empty gold outline before the
          // deposit, filled once it lands — the A→B story in strip grammar.
          const fillToday = state === 'today' && isDeposit && eligible;
          return (
            <div
              key={d.day}
              className={`checkin-day ${state}${fillToday ? ' is-full' : ''}${justCleared.has(`c:${checkin.track}:${d.day - 1}`) ? ' is-just' : ''}`}
              style={fillToday ? ({ '--step-fill': '100%' } as CSSProperties) : undefined}
            >
              <b title={checkinRewardText(checkinDayRewards(d as RichCheckinDay), t)}>{checkinTileLabel(d as RichCheckinDay)}</b>
              {/* The row labels positions — Dia 1..7 — and "Hoje" earns its
                  place on the one tile you can act on. "Amanhã" did not: it
                  needs 41px in a 40px tile and clipped to "Ama…", while the
                  payoff line above already opens with "Amanhã:" and the tile
                  is ringed. So tomorrow keeps its day number, like its
                  neighbours, and the row reads in one grammar again. */}
              <span>
                {state === 'today'
                  ? t('activitiesCheckinTodayTile')
                  : t('activitiesCheckinDay').replace('%n%', String(d.day))}
              </span>
            </div>
          );
        })}
      </div>
      {finaleText ? (
        <small className="checkin-finale">
          {t('activitiesCheckinFinale').replace('%d%', fmtInt(finaleDay?.day || 7)).replace('%v%', finaleText)}
        </small>
      ) : null}
      </span>
    </article>
  );
}


function InviteSheet({ user, displayId, onClose }: { user: PlayerUser | null; displayId: string; onClose: () => void }) {
  const t = useI18nStore((state) => state.t);
  const code = String(user?.invite_code || user?.inviteCode || displayId || '');
  const url = inviteUrl(user, code);
  const [copyMessage, setCopyMessage] = useState('');
  const [copyError, setCopyError] = useState(false);

  async function copyInvite() {
    const value = url || code;
    if (!value) {
      setCopyError(true);
      setCopyMessage(t('inviteCopyFail'));
      window.setTimeout(() => setCopyMessage(''), 1800);
      return;
    }
    const ok = await copyText(value);
    setCopyError(!ok);
    setCopyMessage(t(ok ? 'inviteCopied' : 'inviteCopyManual'));
    window.setTimeout(() => setCopyMessage(''), 2200);
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet invite-sheet-react" role="dialog" aria-modal="true" aria-label={t('inviteTitle')} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('inviteTitle')}</h2>
            <p>{t('inviteSubtitle')}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>

        <div className="invite-code-card">
          <span>{t('inviteCodeLabel')}</span>
          <strong>{code || '-'}</strong>
          <button className="invite-copy-indigo" type="button" onClick={copyInvite}>{t('inviteCopyBtn')}</button>
          {copyMessage ? (
            <p className={copyError ? 'form-error invite-copy-feedback' : 'form-success invite-copy-feedback'} data-invite-copy-feedback-react="true">
              {copyMessage}
            </p>
          ) : null}
        </div>

        <div className="invite-link-card">
          <span>{t('inviteLinkLabel')}</span>
          <code>{url || '-'}</code>
        </div>

        <div className="invite-steps invite-steps-legacy">
          <span><Check size={14} className="text-emerald-400 shrink-0" aria-hidden="true" /> {t('inviteStep1')}</span>
          <span><Check size={14} className="text-emerald-400 shrink-0" aria-hidden="true" /> {t('inviteStep2')}</span>
        </div>
      </section>
    </div>
  );
}

function SheetState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="wallet-empty compact sheet-state-react">
      <Icon size={24} aria-hidden="true" />
      <strong>{title}</strong>
      {description ? <span>{description}</span> : null}
      {actionLabel && onAction ? (
        <button type="button" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function LimitInput({ label, value, max, onChange }: { label: string; value: number; max: number; onChange: (value: number) => void }) {
  return (
    <label className="rg-limit-input">
      <span>{label}<b>{fmtMoney(value)}</b></span>
      <input value={String(value)} inputMode="numeric" onChange={(event) => onChange(clampMoney(event.target.value, 0, max))} />
    </label>
  );
}

function UsageCell({ label, used, remaining }: { label: string; used?: number; remaining?: number }) {
  const t = useI18nStore((state) => state.t);
  return (
    <div>
      <span>{label}</span>
      <strong>{fmtMoney(used || 0)}</strong>
      <small>{t('rgDailyRemaining')} {fmtMoney(remaining || 0)}</small>
    </div>
  );
}

function PendingLimits({ limits }: { limits: ResponsibleGamingLimits }) {
  const t = useI18nStore((state) => state.t);
  const rows = [
    { amount: limits.pending_daily_bet_limit_brl, at: limits.pending_daily_bet_limit_apply_at },
    { amount: limits.pending_weekly_bet_limit_brl, at: limits.pending_weekly_bet_limit_apply_at },
    { amount: limits.pending_monthly_bet_limit_brl, at: limits.pending_monthly_bet_limit_apply_at },
  ].filter((row) => Number(row.amount || 0) > 0);
  if (rows.length === 0) return null;
  return (
    <div className="rg-pending-list">
      {rows.map((row, index) => (
        <span key={`${row.amount}:${row.at}:${index}`}>
          {t('rgLimitPending')} · {fmtMoney(row.amount || 0)} · {formatTime(row.at || 0)}
        </span>
      ))}
    </div>
  );
}

function coolOffLabelKey(days: number): MessageKey {
  if (days === 1) return 'rgCoolOff24h';
  if (days === 7) return 'rgCoolOff7d';
  if (days === 30) return 'rgCoolOff30d';
  return 'rgCoolOff60d';
}

function PreferenceRow({
  icon: Icon,
  label,
  enabled,
  pending,
  onToggle,
}: {
  icon: typeof Bell;
  label: string;
  enabled: boolean;
  pending: boolean;
  onToggle: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  return (
    <button className="me-menu-row" type="button" disabled={pending} onClick={onToggle}>
      <Icon size={19} aria-hidden="true" />
      <span>{label}</span>
      <b className={enabled ? 'enabled' : ''}>{pending ? t('submitting') : enabled ? t('on') : t('off')}</b>
    </button>
  );
}

function preferenceSavedMessage(
  payload: { public_leaderboard?: boolean; activity_notifications?: boolean },
  t: ReturnType<typeof useI18nStore.getState>['t'],
) {
  if (typeof payload.public_leaderboard === 'boolean') {
    return t(payload.public_leaderboard ? 'preferenceLeaderboardOn' : 'preferenceLeaderboardOff');
  }
  if (typeof payload.activity_notifications === 'boolean') {
    return t(payload.activity_notifications ? 'preferenceActivityOn' : 'preferenceActivityOff');
  }
  return t('preferencesSaved');
}

function avatarUrl(user: PlayerUser | null) {
  const direct = user?.avatar_url || user?.avatar || user?.avatar_thumb || user?.avatarThumb || '';
  if (direct) return direct;
  const defaultId = currentAvatarDefaultId(user);
  return defaultId ? defaultAvatarUrl(defaultId) : '';
}

function preferredVerifyChannel(user: PlayerUser | null): 'phone' | 'email' {
  if (!Boolean(user?.phone_verified ?? user?.phoneVerified)) return 'phone';
  return 'email';
}

function vipNearbyLevels(level: number, thresholds: Record<string, number>) {
  const levels = [level - 1, level, level + 1, level + 2]
    .filter((item) => item >= 1 && item <= 100)
    .filter((item, index, arr) => arr.indexOf(item) === index);
  return levels
    .map((item) => ({ level: item, threshold: Number(thresholds[String(item)] || 0) }))
    .filter((item) => item.level === 1 || item.threshold > 0 || thresholds[String(item)] != null);
}

async function copyText(value: string) {
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the selection fallback used by older mobile browsers.
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    textarea.remove();
    return ok;
  } catch {
    return false;
  }
}

const AVATAR_CROP_SIZE = 280;

type AvatarCropState = {
  src: string;
  image: HTMLImageElement;
  imageWidth: number;
  imageHeight: number;
  baseScale: number;
  scale: number;
  x: number;
  y: number;
};

function avatarCropTransform(crop: AvatarCropState) {
  const effectiveScale = crop.scale * crop.baseScale;
  return `translate(-50%, -50%) translate(${crop.x}px, ${crop.y}px) scale(${effectiveScale})`;
}

function clampAvatarCrop(crop: AvatarCropState): AvatarCropState {
  const effectiveScale = crop.scale * crop.baseScale;
  const halfW = Math.max(0, (crop.imageWidth * effectiveScale - AVATAR_CROP_SIZE) / 2);
  const halfH = Math.max(0, (crop.imageHeight * effectiveScale - AVATAR_CROP_SIZE) / 2);
  return {
    ...crop,
    x: Math.max(-halfW, Math.min(halfW, crop.x)),
    y: Math.max(-halfH, Math.min(halfH, crop.y)),
  };
}

function renderAvatarCrop(crop: AvatarCropState) {
  const effectiveScale = crop.scale * crop.baseScale;
  const sourceSize = AVATAR_CROP_SIZE / effectiveScale;
  const sx = crop.imageWidth / 2 - crop.x / effectiveScale - sourceSize / 2;
  const sy = crop.imageHeight / 2 - crop.y / effectiveScale - sourceSize / 2;
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(crop.image, sx, sy, sourceSize, sourceSize, 0, 0, canvas.width, canvas.height);
  for (const format of ['image/webp', 'image/jpeg']) {
    for (const quality of [0.85, 0.75, 0.65]) {
      const dataUrl = canvas.toDataURL(format, quality);
      if (estimatedDataUrlBytes(dataUrl) <= 420 * 1024) return dataUrl;
    }
  }
  return canvas.toDataURL('image/jpeg', 0.6);
}

function estimatedDataUrlBytes(dataUrl: string) {
  const comma = dataUrl.indexOf(',');
  return Math.ceil((dataUrl.length - comma - 1) * 3 / 4);
}

// Mission content lives here for now instead of activity_definition rows:
// shipping copy with the app is one deploy instead of a migration plus an ops
// pass. Shape matches a drum item exactly, so nothing downstream cares where a
// card came from, and promoting these to backend content later just means
// deleting this list. Order = easiest first: a win the player can finish today
// is what earns the second attempt. No progress_pct — these are not tracked
// per user, and a bar pinned at 0% reads as "you can't get anywhere".
type ActivityConfigLike = { activityType?: string; enabled?: boolean; config?: Record<string, unknown> };
type ActivityTierReward = { kind?: string; amount?: number; ticket_count?: number; coupon_code?: string };
type ActivityTierWithRewards = { amount?: number; rewards?: ActivityTierReward[] };

function activityTierRewards(tier: ActivityTierWithRewards | undefined): ActivityTierReward[] {
  if (tier?.rewards?.length) return tier.rewards;
  const amount = Number(tier?.amount || 0);
  return amount > 0 ? [{ kind: 'cash', amount }] : [];
}

// Mission cards are authored here, but every figure on them is read from the
// live activity_config. Hardcoding numbers is how a card ends up advertising a
// bonus ops已关闭 or a rate they retuned — in a money product that is a broken
// promise, not a stale label. A card only renders when its activity is enabled,
// and only claims a reward when one is actually funded.
// Clearing a rung is the one thing on this page worth celebrating, so it gets
// exactly one beat. We remember the count we last showed you, per ladder, so
// the pop fires when YOU cleared it — not on every render, not again on the
// next visit, and never on a first load for progress you made before this
// existed. Wrong-way movement (a reset streak) just re-records, silently.
const LADDER_SEEN_KEY = 'sorteia:ladderCleared';

function readLadderSeen(): Record<string, number> {
  try {
    const parsed = JSON.parse(localStorage.getItem(LADDER_SEEN_KEY) || 'null');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function useJustCleared(counts: Record<string, number>): Set<string> {
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());
  const seenRef = useRef<Record<string, number> | null>(null);
  const signature = JSON.stringify(counts);
  useEffect(() => {
    if (!seenRef.current) seenRef.current = readLadderSeen();
    const seen = seenRef.current;
    const parsed = JSON.parse(signature) as Record<string, number>;
    const next = new Set<string>();
    let changed = false;
    Object.entries(parsed).forEach(([key, count]) => {
      const before = seen[key];
      if (before !== undefined && count > before) {
        for (let index = before; index < count; index += 1) next.add(`${key}:${index}`);
      }
      if (before !== count) {
        seen[key] = count;
        changed = true;
      }
    });
    if (changed) {
      try {
        localStorage.setItem(LADDER_SEEN_KEY, JSON.stringify(seen));
      } catch {
        /* a full or blocked store only costs the animation, never the reward */
      }
    }
    if (!next.size) return undefined;
    setFresh(next);
    const timer = setTimeout(() => setFresh(new Set()), 1600);
    return () => clearTimeout(timer);
  }, [signature]);
  return fresh;
}

// The one figure in the ask that answers "what am I holding" gets the card's
// accent — the sentence stays dim, the number does not.
function renderAskWithAccent(ask: string, accent?: string): ReactNode {
  if (!accent) return ask;
  const index = ask.indexOf(accent);
  if (index < 0) return ask;
  return (
    <>
      {ask.slice(0, index)}
      <b className="ask-num">{accent}</b>
      {ask.slice(index + accent.length)}
    </>
  );
}

// A countdown that visibly runs — h:mm:ss / mm:ss on a one-second tick, same
// clock format as the Sorteios hero. Only this span re-renders, never the
// feed. Once the clock reads at-or-past the draw it switches to the absolute
// time (in the viewer's own timezone), so the card never counts down to a lie.
function LiveCountdown({ at, variant, t }: {
  at: number;
  variant?: 'long' | 'short';
  t: ReturnType<typeof useI18nStore.getState>['t'];
}) {
  const [now, setNow] = useState(() => currentUnixSeconds());
  useEffect(() => {
    const id = window.setInterval(() => setNow(currentUnixSeconds()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (at - now > 0) {
    const key = variant === 'short' ? 'rewardsRaffleCountdownSuffixShort' : 'rewardsRaffleCountdownSuffix';
    return <span className="ask-countdown">{t(key).replace('%t%', formatRaffleCountdown(at, now))}</span>;
  }
  const drawTime = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(new Date(at * 1000));
  const key = variant === 'short' ? 'rewardsRaffleTimeSuffixShort' : 'rewardsRaffleTimeSuffix';
  return <span className="ask-countdown">{t(key).replace('%h%', drawTime)}</span>;
}

// One ladder language for every activity: a rung is cleared, is the one you
// can clear now, or is still ahead. Same grammar as the check-in week strip,
// so a player learns it once.
function ladderSteps<T>(
  entries: T[],
  cleared: number,
  describe: (entry: T, index: number) => { label: string; reward: string },
): NonNullable<ActivityDrumItem['steps']> {
  return entries.map((entry, index) => ({
    ...describe(entry, index),
    state: index < cleared ? 'done' : index === cleared ? 'now' : 'up',
  }));
}

// The rung you are on is also the progress bar. A separate cyan bar underneath
// said the same thing in a second visual language and competed with the gold
// rung for the eye — worse, its fraction counted cleared rungs while the card's
// own subtitle counted the next one, so one card showed 2/5 and "deposit 3 of
// 5" at the same time. One meter, one number.
function withLadderProgress(
  steps: NonNullable<ActivityDrumItem['steps']>,
  progressAt: (index: number) => { fill: number; label?: string } | null,
): NonNullable<ActivityDrumItem['steps']> {
  const index = steps.findIndex((step) => step.state === 'now');
  if (index < 0) return steps;
  const progress = progressAt(index);
  if (!progress) return steps;
  const fill = Math.max(0, Math.min(1, progress.fill));
  // The count shows even at zero — "0/5 rodadas" is what tells a fresh player
  // this rung is a meter, not a price tag. Only the fill waits for progress.
  return steps.map((step, i) => (i === index
    ? { ...step, ...(fill > 0 ? { fill } : {}), label: progress.label || step.label }
    : step));
}

function buildLocalMissions(
  configs: ActivityConfigLike[],
  live: {
    dailyBet?: ActivityDailyBet; referrals?: ActivityReferrals;
    emailVerified?: boolean; phoneVerified?: boolean;
    vipLevel?: number; vipProgressPct?: number; vipNextRequired?: number;
    vipBetHave?: number; vipBetTarget?: number;
    vipDepositHave?: number; vipDepositTarget?: number; vipDepositRequired?: number;
    ticketBalance?: number; rafflePrizeCeiling?: number;
    rafflePrizeBrackets?: Array<{
      min_n?: number; max_n?: number; pool?: number;
      tiers?: Array<{ count?: number; amount?: number; rewards?: Array<{ amount?: number }> }>;
    }>;
    raffleEntries?: number;
    raffleStatus?: string; raffleScheduledAt?: number;
    raffleMyEntries?: number; raffleCanAdd?: boolean;
    hasEmail?: boolean;
  },
  t: ReturnType<typeof useI18nStore.getState>['t'],
): ActivityDrumItem[] {
  const on = (type: string) => configs.find((c) => c.activityType === type && c.enabled);
  const missions: ActivityDrumItem[] = [];

  // The one activity that costs nothing was the last line of a name-only list
  // at the bottom of the page. It goes first, and it says what the player is
  // actually holding — no invented prize figure, just their real ticket count.
  const tickets = Number(live.ticketBalance || 0);
  const ceiling = Number(live.rafflePrizeCeiling || 0);
  const brackets = [...(live.rafflePrizeBrackets || [])].sort((a, b) => Number(a.min_n || 0) - Number(b.min_n || 0));
  const entries = Number(live.raffleEntries || 0);
  // Which bracket today's draw is in, and what each place in it pays. Nothing
  // is "cleared" here — these are prizes, not rungs you climb — so they all
  // render neutral.
  const liveBracket = brackets.find((b) => entries >= Number(b.min_n || 0) && entries <= Number(b.max_n || 0)) || brackets[0];
  let place = 0;
  // Each tile is one prize tier: how much, which places, how many winners.
  // "#1" alone read as a mystery code — rank plus an explicit ×count is what
  // makes it a prize table. The top prize wears the gold outline: a podium
  // has a first place.
  const prizePlaces = (liveBracket?.tiers || []).slice(0, 4).map((tier, index) => {
    const count = Math.max(1, Number(tier?.count || 1));
    const from = place + 1;
    place += count;
    const cash = (tier?.rewards || []).reduce((sum, r) => sum + Number(r?.amount || 0), 0) || Number(tier?.amount || 0);
    return {
      label: count === 1
        ? t('actStepPrize').replace('%n%', fmtInt(from)).replace('%c%', fmtInt(count))
        : t('actStepPrizeRange').replace('%a%', fmtInt(from)).replace('%b%', fmtInt(place)).replace('%c%', fmtInt(count)),
      reward: cash > 0 ? fmtMoney(cash) : '—',
      state: index === 0 ? ('now' as const) : ('up' as const),
    };
  });
  // Every winner in the bracket, not just the four tiles shown — the honest
  // total behind "até R$ 220".
  const raffleWinners = (liveBracket?.tiers || []).reduce((sum, tier) => sum + Math.max(1, Number(tier?.count || 1)), 0);
  // The raffle card is a state machine over the live event. State order is the
  // honesty order: entered beats everything (never resell a draw the player is
  // already in), a full draw beats the zero-ticket errand (never send someone
  // to earn a ticket they cannot use).
  if (brackets.length) {
    const registering = (live.raffleStatus || 'registering') === 'registering';
    const mine = Number(live.raffleMyEntries || 0);
    const canAdd = Boolean(live.raffleCanAdd);
    // The card renders the countdown itself, live — build only says which draw
    // to count to and how much sentence to wrap around it. The component falls
    // back to the absolute time (in the viewer's own timezone — draws are
    // shared across countries, clocks are not) once the clock reads at-or-past
    // the draw, so the card never counts down to a lie.
    const at = Number(live.raffleScheduledAt || 0);
    let ask: string; let ctaKey: MessageKey; let ctaAction: string; let band = 1;
    let askAccent: string | undefined; let countdownVariant: 'long' | 'short' | undefined;
    if (!registering) {
      // Locked in: the reveal is imminent, so the countdown is the whole story.
      ask = mine > 0 ? t('rewardsRaffleAskLockedIn') : t('rewardsRaffleAskLockedOut');
      if (mine > 0) countdownVariant = 'short';
      ctaKey = mine > 0 ? 'rewardsRaffleCtaNumbers' : 'rewardsRaffleCtaSchedule';
      ctaAction = mine > 0 ? 'raffle-participants' : 'sorteios';
      band = 4;
    } else if (mine > 0) {
      ask = mine === 1 ? t('rewardsRaffleAskEnteredOne') : t('rewardsRaffleAskEntered').replace('%n%', fmtInt(mine));
      askAccent = fmtInt(mine);
      countdownVariant = 'short';
      ctaKey = canAdd ? 'rewardsRaffleCtaAdd' : 'rewardsRaffleCtaNumbers';
      ctaAction = canAdd ? 'raffle-register' : 'raffle-participants';
      band = canAdd ? 1 : 4;
    } else if (!canAdd) {
      // Full comes before the zero-ticket errand: earning a ticket for a full
      // draw would be a fetch-quest with no prize at the end.
      ask = t('rewardsRaffleAskFull');
      countdownVariant = 'short';
      ctaKey = 'rewardsRaffleCtaSchedule';
      ctaAction = 'sorteios';
      band = 4;
    } else if (tickets <= 0) {
      ask = t('rewardsRaffleAskEarn');
      ctaKey = 'rewardsRaffleCtaEarn';
      ctaAction = 'earn-tickets';
    } else {
      ask = tickets === 1 ? t('rewardsRaffleAskUseOne') : t('rewardsRaffleAskUse').replace('%n%', fmtInt(tickets));
      askAccent = fmtInt(tickets);
      countdownVariant = 'long';
      ctaKey = 'rewardsRaffleCta';
      ctaAction = 'raffle-register';
    }
    missions.push({
      id: 'local:daily_raffle',
      type: 'daily_raffle',
      group: 'today',
      band,
      catalog_code: 'raffle',
      icon: 'ticket',
      color_theme: 'cyan',
      tier: 1,
      priority: 95,
      title_main_i18n: 'rewardsRaffleTitle',
      secondary: ask,
      ask_accent: askAccent,
      countdown_at: countdownVariant && at > 0 ? at : undefined,
      countdown_variant: countdownVariant,
      // The programme's ceiling with "até": the pool only reaches it once a
      // draw fills, and "até" is what keeps that honest on a quiet draw.
      reward_label: ceiling > 0
        ? t('rewardsRaffleUpTo').replace('%v%', fmtMoney(ceiling))
        : t('rewardsRaffleFree'),
      reward_note: ceiling > 0 && raffleWinners > 1
        ? t('rewardsRaffleWinnersNote').replace('%n%', fmtInt(raffleWinners))
        : undefined,
      steps: prizePlaces.length ? prizePlaces : undefined,
      cta_label_i18n: ctaKey,
      cta_action: ctaAction,
    });
  }

  const bet = on('daily_bet_rounds');
  if (bet) {
    const cfg = bet.config as { tiers?: Array<{ rounds?: number; amount?: number; rewards?: ActivityTierReward[] }>; min_single_bet_amount?: number } | undefined;
    const tiers = cfg?.tiers || [];
    const done = Number(live.dailyBet?.qualifiedRounds || 0);
    // Aim at the next tier the player has not cleared, not always the first.
    const liveTiers = live.dailyBet?.tiers || [];
    const nextTier = liveTiers.find((tier) => !tier.reached) || liveTiers[liveTiers.length - 1] || tiers[0];
    const target = Number(nextTier?.rounds || tiers[0]?.rounds || 0);
    const configuredTier = tiers.find((tier) => Number(tier.rounds || 0) === target) || tiers[0];
    const reward = checkinRewardText(activityTierRewards(configuredTier), t);
    // Ladder rungs read their reward the same way the headline does, so a tier
    // paying tickets or a coupon shows what it really pays.
    const rungTiers = liveTiers.length ? liveTiers : tiers;
    const tierReward = (rounds: number) => {
      const cfgTier = tiers.find((tier) => Number(tier.rounds || 0) === rounds);
      return checkinRewardText(activityTierRewards(cfgTier), t) || '—';
    };
    const min = Number(cfg?.min_single_bet_amount || 0);
    const maxRounds = Math.max(0, ...rungTiers.map((tier) => Number(tier.rounds || 0)));
    const allCleared = maxRounds > 0 && done >= maxRounds;
    const remaining = Math.max(0, target - done);
    // Fresh: name the whole job. In progress: name only what is LEFT — the
    // rung already shows position. Done: the comeback line, and tomorrow's
    // first tier as a dimmed hook.
    const missionAsk = allCleared
      ? t('actDailyMissionAskDone')
      : done === 0
        ? (min > 0
            ? t('actDailyMissionAskStart').replace('%rounds%', fmtInt(target)).replace('%min%', fmtMoney(min))
            : t('actDailyMissionAskStartNoMin').replace('%rounds%', fmtInt(target)))
        : (min > 0
            ? (remaining === 1
                ? t('actDailyMissionAskLeftOne').replace('%min%', fmtMoney(min))
                : t('actDailyMissionAskLeft').replace('%n%', fmtInt(remaining)).replace('%min%', fmtMoney(min)))
            : t('actDailyMissionAskLeftNoMin').replace('%n%', fmtInt(remaining)));
    const tierOneReward = checkinRewardText(activityTierRewards(tiers[0]), t);
    // The day's whole pot, shown only when it is bigger than the headline and
    // honestly summable — every tier pure cash. Mixed currencies (tickets,
    // coupons) do not add up to one figure, so they get no invented total.
    const tierCash = (tier: ActivityTierWithRewards) => {
      const rewards = activityTierRewards(tier);
      if (!rewards.length || rewards.some((r) => Number(r.ticket_count || 0) > 0 || r.coupon_code)) return 0;
      return rewards.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    };
    const dayCashTotal = tiers.length > 1 && tiers.every((tier) => tierCash(tier) > 0)
      ? tiers.reduce((sum, tier) => sum + tierCash(tier), 0)
      : 0;
    missions.push({
      id: 'local:daily_bet_rounds',
      type: 'mission_daily',
      group: 'today',
      band: allCleared ? 4 : 1,
      catalog_code: 'daily_task',
      icon: 'gift',
      color_theme: 'gold',
      tier: 1,
      priority: 90,
      title_main_i18n: 'actDailyMissionTitle',
      secondary: missionAsk,
      progress_pct: target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0,
      progress_label: `${fmtInt(done)}/${fmtInt(target)}`,
      steps: withLadderProgress(
        ladderSteps(
          rungTiers,
          rungTiers.filter((tier) => done >= Number(tier.rounds || 0)).length,
          (tier) => ({
            label: t('actStepRounds').replace('%n%', fmtInt(Number(tier.rounds || 0))),
            reward: tierReward(Number(tier.rounds || 0)),
          }),
        ),
        (index) => {
          // Rungs are cumulative, so the rung you are on starts where the
          // previous one ended — 7 of 10 rounds is 2/5 into the second rung.
          const from = Number(rungTiers[index - 1]?.rounds || 0);
          const to = Number(rungTiers[index]?.rounds || 0);
          if (to <= from) return null;
          return {
            fill: (done - from) / (to - from),
            label: t('actStepRoundsNow').replace('%d%', fmtInt(done)).replace('%n%', fmtInt(to)),
          };
        },
      ),
      reward_label: (allCleared ? tierOneReward : reward) || undefined,
      reward_prefix: allCleared ? t('actTomorrowPrefix') : undefined,
      reward_note: !allCleared && dayCashTotal > 0
        ? t('actDailyMissionPoolNote').replace('%v%', fmtMoney(dayCashTotal))
        : undefined,
      reward_dim: allCleared,
      pays_tickets: tiers.some((tier) => activityTierRewards(tier).some((r) => Number(r.ticket_count || 0) > 0)),
      // "Jogar agora" beside "volte amanhã" reads as "play now to get
      // tomorrow's prize today" — it does not. Done state drops the urgency.
      cta_label_i18n: allCleared ? 'actDailyMissionCtaDone' : 'actDailyMissionCta',
      cta_action: 'hall',
    });
  }

  // The first-deposit ladder is a COUPON, and coupons live in the Cupons
  // pouch — one gold card there already shows all five rungs, which one is
  // live, and the button that spends it. A second copy in the Earn feed was
  // the same five rungs in a second grammar: nothing to claim, nothing Earn
  // could do that the pouch does not. Earn discovers and claims; the pouch
  // holds. This activity therefore builds no card.


  const bind = on('bind_contact');
  if (bind) {
    const cfg = bind.config as {
      phone_rewards?: ActivityTierReward[]; email_rewards?: ActivityTierReward[];
      phone_reward_kind?: string; phone_amount?: number; phone_coupon_code?: string; phone_ticket_count?: number;
      email_reward_kind?: string; email_amount?: number; email_coupon_code?: string; email_ticket_count?: number;
      email_claim_days?: number; phone_claim_days?: number;
    } | undefined;
    const channelRewards = (channel: 'phone' | 'email') => {
      const configured = channel === 'phone' ? cfg?.phone_rewards : cfg?.email_rewards;
      if (configured?.length) return configured;
      const kind = channel === 'phone' ? cfg?.phone_reward_kind : cfg?.email_reward_kind;
      if (kind === 'coupon') return [{ kind, coupon_code: channel === 'phone' ? cfg?.phone_coupon_code : cfg?.email_coupon_code }];
      if (kind === 'raffle_ticket') return [{ kind, ticket_count: Number(channel === 'phone' ? cfg?.phone_ticket_count : cfg?.email_ticket_count) }];
      return [{ kind: 'cash', amount: Number(channel === 'phone' ? cfg?.phone_amount : cfg?.email_amount) }];
    };
    // The two channels remain independent actions. Inside each channel, every
    // configured component is one atomic reward bundle and one inbox claim.
    // A channel already verified simply drops out.
    // WHY THIS CARD HAS NO LADDER: binding a contact is not a climb. Asking
    // for a code and typing it back is one errand, and splitting it into two
    // rungs invented a journey the player does not have. What actually
    // belongs in that space is the reason to do it at all — this is account
    // security (recover the password, keep the account) and the payout is the
    // sweetener, so the strip carries the protections and the ask says so.
    const bindPerks = [t('actBindPerkRecover'), t('actBindPerkProtect')];
    const phoneRewards = channelRewards('phone').filter((reward) => Number(reward.amount || 0) > 0
      || Number(reward.ticket_count || 0) > 0 || Boolean(reward.coupon_code));
    if (phoneRewards.length > 0 && !live.phoneVerified) {
      missions.push({
        id: 'local:bind_phone',
        type: 'bind_contact_phone',
        group: 'once',
        band: 3,
        catalog_code: 'bind',
        icon: 'gift',
        color_theme: 'cyan',
        tier: 1,
        priority: 72,
        title_main_i18n: 'actBindPhoneTitle',
        secondary_i18n: 'actBindPhoneHow',
        reward_label: checkinRewardText(phoneRewards, t),
        reward_note: t('actBindRewardNote'),
        pays_tickets: phoneRewards.some((reward) => Number(reward.ticket_count || 0) > 0),
        perks: bindPerks,
        cta_label_i18n: 'actBindCta',
        cta_action: 'verify-phone',
      });
    }
    const emailRewards = channelRewards('email').filter((reward) => Number(reward.amount || 0) > 0
      || Number(reward.ticket_count || 0) > 0 || Boolean(reward.coupon_code));
    if (emailRewards.length > 0 && !live.emailVerified) {
      const hasEmail = live.hasEmail !== false;
      missions.push({
        id: 'local:bind_email',
        type: 'bind_contact_email',
        group: 'once',
        band: 3,
        catalog_code: 'bind',
        icon: 'gift',
        color_theme: 'cyan',
        tier: 1,
        priority: 71,
        title_main_i18n: hasEmail ? 'actBindEmailTitle' : 'actBindEmailAddTitle',
        secondary_i18n: hasEmail ? 'actBindEmailHow' : 'actBindEmailAddHow',
        reward_label: checkinRewardText(emailRewards, t),
        reward_note: t('actBindRewardNote'),
        pays_tickets: emailRewards.some((reward) => Number(reward.ticket_count || 0) > 0),
        perks: bindPerks,
        cta_label_i18n: hasEmail ? 'actBindCta' : 'actBindEmailAddCta',
        cta_action: 'verify-email',
      });
    }
  }

  const invite = on('invite_first_deposit');
  if (invite) {
    const tiers = [...((invite.config as { tiers?: Array<{ from?: number; to?: number; amount?: number; rewards?: ActivityTierReward[] }> } | undefined)?.tiers || [])]
      .sort((a, b) => Number(a.from || 0) - Number(b.from || 0));
    const invitedToday = Number(live.referrals?.today || 0);
    const firstReward = checkinRewardText(activityTierRewards(tiers[0]), t);
    // THE BACKEND MODEL IS BRACKETS, NOT ORDINALS: friends 1–10 each pay one
    // rate, 11–50 a higher one, 51+ higher still (inviteTier() matches the
    // converted-friend ordinal against [from, to]). The old tiles said "1º,
    // 2º, 3º, #11+", which invented a per-friend ladder that does not exist
    // and hid the fact that the rate GROWS with volume — the whole point of
    // the programme. One tile per real bracket, the live one highlighted.
    //
    // HONESTY: the count that matters is `converted` (friends who actually
    // deposited), because that is what the backend counts and what has been
    // paid. Registrations alone never moved money, so a bracket only shows
    // cleared when converted friends really passed it.
    const converted = Number(live.referrals?.converted || 0);
    const registered = Number(live.referrals?.total || 0);
    const earned = Number(live.referrals?.earned || 0);
    const bracketOf = (n: number) => tiers.find((tier) => n >= Number(tier.from || 0)
      && n <= Number(tier.to || Number.MAX_SAFE_INTEGER));
    // Which bracket the NEXT friend to convert falls in — that is the rate
    // being offered right now, and the headline must state it.
    const liveBracket = bracketOf(converted + 1) || tiers[tiers.length - 1] || tiers[0];
    const liveReward = checkinRewardText(activityTierRewards(liveBracket), t) || firstReward;
    const liveFrom = Number(liveBracket?.from || 1);
    const liveTo = Number(liveBracket?.to || 0);
    const shown = tiers.slice(0, 4);
    const inviteSteps: NonNullable<ActivityDrumItem['steps']> = shown.map((tier, index) => {
      const from = Number(tier.from || 0);
      const to = Number(tier.to || 0);
      const isLive = tier === liveBracket;
      // A bracket is cleared only once every friend in it has converted.
      const cleared = to > 0 && converted >= to;
      // The last bracket's `to` is a config sentinel (10.000) standing in for
      // "no ceiling" — printing it would read as a real cap.
      const openEnded = index === shown.length - 1;
      return {
        label: openEnded || to <= 0
          ? t('actStepFriendUp').replace('%n%', fmtInt(from))
          : t('actStepFriends').replace('%a%', fmtInt(from)).replace('%b%', fmtInt(to)),
        reward: checkinRewardText(activityTierRewards(tier), t) || '—',
        state: cleared ? 'done' as const : isLive ? 'now' as const : 'up' as const,
        // Fill the live bracket by how far INTO it the player is: 12 converted
        // friends is 2 of the 11–50 band, not 12 of 50. Strictly greater than
        // the band's floor — at zero converted there is nothing to fill, and
        // the visibility floor must never invent a sliver of progress.
        fill: isLive && converted > liveFrom - 1 && liveTo > liveFrom - 1
          ? Math.max(0.02, (converted - (liveFrom - 1)) / (liveTo - (liveFrom - 1)))
          : undefined,
      };
    });
    // The next bracket, and how many more conversions reach it — the reason
    // to keep going, stated as a distance and not as a table to decode.
    const nextBracket = tiers.find((tier) => Number(tier.from || 0) > liveFrom
      && checkinRewardText(activityTierRewards(tier), t) !== liveReward);
    const toNext = nextBracket ? Math.max(0, Number(nextBracket.from || 0) - 1 - converted) : 0;
    // One line, in the order that answers "where am I → what's next". Falls
    // back to the registration nudge only while nothing has converted yet.
    const inviteAsk = converted > 0 && toNext > 0
      ? t('actInviteAskNext')
          .replace('%n%', fmtInt(converted))
          .replace('%k%', fmtInt(toNext))
          .replace('%v%', checkinRewardText(activityTierRewards(nextBracket), t) || '—')
      : converted > 0 && earned > 0
        ? t('actInviteAskEarned').replace('%n%', fmtInt(converted)).replace('%v%', fmtMoney(earned))
        : converted > 0
          ? t('actInviteAskConverted').replace('%n%', fmtInt(converted))
          : registered > 0
            ? (registered === 1 ? t('actInviteAskTodayOne') : t('actInviteAskTodayMany').replace('%n%', fmtInt(registered)))
            : t('actInviteAsk');
    if (firstReward) {
      missions.push({
        id: 'local:invite',
        type: 'invite',
        group: 'progress',
        band: converted > 0 ? 2 : invitedToday > 0 ? 2 : 3,
        catalog_code: 'invite',
        icon: 'users',
        color_theme: 'green',
        tier: 2,
        priority: 60,
        title_main_i18n: 'actInviteTitle',
        secondary: inviteAsk,
        // The rate on offer for the NEXT friend, not the programme's first
        // rung — once past friend 10 the card would otherwise undersell.
        reward_label: liveReward,
        reward_note: t('actInvitePerFriend'),
        steps: inviteSteps,
        cta_label_i18n: 'actInviteCta',
        cta_action: 'invite',
      });
    }
  }

  const vip = on('vip_upgrade');
  if (vip) {
    // Name the next level, what it pays, and how far away it is — "each level
    // unlocks bigger rewards" is a slogan, not a goal. The distance is stated
    // as cumulative wagering ("no total"), because "aposte mais R$ 1.580" on
    // its own can read as one terrifying bet.
    const levels = (vip.config as { levels?: Array<{ level?: number; rewards?: Array<{ kind?: string; amount?: number; ticket_count?: number }> }> } | undefined)?.levels || [];
    const current = Number(live.vipLevel || 0);
    const nextLevel = current + 1;
    const funded = levels
      .filter((entry) => Number(entry.level) > current && checkinRewardText(entry.rewards || [], t))
      .sort((a, b) => Number(a.level) - Number(b.level));
    const remaining = Number(live.vipNextRequired || 0);
    const pct = Number(live.vipProgressPct || 0);
    // Nothing funded above the current level = nothing to climb for. No
    // trophy card — the Me tab already congratulates.
    if (funded.length) {
      const target = funded[0];
      const targetReward = checkinRewardText(target.rewards || [], t);
      const nextIsFunded = Number(target.level) === nextLevel;
      // One climb at a time: the card is VIP n → n+1 and nothing else. The
      // level gate is two-dimensional — cumulative bets AND cumulative
      // deposits — so it renders as two labeled meters with the real totals.
      // Until the backend ships the deposit half, the card degrades to the
      // bets meter alone instead of inventing a zero.
      const betHave = Number(live.vipBetHave || 0);
      const betTarget = Number(live.vipBetTarget || 0) > 0 ? Number(live.vipBetTarget) : betHave + remaining;
      const depositRequired = Number(live.vipDepositRequired || 0);
      const depositTarget = live.vipDepositTarget !== undefined && Number(live.vipDepositTarget) > 0
        ? Number(live.vipDepositTarget)
        : live.vipDepositHave !== undefined && depositRequired > 0
          ? Number(live.vipDepositHave) + depositRequired
          : 0;
      const meters: NonNullable<ActivityDrumItem['meters']> = [];
      if (betTarget > 0) meters.push({ label: t('actVipMeterBets'), have: betHave, target: betTarget });
      if (depositTarget > 0 && live.vipDepositHave !== undefined) {
        meters.push({ label: t('actVipMeterDeposits'), have: Number(live.vipDepositHave), target: depositTarget });
      }
      missions.push({
        id: 'local:vip_upgrade',
        type: 'vip_upgrade',
        group: 'progress',
        band: pct > 0 || current > 0 ? 2 : 3,
        catalog_code: 'vip_upgrade',
        icon: 'gift',
        color_theme: 'gold',
        tier: 2,
        priority: 50,
        title_main_i18n: current === 0 ? 'actVipUpTitleNew' : 'actVipUpTitle',
        title_main_params: { from: current, to: nextLevel },
        // The ask names whichever half of the gate is still open — bets
        // first, then deposits, then the "almost there" line.
        secondary: remaining > 0
          ? (current === 0
              ? t('actVipUpAskNew').replace('%v%', fmtMoney(remaining)).replace('%to%', fmtInt(nextLevel))
              : t('actVipUpAskNext').replace('%v%', fmtMoney(remaining)))
          : depositRequired > 0 && live.vipDepositHave !== undefined
            ? t('actVipUpAskDeposit').replace('%v%', fmtMoney(depositRequired))
            : t('actVipUpAskZero'),
        meters: meters.length ? meters : undefined,
        // The next level's own prize. When the programme funds a farther
        // level instead, the figure says whose it is — never dressed up as
        // the next rung's.
        reward_label: targetReward || undefined,
        reward_prefix: targetReward && !nextIsFunded ? t('actVipRewardAtLevel').replace('%n%', fmtInt(Number(target.level))) : undefined,
        cta_label_i18n: 'actVipUpCta',
        cta_action: 'hall',
      });
    }
  }

  return missions;
}

function normalizeActivities(items: ActivityDrumItem[]) {
  const now = Math.floor(Date.now() / 1000);
  return items
    .filter((item) => !item.starts_at || item.starts_at <= now)
    .filter((item) => !item.expires_at || item.expires_at >= now)
    .filter(shouldShowActivity)
    .sort((a, b) => Number(a.tier || 2) - Number(b.tier || 2) || Number(b.priority || 0) - Number(a.priority || 0));
}

function shouldShowActivity(item: ActivityDrumItem) {
  if (activityStateHas(item, 'dismissed_at')) return false;
  if (item.lifecycle === 'once_read' && activityStateHas(item, 'viewed_at')) return false;
  if (item.lifecycle === 'once_claimed' && activityStateHas(item, 'clicked_at')) return false;
  return true;
}

function mergeActivityState(current: ActivityDrumResponse | undefined, id: string, state?: Record<string, unknown>) {
  if (!current || !state) return current;
  return {
    ...current,
    items: (current.items || []).map((item) => (
      item.id === id ? { ...item, state: { ...(item.state || {}), ...state } } : item
    )),
  };
}

function activityStateHas(item: ActivityDrumItem, key: 'viewed_at' | 'clicked_at' | 'dismissed_at') {
  return Boolean(item.state && item.state[key]);
}

function isUuidLike(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || ''));
}

// Five rows all showing the same gift outline made this a 34px column of
// decoration. Each family of task gets its own mark, so the list can be read
// down the left edge.
function ActivityIcon({ item }: { item: ActivityDrumItem }) {
  const icon = item.icon || item.type || '';
  const action = activityActionValue(item.cta_action || '');
  const both = `${icon} ${item.type || ''} ${action}`;
  if (icon.includes('trophy') || both.includes('tournament')) return <List size={18} aria-hidden="true" />;
  if (both.includes('invite') || both.includes('referral') || icon.includes('users')) return <Users size={18} aria-hidden="true" />;
  if (both.includes('raffle') || both.includes('ticket')) return <Ticket size={18} aria-hidden="true" />;
  if (both.includes('deposit') || both.includes('coupon') || both.includes('wallet')) return <Coins size={18} aria-hidden="true" />;
  if (both.includes('vip')) return <ShieldCheck size={18} aria-hidden="true" />;
  if (both.includes('email') || both.includes('mail')) return <Mail size={18} aria-hidden="true" />;
  if (both.includes('phone') || both.includes('sms') || both.includes('bind') || both.includes('verify')) return <Phone size={18} aria-hidden="true" />;
  if (both.includes('bet') || both.includes('mission') || both.includes('hall') || both.includes('play')) return <Dices size={18} aria-hidden="true" />;
  return <Gift size={18} aria-hidden="true" />;
}

// One accent per activity. It only ever touches decoration — the icon chip, a
// low wash behind the card, the watermark. Reward figures, CTAs and rung states
// keep the semantic palette (gold = take it now, teal = cleared), so the page
// still reads by meaning; this layer only makes the cards tellable apart.
const ACTIVITY_ACCENT: Record<string, string> = {
  'checkin:login': '141, 211, 199',
  'checkin:deposit': '134, 197, 122',
  'checkin:bet': '129, 178, 214',
  daily_raffle: '96, 205, 224',
  mission_daily: '160, 148, 232',
  deposit_bonus: '246, 196, 83',
  invite: '232, 141, 183',
  invite_first_deposit: '232, 141, 183',
  vip_upgrade: '186, 154, 233',
  bind_contact_email: '124, 176, 232',
  bind_contact_phone: '108, 190, 178',
};

const ACCENT_FALLBACK = '148, 163, 184';

function activityAccent(item: ActivityDrumItem) {
  return ACTIVITY_ACCENT[String(item.type || '')]
    || ACTIVITY_ACCENT[String(item.catalog_code || '')]
    || ACCENT_FALLBACK;
}

// "Free 100 BRL bag": a demo row V27 seeded, frozen at 40% for every player
// because that figure was a literal, so it could never be unlocked — and its
// claim credited nothing. Retired in V152; filtered here for the window before
// that migration reaches production, and for any row that outlives it.
function isRetiredFreebag(item: ActivityDrumItem) {
  const action = activityActionValue(item.cta_action || '');
  return action === 'claim-freebag' || String(item.type || '').includes('freebag');
}

// A card tracks progress only if the backend said so — an absent progress_pct
// is "not applicable", not "zero".
function activityHasProgress(item: ActivityDrumItem) {
  return item.progress_pct != null || Boolean(item.progress_label) || Boolean(activityFraction(item));
}

function activityProgress(item: ActivityDrumItem) {
  return Math.max(0, Math.min(100, Number(item.progress_pct || 0)));
}

function activityLocked(item: ActivityDrumItem) {
  const status = String(item.status || '').toLowerCase();
  if (status === 'locked') return true;
  return false;
}

function activityProgressLabel(item: ActivityDrumItem, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (item.progress_label) return item.progress_label;
  if (item.reward || item.reward_label) return `${fmtInt(activityProgress(item))}% · ${item.reward || item.reward_label}`;
  if (activityLocked(item)) return `${fmtInt(activityProgress(item))}% · ${t('activitiesLocked')}`;
  return `${fmtInt(activityProgress(item))}%`;
}

function activityCtaLabel(item: ActivityDrumItem, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (activityLocked(item)) return t('activitiesLocked');
  return activityText(item.cta_label, item.cta_label_i18n, {}, t) || t('view');
}

function activitySecondsLeft(item: ActivityDrumItem) {
  const expiresAt = Number(item.expires_at || 0);
  if (!expiresAt) return 0;
  return Math.max(0, expiresAt - Math.floor(Date.now() / 1000));
}

function formatActivityTimeLeft(seconds: number) {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${Math.max(1, minutes)}m`;
}

function routeFromActivityAction(action: string): PlayerRoute | null {
  const value = activityActionValue(action);
  if (value === 'wallet' || value === 'carteira' || value === 'billetera') return 'wallet';
  if (value === 'wallet-tickets' || value === 'tickets' || value === 'bilhetes') return 'wallet.tickets';
  if (value === 'orders' || value === 'pedidos') return 'pedidos';
  if (value === 'tournament' || value === 'leaderboard' || value === 'ranking' || value === 'top') return 'leaderboard';
  if (value === 'avisos' || value === 'notifications' || value === 'notices') return 'avisos';
  if (value === 'faq' || value === 'avisos-faq') return 'avisos.faq';
  if (value === 'rules' || value === 'fairness' || value === 'provably-fair') return 'rules';
  if (value === 'archive' || value === 'history') return 'archive';
  if (value === 'hall' || value === 'lobby') return 'lobby';
  if (value === 'me' || value === 'profile-center') return 'me';
  if (value === 'profile' || value === 'me-profile') return 'me.profile';
  if (value === 'vip' || value === 'me-vip') return 'me.vip';
  if (value === 'limits' || value === 'responsible' || value === 'responsible-gaming') return 'me.limits';
  if (value === 'activities' || value === 'promos' || value === 'promocoes') return 'me.activities';
  if (value === 'invite' || value === 'referral') return 'me.invite';
  if (value === 'security') return 'me.security';
  if (value === 'kyc' || value === 'identity' || value === 'identity-verification') return 'me.kyc';
  if (value === 'support' || value === 'help') return 'avisos.help';
  if (value === 'bet-history' || value === 'my-bets') return 'bet-history';
  return null;
}

function walletActionFromActivityAction(action: string) {
  const value = activityActionValue(action);
  if (['deposit', 'pix-deposit', 'deposito', 'recharge', 'topup'].includes(value)) return 'deposit';
  if (['withdraw', 'pix-withdraw', 'saque', 'cashout'].includes(value)) return 'withdraw';
  return null;
}

function activityActionValue(action: string) {
  let value = String(action || '').trim().toLowerCase().replace(/_/g, '-');
  if (value.startsWith('nav-')) value = value.slice(4);
  if (value.startsWith('open-')) value = value.slice(5);
  return value;
}

function raffleActivityEventId(item: ActivityDrumItem) {
  const params = [item.title_params, item.title_main_params, item.secondary_params, item.state].filter(Boolean);
  const direct = item.event_id || item.eventId || item.raffle_event_id || item.raffleEventId;
  if (direct) return String(direct);
  for (const param of params) {
    const value = param?.event_id || param?.eventId || param?.raffle_event_id || param?.raffleEventId;
    if (value) return String(value);
  }
  const dynamicRaffleId = String(item.id || '').match(/^act-raffle-(.+?)-(?:registering|locked|settling|settled|voided)$/i)?.[1];
  if (dynamicRaffleId) return dynamicRaffleId;
  const fromId = String(item.id || '').match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0];
  return fromId || null;
}


function checkinTrackLabel(track: string | undefined, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (track === 'login') return t('activitiesTrackLogin');
  if (track === 'bet') return t('activitiesTrackBet');
  if (track === 'deposit') return t('activitiesTrackDeposit');
  return track || t('activityFallback');
}

function activityTypeLabel(type: string | undefined, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (!type) return t('activityFallback');
  if (type.startsWith('checkin')) return t('activitiesTypeCheckin');
  if (type === 'daily_bet_rounds') return t('activitiesTypeDailyBet');
  if (type === 'bind_contact') return t('activitiesTypeBindContact');
  if (type === 'vip_upgrade') return t('activitiesTypeVip');
  if (type === 'deposit_bonus') return t('activitiesTypeDepositBonus');
  if (type === 'first_day_loss_comp') return t('activitiesTypeLossComp');
  if (type === 'welcome_pack') return t('activitiesTypeWelcomePack');
  if (type === 'invite_first_deposit') return t('activitiesTypeInvite');
  if (type === 'weekly_rescue') return t('activitiesTypeWeeklyRescue');
  // Last resort for a type the backend ships before this list knows it: still
  // readable ("welcome_pack" → "Welcome pack"), never a raw slug on screen.
  return type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function activityText(
  plain: string | undefined,
  key: string | undefined,
  params: Record<string, string | number> | undefined,
  t: ReturnType<typeof useI18nStore.getState>['t'],
) {
  const translated = key ? t(activityMessageKey(key)) : '';
  return interpolate(translated && translated !== 'activityFallback' ? translated : plain || key || '', params);
}

// The backend ships i18n KEYS for activity copy, so new cards are meant to be
// configurable without a release. This used to be a hardcoded whitelist of 15
// keys — anything new fell through to "Activity", which made backend-authored
// content impossible. Resolve against the dictionary instead: any key that
// exists is honoured, anything unknown still falls back safely.
function activityMessageKey(key: string): MessageKey {
  return Object.prototype.hasOwnProperty.call(MESSAGES_PT_BR, key)
    ? (key as MessageKey)
    : 'activityFallback';
}

function interpolate(text: string, params: Record<string, string | number> | undefined) {
  if (!params) return text;
  return Object.entries(params).reduce((value, [key, param]) => value.split(`%${key}%`).join(String(param)), text);
}

function inviteUrl(user: PlayerUser | null, code: string) {
  const explicit = String(user?.invite_link || user?.inviteLink || '').trim();
  // Only trust a backend link if it's already a full URL. Backends often send
  // a bare path like "/#/auth.register?invite=CODE", which renders as a broken,
  // un-shareable link — resolve everything else against the current origin.
  if (/^https?:\/\//i.test(explicit)) return explicit;
  const origin = (() => { try { return window.location.origin; } catch { return ''; } })();
  if (explicit.startsWith('/')) return origin ? `${origin}${explicit}` : explicit;
  if (!code) return '';
  return `${origin}/?invite=${encodeURIComponent(code)}#/auth.register`;
}

function formFromLimits(limits: ResponsibleGamingLimits) {
  return {
    daily: Number(limits.daily_bet_limit_brl || 5000),
    weekly: Number(limits.weekly_bet_limit_brl || 25000),
    monthly: Number(limits.monthly_bet_limit_brl || 100000),
  };
}

function formatSessionMinutes(minutes: number, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const safeMinutes = Math.max(0, Math.floor(minutes));
  if (safeMinutes < 60) return t('sessionMinutes').replace('{{m}}', String(safeMinutes)).replace('%m%', String(safeMinutes));
  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

function clampMoney(value: string | number, min: number, max: number) {
  const numeric = typeof value === 'number' ? value : Number(String(value).replace(/\D/g, ''));
  return Math.max(min, Math.min(max, Number.isFinite(numeric) ? numeric : min));
}

function coolOffRemainingLabel(until: number | undefined) {
  const left = Number(until || 0) - Math.floor(Date.now() / 1000);
  if (left <= 0) return '';
  const days = Math.floor(left / 86_400);
  const hours = Math.floor((left % 86_400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((left % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

function normalizeSecuritySessions(sessions: SecuritySession[]) {
  return sessions
    .filter((session) => session && session.jti)
    .sort((a, b) => Number(b.current) - Number(a.current) || Number(b.created_at || 0) - Number(a.created_at || 0));
}

function securityStrengthLabel(
  phoneVerified: boolean,
  emailVerified: boolean,
  t: ReturnType<typeof useI18nStore.getState>['t'],
) {
  const score = 1 + Number(phoneVerified) + Number(emailVerified);
  if (score >= 3) return { score, label: t('securityStrengthStrong'), tone: 'strong' };
  if (score === 2) return { score, label: t('securityStrengthMedium'), tone: 'medium' };
  return { score, label: t('securityStrengthWeak'), tone: 'weak' };
}

function kycStatusLabel(status: string | undefined, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const value = String(status || 'none').toLowerCase();
  if (value === 'pending') return t('kycStatusPending');
  if (value === 'rejected') return t('kycStatusRejected');
  if (value === 'verified') return t('verified');
  return t('kycStatusNone');
}

function passwordResetIdentifier(user: PlayerUser | null) {
  const phone = String(user?.phone || '').replace(/\D/g, '');
  if (phone) return { value: phone, label: displayPhone(phone, activeCountry()) || phone };
  const email = String(user?.email || '').trim();
  if (email) return { value: email, label: email };
  return { value: '', label: '' };
}

function sessionDeviceLabel(session: SecuritySession) {
  const ua = String(session.user_agent || '');
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS Safari';
  if (/Android/i.test(ua)) return /Chrome/i.test(ua) ? 'Android Chrome' : 'Android';
  if (/Macintosh|Mac OS/i.test(ua)) return /Chrome/i.test(ua) ? 'Mac Chrome' : 'Mac Safari';
  if (/Windows/i.test(ua)) return /Chrome/i.test(ua) ? 'Windows Chrome' : 'Windows';
  if (/Firefox/i.test(ua)) return 'Firefox';
  return ua ? ua.slice(0, 36) : 'Outro dispositivo';
}

function isEmailValid(value: string) {
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value.trim());
}

function otpExpiryLabel(seconds: number, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (seconds <= 0) return '';
  const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
  const rest = String(seconds % 60).padStart(2, '0');
  return t('loginOtpExpiresIn').replace('%t%', `${minutes}:${rest}`);
}

const USERNAME_COOLDOWN_SEC = 15 * 24 * 3600;
function usernameValidationMessage(code: string, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const map: Record<string, MessageKey> = {
    too_short: 'usernameErrTooShort',
    too_long: 'usernameErrTooLong',
    first_char: 'usernameErrFirstChar',
    invalid_char: 'usernameErrInvalidChar',
    consecutive_special: 'usernameErrConsecutive',
    trailing_special: 'usernameErrTrailing',
    reserved: 'usernameErrReserved',
    taken: 'usernameErrTaken',
  };
  return t(map[code] || 'usernameErrInvalid');
}

function usernameServerError(error: unknown, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const payload = errorPayload(error);
  const code = payload && typeof payload === 'object'
    ? String((payload as { reason?: string; code?: string }).reason || (payload as { code?: string }).code || '')
    : error instanceof Error ? error.message : 'invalid';
  if (code === 'cooldown_active') {
    const nextAt = payload && typeof payload === 'object' ? Number((payload as { next_change_at?: number; nextChangeAt?: number }).next_change_at ?? (payload as { nextChangeAt?: number }).nextChangeAt ?? 0) : 0;
    return t('usernameErrCooldown').replace('%date%', nextAt ? formatDateShort(nextAt) : '');
  }
  if (code === 'same') return t('usernameErrSame');
  return usernameValidationMessage(code, t);
}

function errorPayload(error: unknown) {
  if (error && typeof error === 'object' && 'payload' in error) return (error as { payload?: unknown }).payload;
  return null;
}

function foldAccents(value: string) {
  try {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  } catch {
    return value;
  }
}

function usernameChangeGate(user: PlayerUser | null) {
  const usernameGraceUsed = Boolean(user?.username_grace_used ?? user?.usernameGraceUsed);
  const changedAt = Number(user?.username_changed_at ?? user?.usernameChangedAt ?? 0);
  if (!usernameGraceUsed) return { ok: true, viaGrace: true, nextAt: 0 };
  const nextAt = changedAt + USERNAME_COOLDOWN_SEC;
  if (!changedAt || Math.floor(Date.now() / 1000) >= nextAt) return { ok: true, viaGrace: false, nextAt: 0 };
  return { ok: false, viaGrace: false, nextAt };
}

function currentAvatarDefaultId(user: PlayerUser | null) {
  return Number(user?.avatar_default_id ?? user?.avatarDefaultId ?? 0) || 0;
}

function mergeUsernameUser(user: PlayerUser | null, payload: UsernameResponse, fallback: string): PlayerUser | null {
  if (!user) return user;
  const username = payload.username || fallback;
  const nextChangeAt = Number(payload.next_change_at ?? payload.nextChangeAt ?? 0) || 0;
  return {
    ...user,
    username,
    name: username,
    initial: username[0]?.toUpperCase(),
    username_grace_used: Boolean(payload.used_grace ?? payload.usedGrace ?? true),
    usernameChangedAt: nextChangeAt ? nextChangeAt - USERNAME_COOLDOWN_SEC : Math.floor(Date.now() / 1000),
  };
}

function mergePreferenceUser(user: PlayerUser, payload: { public_leaderboard?: boolean; activity_notifications?: boolean }): PlayerUser {
  return {
    ...user,
    public_leaderboard: payload.public_leaderboard ?? user.public_leaderboard,
    publicLeaderboard: payload.public_leaderboard ?? user.publicLeaderboard,
    activity_notifications: payload.activity_notifications ?? user.activity_notifications,
    activityNotifications: payload.activity_notifications ?? user.activityNotifications,
  };
}

function mergeAvatarUser(user: PlayerUser | null, payload: { type: 'default'; id: number } | { type: 'remove' } | { type: 'upload'; dataUrl: string }): PlayerUser | null {
  if (!user) return user;
  if (payload.type === 'remove') {
    return {
      ...user,
      avatar: '',
      avatar_url: '',
      avatar_default_id: undefined,
      avatarDefaultId: undefined,
    };
  }
  if (payload.type === 'upload') {
    return {
      ...user,
      avatar: payload.dataUrl,
      avatar_url: payload.dataUrl,
      avatar_thumb: payload.dataUrl,
      avatarThumb: payload.dataUrl,
      avatar_default_id: undefined,
      avatarDefaultId: undefined,
    };
  }
  const avatar = defaultAvatarUrl(payload.id);
  return {
    ...user,
    avatar,
    avatar_url: avatar,
    avatar_default_id: payload.id,
    avatarDefaultId: payload.id,
  };
}

function avatarGroupLabelKey(key: string): MessageKey {
  return `avatarGroup_${key}` as MessageKey;
}

function formatDateShort(seconds: number) {
  if (!seconds) return '';
  try {
    return new Date(seconds * 1000).toLocaleDateString([], { month: '2-digit', day: '2-digit', year: 'numeric' });
  } catch {
    return '';
  }
}

function formatTime(seconds: number) {
  if (!seconds) return '--';
  try {
    return new Date(seconds * 1000).toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '--';
  }
}

function languageFullName(lang: string) {
  if (lang === 'en') return 'English';
  if (lang === 'es') return 'Español';
  return 'Português';
}

function activityFraction(item: ActivityDrumItem) {
  const raw = item as ActivityDrumItem & { progress?: { done?: number; needed?: number }; progress_done?: number; progress_needed?: number };
  const done = Number(raw.progress?.done ?? raw.progress_done ?? NaN);
  const needed = Number(raw.progress?.needed ?? raw.progress_needed ?? NaN);
  if (Number.isNaN(done)) return '';
  return Number.isFinite(needed) && needed > 0 ? `${done} / ${needed}` : String(done);
}
